import { storage, db } from "@/lib/firebase";
import {
  newExportInputsUploadId,
  startExportJob,
  uploadExportInputs,
  type ExportInputs,
} from "./exportJobService";
import { EntryCollection } from "@/types/entryCollection";
import { EntryConfig } from "@/types/entryConfig";
import { Experiment } from "@/types/experiment";
import type { Organization } from "@/types/organization";
import type { Trial } from "@/types/trial";
import { Plot } from "@/types/plot";
import { DocumentReference, Timestamp, getDoc, doc } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import JSZip from "jszip";
import { combinedAudioPartZipPath } from "@shared/exportJob";
import {
  collectionCaptureRecord,
  assertCollectionCapturesExportable,
  requiredCaptureDownloadError,
  type CollectionCaptureRecord,
} from "@shared/collectionCaptureInventory";
import {
  countPerSquareMeterToPerAcre,
  computeWeightedCountPerM2,
  computeWeightedStandCountParts,
} from "@/lib/standCountUtils";
import { unparse } from "papaparse";
import {
  generateMediaFilename,
  generateCombinedAudioFilename,
  getExtensionFromPath,
  formatLocalDateTime,
  formatTimestampForFilename,
} from "@/utils/mediaUtils";
import { buildExportInfo } from "@/utils/exportInfo";
import {
  assertCocoExportSourceMatches,
  cocoExportSourceMatches,
  generateCocoAnnotations,
  type CocoExportSource,
} from "@/services/cocoExportService";
import {
  getImageCsvPath,
  getAnnotatedImageZipPath,
  getCocoAnnotationsZipPath,
  getAudioZipPath,
  getAudioCsvPath,
  getReadmeZipPath,
  getExportInfoZipPath,
  getCollectionCapturesZipPath,
  getMediaZipPath,
  buildExportFileNames,
  EXPORT_PATH_BUDGET,
  type ExportFileNames,
  type ExportMediaType,
  type MediaDownloadOptions,
} from "@/utils/exportPaths";
import { renderAnnotatedImage } from "@/services/annotationRenderingService";
import { buildAnnotatedImageRenderOptions } from "@/services/annotatedImageExportService";
import {
  buildAnnotationClassNamesByEntryConfigId,
  getAnnotationClassNamesForEntryConfig,
  getTaskAnnotationCategorySchemas,
  getTaskAnnotationClassColorOrder,
} from "@/utils/annotationClassConfig";
import { CaptureMetadata, Annotation } from "@/types/annotations";
import {
  getAnnotationMetadata,
  getMostRecentStoppedTimerMs,
  getMostRecentStoppedTimerMsForSample,
  getStoppedTimerMs,
  timerMsToSeconds,
} from "@/utils/entryUtils";
import {
  getImageMeasurementValue,
  getImageMeasurementValueWithStatus,
  getRowPlantedStandCountValue,
  getStandCountValue,
  getStandCountValueWithStatus,
} from "@/utils/entryCollectionUtils";
import { convertDescriptorValueForDisplay, getDescriptorDisplayUnit } from "@/types/measurementMetrics";
import { assignBucket, computeGradeDistribution } from "@/utils/gradingUtils";
import type { ImageMeasurementConfig, ImageMeasurementGrading } from "@/types/entryConfig";
import {
  getDescriptorsForEntryConfig,
  getUnboundDescriptors,
  hasCustomLabel,
  type CanonicalBinding,
  type MeasurementDescriptor,
} from "@shared/types/measurementDescriptors";

/**
 * Interface for the input data needed to generate a CSV export
 */
export interface TaskExportData {
  experiment: Experiment;
  plots: Plot[];
  entryCollections: EntryCollection[];
  entryConfigs: EntryConfig[];
  /** Names for export_info.json, recorded only when they belong to the verified task */
  trial?: Pick<Trial, "id" | "name"> | null;
  organization?: Pick<Organization, "id" | "name"> | null;
}

/**
 * Represents a media file with its cloud path and type
 */
interface MediaFileInfo {
  cloudPath: string;
  type: ExportMediaType;
}

/**
 * Granular options for which media types to include in a download.
 * When all options are false, the export is CSV-only.
 */
export type { MediaDownloadOptions };

export interface TaskExportRouteContext {
  trialId: string;
  experimentId: string;
  taskId: string;
}

export const ALL_MEDIA_OPTIONS: MediaDownloadOptions = {
  includeAnnotatedImages: true,
  includeOriginalImages: true,
  includeDepthAndMetadata: true,
  includeAudio: true,
};

export const NO_MEDIA_OPTIONS: MediaDownloadOptions = {
  includeAnnotatedImages: false,
  includeOriginalImages: false,
  includeDepthAndMetadata: false,
  includeAudio: false,
};

const METADATA_PREFETCH_TIMEOUT_MS = 5 * 60_000;
const METADATA_PREFETCH_CANCEL_POLL_MS = 250;
const DOWNLOAD_CANCELLED_MESSAGE = "Download cancelled";
const METADATA_FETCH_TIMED_OUT = Symbol("metadataFetchTimedOut");
const METADATA_FETCH_CANCELLED = Symbol("metadataFetchCancelled");
const CENTIMETER_DECIMAL_PLACES = 2;

const roundToDecimalPlaces = (value: number, decimalPlaces: number): number => {
  const factor = 10 ** decimalPlaces;
  return Math.round(value * factor) / factor;
};

const roundCentimeters = (valueCm: number): number => roundToDecimalPlaces(valueCm, CENTIMETER_DECIMAL_PLACES);

const metersToRoundedCentimeters = (valueMeters: number): number => roundCentimeters(valueMeters * 100);

const formatCentimetersForCsv = (valueCm: number): string => valueCm.toFixed(CENTIMETER_DECIMAL_PLACES);

const formatMetersAsCentimetersForCsv = (valueMeters: number): string => formatCentimetersForCsv(valueMeters * 100);

export function hasAnyMediaOption(options: MediaDownloadOptions): boolean {
  return (
    options.includeAnnotatedImages ||
    options.includeOriginalImages ||
    options.includeDepthAndMetadata ||
    options.includeAudio
  );
}

export function hasAllMediaOptions(options: MediaDownloadOptions): boolean {
  return (
    options.includeAnnotatedImages &&
    options.includeOriginalImages &&
    options.includeDepthAndMetadata &&
    options.includeAudio
  );
}

/** Check if a media file type is included based on the current download options. */
function isMediaFileIncluded(type: ExportMediaType, options: MediaDownloadOptions): boolean {
  if (type === "image") return options.includeOriginalImages;
  if (type === "depth" || type === "metadata") return options.includeDepthAndMetadata;
  return false;
}

interface AnnotatedEntryInfo {
  plotId: string;
  sampleKey: string;
  imgCloudPath: string;
  captureMetadataCloudPath: string;
  filename: string;
  entryType: string;
  entryConfigId?: string;
}

interface TranscriptionAggregate {
  plotId: string;
  combinedText: string;
  combinedAudioFilename: string | null;
  audioCloudPaths: string[];
  hasAudio: boolean;
}

/**
 * Represents a column in the CSV export
 */
interface CSVColumn {
  header: string;
  entryConfigId?: string;
  subField?: string;
  sampleKey?: string; // Which sample number this column represents (e.g., "1", "2", "3")
  collectorKey?: string; // Which collector this column represents for collector-aware wide exports
  isImagePath?: boolean; // True if this column contains image/media paths
  isIndividualMeasurement?: boolean; // True if this column contains JSON arrays of individual measurements
  isExtractedText?: boolean; // True if this column contains OCR-extracted text from images
}

interface CollectorColumnLayout {
  collectorKey: string;
  collectorLabel: string;
  maxSamples: number;
}

interface CollectorColumnTarget {
  collectorKey?: string;
  collectorLabel?: string;
  maxSamples: number;
  forceSampleLabel: boolean;
}

interface TraitColumnLayout {
  config: EntryConfig;
  configId: string;
  collections: EntryCollection[];
  maxSamples: number;
  useCollectorColumns: boolean;
  collectors: CollectorColumnLayout[];
  hasAnyExtractedText: boolean;
  hasStandCountAvgLength: boolean;
  hasStandCountAvgWidth: boolean;
  hasStandCountWeight: boolean;
}

/**
 * Progress information for ZIP export
 */
export interface ExportProgress {
  phase: "preparing" | "downloading" | "processing" | "compressing" | "fallback" | "server";
  current: number;
  total: number;
  currentFile?: string;
  message?: string;
}

/**
 * Threshold for routing media downloads to server-side Cloud Function.
 * Below this: client-side JSZip. Above this: Cloud Function streaming.
 * 300 files * ~2.5 MB avg = ~750 MB uncompressed, within Chrome's ~2.15 GB limit.
 */
export const MEDIA_FILE_THRESHOLD = 300;

export type ProgressCallback = (progress: ExportProgress) => void;

/**
 * Yield to the main thread to allow UI updates
 */
function yieldToMain(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

const WAV_HEADER_SIZE = 44;

function addMissingTranscriptionConfigs(
  entryConfigs: EntryConfig[],
  entryCollections: EntryCollection[]
): EntryConfig[] {
  const configsById = new Map<string, EntryConfig>();
  entryConfigs.forEach((config) => {
    if (config.id) configsById.set(config.id, config);
  });

  for (const collection of entryCollections) {
    if (collection.type !== "transcription") continue;

    const refId =
      typeof collection.entryConfigRef === "string" ? collection.entryConfigRef : collection.entryConfigRef?.id;

    if (!refId || configsById.has(refId)) continue;

    const createdAt = (collection.createdAt as Timestamp) || Timestamp.now();
    const updatedAt = (collection.updatedAt as Timestamp) || createdAt;
    const createdBy = (collection.createdBy as DocumentReference) || ({} as DocumentReference);
    const updatedBy = (collection.updatedBy as DocumentReference) || createdBy;

    const placeholder: EntryConfig = {
      id: refId,
      name: "Transcription",
      description: "",
      isArchived: false,
      isSystem: true,
      type: "transcription",
      metadata: {},
      transcriptionConfig: {},
      supportedImageAnalysisTypes: [],
      createdAt,
      updatedAt,
      createdBy,
      updatedBy,
    };

    configsById.set(refId, placeholder);
  }

  return Array.from(configsById.values());
}

function formatTimestampForDisplay(timestamp: Timestamp): string {
  const date = timestamp.toDate();
  return `${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function buildTranscriptionAggregates(entryCollections: EntryCollection[]): Map<string, TranscriptionAggregate> {
  const aggregates = new Map<string, TranscriptionAggregate>();

  for (const collection of entryCollections) {
    if (collection.type !== "transcription") continue;

    const plotRef = collection.plotRef;
    const plotId = typeof plotRef === "string" ? plotRef : plotRef.id;
    const entries = collection.transcriptionEntries || {};

    const samples = Object.entries(entries).map(([sampleKey, entry]) => {
      const ts =
        (entry.updatedAt as Timestamp) || (collection.updatedAt as Timestamp) || (collection.createdAt as Timestamp);
      return {
        sampleKey,
        text: entry.text || "",
        audioPath: entry.cloudAudioPath,
        timestamp: ts,
      };
    });

    if (samples.length === 0) continue;

    // Sort by timestamp then sample key numeric
    samples.sort((a, b) => {
      const aTs = a.timestamp?.seconds || 0;
      const bTs = b.timestamp?.seconds || 0;
      if (aTs !== bTs) return aTs - bTs;
      return Number(a.sampleKey) - Number(b.sampleKey);
    });

    const combinedTextParts: string[] = [];
    const audioCloudPaths: string[] = [];
    let latestTimestamp: Timestamp | null = null;

    for (const sample of samples) {
      if (sample.text) {
        const ts = sample.timestamp || collection.createdAt;
        const formattedTs = ts ? formatTimestampForDisplay(ts as Timestamp) : "Unknown time";
        combinedTextParts.push(`[${formattedTs}] ${sample.text}`);
      }
      if (sample.audioPath) {
        audioCloudPaths.push(sample.audioPath);
      }
      if (sample.timestamp) {
        if (!latestTimestamp || sample.timestamp.seconds > latestTimestamp.seconds) {
          latestTimestamp = sample.timestamp as Timestamp;
        }
      }
    }

    const hasAudio = audioCloudPaths.length > 0;
    let combinedAudioFilename: string | null = null;
    if (hasAudio) {
      const tsForName =
        latestTimestamp ||
        (collection.updatedAt as Timestamp) ||
        (collection.createdAt as Timestamp) ||
        Timestamp.now();
      combinedAudioFilename = generateCombinedAudioFilename(plotId, tsForName);
    }

    aggregates.set(plotId, {
      plotId,
      combinedText: combinedTextParts.join("\n"),
      combinedAudioFilename,
      audioCloudPaths,
      hasAudio,
    });
  }

  return aggregates;
}

/**
 * Parse a WAV header and return format info + data offset.
 * Returns null if the buffer is not a valid PCM WAV.
 */
function parseWavHeader(buf: ArrayBuffer): {
  channels: number;
  sampleRate: number;
  bitsPerSample: number;
  dataOffset: number;
  dataSize: number;
} | null {
  if (buf.byteLength < WAV_HEADER_SIZE) return null;
  const view = new DataView(buf);

  // Verify RIFF/WAVE
  if (view.getUint32(0, false) !== 0x52494646) return null; // "RIFF"
  if (view.getUint32(8, false) !== 0x57415645) return null; // "WAVE"

  // Walk chunks to find "fmt " and "data"
  let channels = 0,
    sampleRate = 0,
    bitsPerSample = 0;
  let dataOffset = 0,
    dataSize = 0;
  let offset = 12;

  while (offset + 8 <= buf.byteLength) {
    const chunkId = view.getUint32(offset, false);
    const chunkSize = view.getUint32(offset + 4, true);

    if (chunkId === 0x666d7420) {
      // "fmt "
      if (view.getUint16(offset + 8, true) !== 1) return null; // PCM only
      channels = view.getUint16(offset + 10, true);
      sampleRate = view.getUint32(offset + 12, true);
      bitsPerSample = view.getUint16(offset + 22, true);
    } else if (chunkId === 0x64617461) {
      // "data"
      dataOffset = offset + 8;
      dataSize = chunkSize;
    }

    offset += 8 + chunkSize;
    if (offset % 2 !== 0) offset++; // chunks are word-aligned
  }

  if (!channels || !sampleRate || !bitsPerSample || !dataOffset) return null;
  return { channels, sampleRate, bitsPerSample, dataOffset, dataSize };
}

/**
 * Concatenate WAV ArrayBuffers at their original sample rate.
 * Avoids Web Audio API which upsamples to the system rate (48 kHz).
 * Returns null if formats are incompatible.
 */
function concatWavBuffers(buffers: ArrayBuffer[]): ArrayBuffer | null {
  if (buffers.length === 0) return null;
  if (buffers.length === 1) return buffers[0];

  const headers = buffers.map(parseWavHeader);
  const first = headers[0];
  if (!first) return null;

  // All files must match format
  for (let i = 1; i < headers.length; i++) {
    const h = headers[i];
    if (!h) return null;
    if (h.sampleRate !== first.sampleRate || h.channels !== first.channels || h.bitsPerSample !== first.bitsPerSample) {
      console.warn(
        `WAV format mismatch: file 0 (${first.sampleRate}Hz, ${first.channels}ch, ${first.bitsPerSample}bit) vs file ${i} (${h.sampleRate}Hz, ${h.channels}ch, ${h.bitsPerSample}bit)`
      );
      return null;
    }
  }

  // Sum all data sizes
  const totalDataSize = headers.reduce((sum, h) => sum + h!.dataSize, 0);
  const blockAlign = first.channels * (first.bitsPerSample / 8);
  const byteRate = first.sampleRate * blockAlign;

  // Build output WAV
  const out = new ArrayBuffer(WAV_HEADER_SIZE + totalDataSize);
  const view = new DataView(out);
  let off = 0;

  // RIFF header
  view.setUint32(off, 0x52494646, false);
  off += 4; // "RIFF"
  view.setUint32(off, 36 + totalDataSize, true);
  off += 4;
  view.setUint32(off, 0x57415645, false);
  off += 4; // "WAVE"

  // fmt chunk
  view.setUint32(off, 0x666d7420, false);
  off += 4; // "fmt "
  view.setUint32(off, 16, true);
  off += 4;
  view.setUint16(off, 1, true);
  off += 2; // PCM
  view.setUint16(off, first.channels, true);
  off += 2;
  view.setUint32(off, first.sampleRate, true);
  off += 4;
  view.setUint32(off, byteRate, true);
  off += 4;
  view.setUint16(off, blockAlign, true);
  off += 2;
  view.setUint16(off, first.bitsPerSample, true);
  off += 2;

  // data chunk
  view.setUint32(off, 0x64617461, false);
  off += 4; // "data"
  view.setUint32(off, totalDataSize, true);
  off += 4;

  // Copy PCM data from each file
  const outBytes = new Uint8Array(out);
  for (let i = 0; i < buffers.length; i++) {
    const h = headers[i]!;
    outBytes.set(new Uint8Array(buffers[i], h.dataOffset, h.dataSize), off);
    off += h.dataSize;
  }

  return out;
}

async function buildCombinedAudioBlob(
  aggregate: TranscriptionAggregate
): Promise<{ filename: string; blob: Blob } | null> {
  if (!aggregate.hasAudio || !aggregate.combinedAudioFilename) return null;

  const wavBuffers: ArrayBuffer[] = [];
  for (const cloudPath of aggregate.audioCloudPaths) {
    try {
      const storageRef = ref(storage, cloudPath);
      const url = await getDownloadURL(storageRef);
      const response = await fetch(url);
      if (!response.ok) continue;
      wavBuffers.push(await response.arrayBuffer());
    } catch (error) {
      console.error("Error downloading transcription audio", error);
    }
  }

  if (wavBuffers.length === 0) return null;

  const combined = concatWavBuffers(wavBuffers);
  if (!combined) return null;

  const blob = new Blob([combined], { type: "audio/wav" });
  return { filename: aggregate.combinedAudioFilename, blob };
}

/**
 * Get sample keys from an entry collection based on its type
 * Returns an empty array if no entries exist
 */
function getCollectionSampleKeys(collection: EntryCollection): string[] {
  switch (collection.type) {
    case "text":
      return collection.textEntries ? Object.keys(collection.textEntries) : [];
    case "number":
      return collection.numberEntries ? Object.keys(collection.numberEntries) : [];
    case "category":
      return collection.categoryEntries ? Object.keys(collection.categoryEntries) : [];
    case "date":
      return collection.dateEntries ? Object.keys(collection.dateEntries) : [];
    case "boolean":
      return collection.booleanEntries ? Object.keys(collection.booleanEntries) : [];
    case "percentage":
      return collection.percentageEntries ? Object.keys(collection.percentageEntries) : [];
    case "photo":
      return collection.photoEntries ? Object.keys(collection.photoEntries) : [];
    case "transcription":
      return collection.transcriptionEntries ? Object.keys(collection.transcriptionEntries) : [];
    case "leafAreaIndex":
      return collection.leafAreaIndexEntries ? Object.keys(collection.leafAreaIndexEntries) : [];
    case "ruler":
      return collection.rulerEntries ? Object.keys(collection.rulerEntries) : [];
    case "imageMeasurement":
      return collection.imageMeasurementEntries ? Object.keys(collection.imageMeasurementEntries) : [];
    case "standCountMeasurement":
      return collection.standCountMeasurementEntries ? Object.keys(collection.standCountMeasurementEntries) : [];
    case "rowPlantedStandCount":
      return collection.rowPlantedStandCountEntries ? Object.keys(collection.rowPlantedStandCountEntries) : [];
    case "timer":
      return collection.timerEntries ? Object.keys(collection.timerEntries) : [];
    default:
      return [];
  }
}

/**
 * Get the entry config ID from a collection reference.
 */
function getEntryConfigIdFromCollection(collection: EntryCollection): string {
  return typeof collection.entryConfigRef === "string" ? collection.entryConfigRef : collection.entryConfigRef.id;
}

/**
 * Get the createdBy path from a collection, if present.
 */
function getCollectionCreatorPath(collection: EntryCollection): string | null {
  if (!collection.createdBy) return null;
  return typeof collection.createdBy === "string" ? collection.createdBy : collection.createdBy.path;
}

/**
 * Get the highest sample slot used by a collection.
 * Falls back to sample count if keys are not numeric.
 */
function getCollectionMaxSampleNumber(collection: EntryCollection): number {
  const sampleKeys = getCollectionSampleKeys(collection);
  if (sampleKeys.length === 0) return 0;

  let maxNumericSample = 0;
  for (const sampleKey of sampleKeys) {
    const parsed = Number(sampleKey);
    if (Number.isFinite(parsed)) {
      maxNumericSample = Math.max(maxNumericSample, parsed);
    }
  }

  return Math.max(maxNumericSample, sampleKeys.length);
}

/**
 * Build a stable collector identity for wide-format columns.
 */
function getCollectorIdentity(
  collection: EntryCollection,
  userEmailCache: Map<string, string>
): { collectorKey: string; collectorLabel: string; sortLabel: string } {
  const creatorPath = getCollectionCreatorPath(collection);
  if (creatorPath) {
    const fallbackLabel = creatorPath.split("/")[1] || creatorPath;
    const collectorLabel = userEmailCache.get(creatorPath) || fallbackLabel;
    return {
      collectorKey: creatorPath,
      collectorLabel,
      sortLabel: collectorLabel.toLowerCase(),
    };
  }

  const fallback = collection.id || "unknown";
  return {
    collectorKey: `collection:${fallback}`,
    collectorLabel: fallback,
    sortLabel: fallback.toLowerCase(),
  };
}

function qualifyHeaderWithCollector(header: string, collectorLabel?: string): string {
  return collectorLabel ? `${header} (${collectorLabel})` : header;
}

function buildSampleHeader(
  traitName: string,
  sampleNum: number,
  maxSamples: number,
  suffix: string = "",
  forceSampleLabel: boolean = false,
  collectorLabel?: string
): string {
  const baseHeader =
    forceSampleLabel || maxSamples > 1
      ? `${traitName} Sample ${sampleNum}${suffix ? ` ${suffix}` : ""}`
      : `${traitName}${suffix ? ` ${suffix}` : ""}`;
  return qualifyHeaderWithCollector(baseHeader, collectorLabel);
}

const ADDITIONAL_MEASUREMENT_SAMPLE_PREFIX = "additionalMeasurement:";
const ADDITIONAL_MEASUREMENT_PLOT_PREFIX = "plotAdditionalMeasurement:";
const ADDITIONAL_MEASUREMENT_OBJECT_PREFIX = "objAdditionalMeasurement:";

const buildAdditionalMeasurementSampleSubField = (descriptor: MeasurementDescriptor): string =>
  `${ADDITIONAL_MEASUREMENT_SAMPLE_PREFIX}${descriptor.key}`;

const buildAdditionalMeasurementPlotSubField = (descriptor: MeasurementDescriptor): string =>
  `${ADDITIONAL_MEASUREMENT_PLOT_PREFIX}${descriptor.key}`;

const buildAdditionalMeasurementObjectSubField = (descriptor: MeasurementDescriptor): string =>
  `${ADDITIONAL_MEASUREMENT_OBJECT_PREFIX}${descriptor.key}`;

const parseAdditionalMeasurementSubField = (subField: string | undefined, prefix: string): string | null =>
  subField?.startsWith(prefix) ? subField.slice(prefix.length) : null;

function descriptorUnitSuffix(descriptor: MeasurementDescriptor): string {
  const unit = getDescriptorDisplayUnit(descriptor);
  return unit ? ` (${unit})` : "";
}

function descriptorSampleSuffix(descriptor: MeasurementDescriptor): string {
  const prefix = descriptor.aggregation === "mean" ? "Mean" : "Total";
  return `${prefix} ${descriptor.label}${descriptorUnitSuffix(descriptor)}`;
}

function descriptorPlotSuffix(descriptor: MeasurementDescriptor): string {
  const prefix = descriptor.aggregation === "mean" ? "Total Mean" : "Total";
  return `${prefix} ${descriptor.label}${descriptorUnitSuffix(descriptor)}`;
}

function descriptorObjectSuffix(descriptor: MeasurementDescriptor): string {
  return `Obj ${descriptor.label}s${descriptorUnitSuffix(descriptor)}`;
}

function getBoundDescriptor(config: EntryConfig, binding: CanonicalBinding): MeasurementDescriptor | undefined {
  return getDescriptorsForEntryConfig(config).find((descriptor) => descriptor.binding === binding);
}

function getBoundMeasurementSuffix(config: EntryConfig, binding: CanonicalBinding, legacySuffix: string): string {
  const descriptor = getBoundDescriptor(config, binding);
  if (!descriptor || !hasCustomLabel(descriptor)) return legacySuffix;
  const defaultLabel = binding[0].toUpperCase() + binding.slice(1);
  return legacySuffix.replace(defaultLabel, descriptor.label);
}

function findUnboundDescriptor(config: EntryConfig | undefined, key: string | null): MeasurementDescriptor | undefined {
  if (!config || !key) return undefined;
  return getUnboundDescriptors(config).find((descriptor) => descriptor.key === key);
}

function formatDescriptorDisplayValueForCsv(value: number, descriptor: MeasurementDescriptor): string | number {
  const unit = getDescriptorDisplayUnit(descriptor);
  if (unit === "cm") return formatCentimetersForCsv(value);
  if (unit === "g") return roundToDecimalPlaces(value, 1);
  if (descriptor.unit === "count") return Math.round(value);
  return value;
}

function formatDescriptorValueForCsv(value: number, descriptor: MeasurementDescriptor): string | number {
  return formatDescriptorDisplayValueForCsv(convertDescriptorValueForDisplay(value, descriptor), descriptor);
}

function roundDescriptorValueForJsonArray(value: number, descriptor: MeasurementDescriptor): number {
  const displayValue = convertDescriptorValueForDisplay(value, descriptor);
  const unit = getDescriptorDisplayUnit(descriptor);
  if (unit === "cm") return roundCentimeters(displayValue);
  if (unit === "g") return roundToDecimalPlaces(displayValue, 1);
  if (descriptor.unit === "count") return Math.round(displayValue);
  return displayValue;
}

function hasStandCountMetricValue(
  collections: EntryCollection[],
  metric: "avgLength" | "avgWidth" | "totalWeight"
): boolean {
  const field =
    metric === "avgLength" ? "avgLengthMeters" : metric === "avgWidth" ? "avgWidthMeters" : "totalWeightGrams";

  return collections.some((collection) => {
    if (collection.type !== "standCountMeasurement" || !collection.standCountMeasurementEntries) return false;
    return Object.values(collection.standCountMeasurementEntries).some(
      (entry) => entry.imageMeasurement?.[field] != null
    );
  });
}

function buildTraitColumnLayouts(
  entryConfigs: EntryConfig[],
  entryCollections: EntryCollection[],
  userEmailCache: Map<string, string>
): Map<string, TraitColumnLayout> {
  const layouts = new Map<string, TraitColumnLayout>();

  for (const config of entryConfigs) {
    const configId = config.id || "";
    const relevantCollections = entryCollections.filter(
      (collection) => getEntryConfigIdFromCollection(collection) === configId
    );

    const collectorsByKey = new Map<string, CollectorColumnLayout & { sortLabel: string }>();
    let maxSamples = 0;

    for (const collection of relevantCollections) {
      const sampleCount = getCollectionMaxSampleNumber(collection);
      if (sampleCount === 0) continue;

      maxSamples = Math.max(maxSamples, sampleCount);

      const collectorIdentity = getCollectorIdentity(collection, userEmailCache);
      const existingCollector = collectorsByKey.get(collectorIdentity.collectorKey);
      if (existingCollector) {
        existingCollector.maxSamples = Math.max(existingCollector.maxSamples, sampleCount);
      } else {
        collectorsByKey.set(collectorIdentity.collectorKey, {
          collectorKey: collectorIdentity.collectorKey,
          collectorLabel: collectorIdentity.collectorLabel,
          maxSamples: sampleCount,
          sortLabel: collectorIdentity.sortLabel,
        });
      }
    }

    const collectors = Array.from(collectorsByKey.values())
      .sort(
        (a, b) =>
          a.sortLabel.localeCompare(b.sortLabel) ||
          a.collectorLabel.localeCompare(b.collectorLabel) ||
          a.collectorKey.localeCompare(b.collectorKey)
      )
      .map(({ sortLabel: _sortLabel, ...collector }) => collector);

    layouts.set(configId, {
      config,
      configId,
      collections: relevantCollections,
      maxSamples,
      useCollectorColumns: config.type !== "transcription" && collectors.length > 1,
      collectors,
      hasAnyExtractedText: relevantCollections.some(hasExtractedImageTextInCollection),
      hasStandCountAvgLength: hasStandCountMetricValue(relevantCollections, "avgLength"),
      hasStandCountAvgWidth: hasStandCountMetricValue(relevantCollections, "avgWidth"),
      hasStandCountWeight: hasStandCountMetricValue(relevantCollections, "totalWeight"),
    });
  }

  return layouts;
}

function getCollectorColumnTargets(layout: TraitColumnLayout): CollectorColumnTarget[] {
  if (layout.useCollectorColumns) {
    return layout.collectors
      .filter((collector) => collector.maxSamples > 0)
      .map((collector) => ({
        collectorKey: collector.collectorKey,
        collectorLabel: collector.collectorLabel,
        maxSamples: collector.maxSamples,
        forceSampleLabel: true,
      }));
  }

  if (layout.maxSamples === 0) return [];

  return [
    {
      maxSamples: layout.maxSamples,
      forceSampleLabel: false,
    },
  ];
}

/**
 * Extract individual measurements from metadata for a specific sample
 * Returns a JSON array string (e.g., "[15.2, 12.8, 14.5]") or empty string
 */
function extractIndividualMeasurementsFromMetadata(
  metadata: CaptureMetadata | null,
  measurementType: "length" | "width" | "mass"
): string {
  const visibleAnnotations = extractVisibleAnnotations(metadata);

  if (visibleAnnotations.length === 0) {
    return "";
  }

  // Extract specific measurement from each annotation
  const measurements = visibleAnnotations
    .map((annotation) => {
      const physicalMeasurements = annotation.measurements;
      if (!physicalMeasurements) return null;

      switch (measurementType) {
        case "length":
          // Convert meters to centimeters
          return physicalMeasurements.lengthM != null ? metersToRoundedCentimeters(physicalMeasurements.lengthM) : null;
        case "width":
          // Convert meters to centimeters
          return physicalMeasurements.widthM != null ? metersToRoundedCentimeters(physicalMeasurements.widthM) : null;
        case "mass":
          // Already in grams
          return physicalMeasurements.massG != null ? Math.round(physicalMeasurements.massG * 10) / 10 : null;
        default:
          return null;
      }
    })
    .filter((value) => value !== null);

  // Return JSON array string
  return measurements.length > 0 ? JSON.stringify(measurements) : "";
}

function extractAdditionalMeasurementsFromMetadata(
  metadata: CaptureMetadata | null,
  descriptor: MeasurementDescriptor
): string {
  const visibleAnnotations = extractVisibleAnnotations(metadata);

  if (visibleAnnotations.length === 0) {
    return "";
  }

  const measurements = visibleAnnotations
    .map((annotation) => {
      const value = annotation.measurements?.additionalMeasurements?.[descriptor.key];
      return value != null ? roundDescriptorValueForJsonArray(value, descriptor) : null;
    })
    .filter((value) => value !== null);

  return measurements.length > 0 ? JSON.stringify(measurements) : "";
}

/**
 * Generate column definitions for individual object measurements
 * Creates columns for JSON arrays of lengths, widths, and optionally weights
 */
function generateIndividualMeasurementColumns(
  config: EntryConfig,
  maxSamples: number,
  hasWeightMeasurement: boolean,
  unboundDescriptors: MeasurementDescriptor[],
  collectorKey?: string,
  collectorLabel?: string,
  forceSampleLabel: boolean = false
): CSVColumn[] {
  const columns: CSVColumn[] = [];
  const traitName = config.name;
  const configId = config.id || "";

  for (let sampleNum = 1; sampleNum <= maxSamples; sampleNum++) {
    const sampleKey = String(sampleNum);

    for (const descriptor of unboundDescriptors) {
      columns.push({
        header: buildSampleHeader(
          traitName,
          sampleNum,
          maxSamples,
          descriptorObjectSuffix(descriptor),
          forceSampleLabel,
          collectorLabel
        ),
        entryConfigId: configId,
        subField: buildAdditionalMeasurementObjectSubField(descriptor),
        sampleKey,
        collectorKey,
        isIndividualMeasurement: true,
      });
    }

    // Column for lengths array
    const lengthHeader = buildSampleHeader(
      traitName,
      sampleNum,
      maxSamples,
      getBoundMeasurementSuffix(config, "length", "Obj Lengths (cm)"),
      forceSampleLabel,
      collectorLabel
    );
    columns.push({
      header: lengthHeader,
      entryConfigId: configId,
      subField: "objLengths",
      sampleKey,
      collectorKey,
      isIndividualMeasurement: true,
    });

    // Column for widths array
    const widthHeader = buildSampleHeader(
      traitName,
      sampleNum,
      maxSamples,
      getBoundMeasurementSuffix(config, "width", "Obj Widths (cm)"),
      forceSampleLabel,
      collectorLabel
    );
    columns.push({
      header: widthHeader,
      entryConfigId: configId,
      subField: "objWidths",
      sampleKey,
      collectorKey,
      isIndividualMeasurement: true,
    });

    // Column for weights array (only if hasWeightMeasurement is true)
    if (hasWeightMeasurement) {
      const weightHeader = buildSampleHeader(
        traitName,
        sampleNum,
        maxSamples,
        getBoundMeasurementSuffix(config, "weight", "Obj Weights (g)"),
        forceSampleLabel,
        collectorLabel
      );
      columns.push({
        header: weightHeader,
        entryConfigId: configId,
        subField: "objWeights",
        sampleKey,
        collectorKey,
        isIndividualMeasurement: true,
      });
    }
  }

  return columns;
}

/**
 * Sanitize OCR-extracted text for CSV export.
 * Replaces newlines with spaces and trims whitespace. PapaParse handles quote escaping.
 */
function sanitizeExtractedTextForCsv(text?: string): string {
  if (!text) return "";
  return text.replace(/[\r\n]+/g, " ").trim();
}

/**
 * Check if any entry in a collection has extractedImageText set.
 */
function hasExtractedImageTextInCollection(collection: EntryCollection): boolean {
  const checkEntries = (entries?: Record<string, { extractedImageText?: string }>): boolean => {
    if (!entries) return false;
    return Object.values(entries).some((entry) => !!entry.extractedImageText);
  };

  return (
    checkEntries(collection.photoEntries) ||
    checkEntries(collection.textEntries) ||
    checkEntries(collection.numberEntries) ||
    checkEntries(collection.categoryEntries) ||
    checkEntries(collection.dateEntries) ||
    checkEntries(collection.booleanEntries) ||
    checkEntries(collection.percentageEntries) ||
    checkEntries(collection.leafAreaIndexEntries) ||
    checkEntries(collection.imageMeasurementEntries) ||
    checkEntries(collection.standCountMeasurementEntries) ||
    checkEntries(collection.rulerEntries)
  );
}

/**
 * Generate CSV header columns based on plot metadata and entry configs (traits)
 */
function generateHeaderColumns(
  experiment: Experiment,
  entryConfigs: EntryConfig[],
  entryCollections: EntryCollection[],
  mediaOptions: MediaDownloadOptions = ALL_MEDIA_OPTIONS,
  metadataCache?: Map<string, CaptureMetadata | null>,
  userEmailCache: Map<string, string> = new Map()
): CSVColumn[] {
  const columns: CSVColumn[] = [];

  // Dynamic plot metadata columns from experiment plotMap
  if (experiment.plotMap?.headers) {
    for (const header of experiment.plotMap.headers) {
      columns.push({ header });
    }
  } else if (experiment.plotMap?.plotIDName) {
    columns.push({ header: experiment.plotMap.plotIDName });
  } else {
    // Fallback: no plotMap at all (grid/sequence experiments created before fix)
    columns.push({ header: "Plot ID" });
  }

  const traitLayouts = buildTraitColumnLayouts(entryConfigs, entryCollections, userEmailCache);

  // Dynamic trait columns based on entry configs
  // First pass: Add all value columns
  for (const config of entryConfigs) {
    const configId = config.id || "";
    const layout = traitLayouts.get(configId);

    // Skip if no samples exist for this trait
    if (!layout || layout.maxSamples === 0) continue;

    const traitName = config.name;
    const columnTargets = getCollectorColumnTargets(layout);

    switch (config.type) {
      case "text":
      case "number":
      case "category":
      case "boolean":
      case "percentage":
      case "timer":
      case "date": {
        // Create value columns only (image paths added in second pass).
        // Timer is a scalar single-value-per-sample type (one duration column);
        // its value is exported in seconds, so label the column header "(s)".
        // The other scalar types in this group carry no unit suffix.
        const unitSuffix = config.type === "timer" ? "(s)" : "";
        for (const target of columnTargets) {
          for (let sampleNum = 1; sampleNum <= target.maxSamples; sampleNum++) {
            const sampleKey = String(sampleNum);
            columns.push({
              header: buildSampleHeader(
                traitName,
                sampleNum,
                target.maxSamples,
                unitSuffix,
                target.forceSampleLabel,
                target.collectorLabel
              ),
              entryConfigId: configId,
              sampleKey,
              collectorKey: target.collectorKey,
            });
          }
        }
        break;
      }

      case "photo": {
        // Photo has no value columns, only image paths (added in second pass)
        break;
      }

      case "transcription": {
        const textHeader = traitName;
        columns.push({ header: textHeader, entryConfigId: configId, subField: "combinedText" });
        const audioHeader = `${traitName} Audio Path`;
        columns.push({
          header: audioHeader,
          entryConfigId: configId,
          subField: "combinedAudioPath",
          isImagePath: true,
        });
        break;
      }

      case "leafAreaIndex": {
        // Create value columns only (image paths added in second pass)
        for (const target of columnTargets) {
          for (let sampleNum = 1; sampleNum <= target.maxSamples; sampleNum++) {
            const sampleKey = String(sampleNum);
            columns.push({
              header: buildSampleHeader(
                traitName,
                sampleNum,
                target.maxSamples,
                "",
                target.forceSampleLabel,
                target.collectorLabel
              ),
              entryConfigId: configId,
              subField: "value",
              sampleKey,
              collectorKey: target.collectorKey,
            });
          }
        }
        break;
      }

      case "ruler": {
        // Create ruler measurement column per sample
        for (const target of columnTargets) {
          for (let sampleNum = 1; sampleNum <= target.maxSamples; sampleNum++) {
            const sampleKey = String(sampleNum);
            columns.push({
              header: buildSampleHeader(
                traitName,
                sampleNum,
                target.maxSamples,
                "(m)",
                target.forceSampleLabel,
                target.collectorLabel
              ),
              entryConfigId: configId,
              subField: "measurement",
              sampleKey,
              collectorKey: target.collectorKey,
            });
          }
        }
        break;
      }

      case "imageMeasurement": {
        // Check if this config has weight measurement enabled
        const hasWeightMeasurement =
          config.imageMeasurementConfig?.imageMeasurementModelOptions?.hasWeightMeasurement ?? false;
        const unboundDescriptors = getUnboundDescriptors(config);

        // Create columns for count, mean length, mean width, and optionally total weight per sample
        for (const target of columnTargets) {
          for (let sampleNum = 1; sampleNum <= target.maxSamples; sampleNum++) {
            const sampleKey = String(sampleNum);

            for (const descriptor of unboundDescriptors) {
              columns.push({
                header: buildSampleHeader(
                  traitName,
                  sampleNum,
                  target.maxSamples,
                  descriptorSampleSuffix(descriptor),
                  target.forceSampleLabel,
                  target.collectorLabel
                ),
                entryConfigId: configId,
                subField: buildAdditionalMeasurementSampleSubField(descriptor),
                sampleKey,
                collectorKey: target.collectorKey,
              });
            }

            columns.push({
              header: buildSampleHeader(
                traitName,
                sampleNum,
                target.maxSamples,
                "Count",
                target.forceSampleLabel,
                target.collectorLabel
              ),
              entryConfigId: configId,
              subField: "count",
              sampleKey,
              collectorKey: target.collectorKey,
            });
            columns.push({
              header: buildSampleHeader(
                traitName,
                sampleNum,
                target.maxSamples,
                getBoundMeasurementSuffix(config, "length", "Mean Length (cm)"),
                target.forceSampleLabel,
                target.collectorLabel
              ),
              entryConfigId: configId,
              subField: "length",
              sampleKey,
              collectorKey: target.collectorKey,
            });
            columns.push({
              header: buildSampleHeader(
                traitName,
                sampleNum,
                target.maxSamples,
                getBoundMeasurementSuffix(config, "width", "Mean Width (cm)"),
                target.forceSampleLabel,
                target.collectorLabel
              ),
              entryConfigId: configId,
              subField: "width",
              sampleKey,
              collectorKey: target.collectorKey,
            });

            if (hasWeightMeasurement) {
              columns.push({
                header: buildSampleHeader(
                  traitName,
                  sampleNum,
                  target.maxSamples,
                  getBoundMeasurementSuffix(config, "weight", "Total Weight (g)"),
                  target.forceSampleLabel,
                  target.collectorLabel
                ),
                entryConfigId: configId,
                subField: "weight",
                sampleKey,
                collectorKey: target.collectorKey,
              });
            }
          }
          const individualColumns = generateIndividualMeasurementColumns(
            config,
            target.maxSamples,
            hasWeightMeasurement,
            unboundDescriptors,
            target.collectorKey,
            target.collectorLabel,
            target.forceSampleLabel
          );
          columns.push(...individualColumns);
        }

        for (const descriptor of unboundDescriptors) {
          columns.push({
            header: `${traitName} ${descriptorPlotSuffix(descriptor)}`,
            entryConfigId: configId,
            subField: buildAdditionalMeasurementPlotSubField(descriptor),
          });
        }
        columns.push({ header: `${traitName} Total Count`, entryConfigId: configId, subField: "plotCount" });
        columns.push({
          header: `${traitName} ${getBoundMeasurementSuffix(config, "length", "Total Mean Length (cm)")}`,
          entryConfigId: configId,
          subField: "plotLength",
        });
        columns.push({
          header: `${traitName} ${getBoundMeasurementSuffix(config, "width", "Total Mean Width (cm)")}`,
          entryConfigId: configId,
          subField: "plotWidth",
        });
        if (hasWeightMeasurement) {
          columns.push({
            header: `${traitName} ${getBoundMeasurementSuffix(config, "weight", "Total Weight (g)")}`,
            entryConfigId: configId,
            subField: "plotWeight",
          });
        }

        // Grading columns — one per bucket plus ungraded + dominant summary,
        // emitted only when the trait has grading configured.
        const grading = config.imageMeasurementConfig?.grading;
        if (grading && grading.buckets.length > 0) {
          for (const bucket of grading.buckets) {
            columns.push({
              header: `${traitName} Grade ${bucket.label} Count`,
              entryConfigId: configId,
              subField: `plotGradeCount:${bucket.id}`,
            });
          }
          columns.push({
            header: `${traitName} Grade Ungraded Count`,
            entryConfigId: configId,
            subField: "plotGradeUngraded",
          });
          columns.push({
            header: `${traitName} Grade Dominant`,
            entryConfigId: configId,
            subField: "plotGradeDominant",
          });
          columns.push({
            header: `${traitName} Grade Dominant %`,
            entryConfigId: configId,
            subField: "plotGradeDominantPct",
          });
        }

        break;
      }

      case "standCountMeasurement": {
        for (const target of columnTargets) {
          for (let sampleNum = 1; sampleNum <= target.maxSamples; sampleNum++) {
            const sampleKey = String(sampleNum);

            columns.push({
              header: buildSampleHeader(
                traitName,
                sampleNum,
                target.maxSamples,
                "Raw Count",
                target.forceSampleLabel,
                target.collectorLabel
              ),
              entryConfigId: configId,
              subField: "rawCount",
              sampleKey,
              collectorKey: target.collectorKey,
            });
            columns.push({
              header: buildSampleHeader(
                traitName,
                sampleNum,
                target.maxSamples,
                "Count / m²",
                target.forceSampleLabel,
                target.collectorLabel
              ),
              entryConfigId: configId,
              subField: "countPerM2",
              sampleKey,
              collectorKey: target.collectorKey,
            });
            columns.push({
              header: buildSampleHeader(
                traitName,
                sampleNum,
                target.maxSamples,
                "Plants / acre",
                target.forceSampleLabel,
                target.collectorLabel
              ),
              entryConfigId: configId,
              subField: "plantsPerAcre",
              sampleKey,
              collectorKey: target.collectorKey,
            });

            if (layout.hasStandCountAvgLength) {
              columns.push({
                header: buildSampleHeader(
                  traitName,
                  sampleNum,
                  target.maxSamples,
                  "Avg. Length (cm)",
                  target.forceSampleLabel,
                  target.collectorLabel
                ),
                entryConfigId: configId,
                subField: "avgLength",
                sampleKey,
                collectorKey: target.collectorKey,
              });
            }

            if (layout.hasStandCountAvgWidth) {
              columns.push({
                header: buildSampleHeader(
                  traitName,
                  sampleNum,
                  target.maxSamples,
                  "Avg. Width (cm)",
                  target.forceSampleLabel,
                  target.collectorLabel
                ),
                entryConfigId: configId,
                subField: "avgWidth",
                sampleKey,
                collectorKey: target.collectorKey,
              });
            }

            if (layout.hasStandCountWeight) {
              columns.push({
                header: buildSampleHeader(
                  traitName,
                  sampleNum,
                  target.maxSamples,
                  "Total Weight (g)",
                  target.forceSampleLabel,
                  target.collectorLabel
                ),
                entryConfigId: configId,
                subField: "weight",
                sampleKey,
                collectorKey: target.collectorKey,
              });
            }
          }
        }

        if (layout.maxSamples > 1 || layout.useCollectorColumns) {
          columns.push({
            header: `${traitName} Total Raw Count`,
            entryConfigId: configId,
            subField: "totalRawCount",
            sampleKey: "total",
          });
          columns.push({
            header: `${traitName} Total Count / m²`,
            entryConfigId: configId,
            subField: "totalCountPerM2",
            sampleKey: "total",
          });
          columns.push({
            header: `${traitName} Total Plants / acre`,
            entryConfigId: configId,
            subField: "totalPlantsPerAcre",
            sampleKey: "total",
          });
        }
        if (layout.hasStandCountAvgLength) {
          columns.push({
            header: `${traitName} Total Avg. Length (cm)`,
            entryConfigId: configId,
            subField: "plotAvgLength",
          });
        }
        if (layout.hasStandCountAvgWidth) {
          columns.push({
            header: `${traitName} Total Avg. Width (cm)`,
            entryConfigId: configId,
            subField: "plotAvgWidth",
          });
        }
        if (layout.hasStandCountWeight) {
          columns.push({ header: `${traitName} Total Weight (g)`, entryConfigId: configId, subField: "plotWeight" });
        }
        break;
      }

      case "rowPlantedStandCount": {
        for (const target of columnTargets) {
          for (let sampleNum = 1; sampleNum <= target.maxSamples; sampleNum++) {
            const sampleKey = String(sampleNum);

            columns.push({
              header: buildSampleHeader(
                traitName,
                sampleNum,
                target.maxSamples,
                "Plant Count",
                target.forceSampleLabel,
                target.collectorLabel
              ),
              entryConfigId: configId,
              subField: "plantCount",
              sampleKey,
              collectorKey: target.collectorKey,
            });
            columns.push({
              header: buildSampleHeader(
                traitName,
                sampleNum,
                target.maxSamples,
                "Walk Distance (m)",
                target.forceSampleLabel,
                target.collectorLabel
              ),
              entryConfigId: configId,
              subField: "walkDistance",
              sampleKey,
              collectorKey: target.collectorKey,
            });
            columns.push({
              header: buildSampleHeader(
                traitName,
                sampleNum,
                target.maxSamples,
                "Plants/m",
                target.forceSampleLabel,
                target.collectorLabel
              ),
              entryConfigId: configId,
              subField: "plantsPerMeter",
              sampleKey,
              collectorKey: target.collectorKey,
            });
            columns.push({
              header: buildSampleHeader(
                traitName,
                sampleNum,
                target.maxSamples,
                "Avg. Spacing (cm)",
                target.forceSampleLabel,
                target.collectorLabel
              ),
              entryConfigId: configId,
              subField: "avgSpacing",
              sampleKey,
              collectorKey: target.collectorKey,
            });
            columns.push({
              header: buildSampleHeader(
                traitName,
                sampleNum,
                target.maxSamples,
                "Avg. Length (cm)",
                target.forceSampleLabel,
                target.collectorLabel
              ),
              entryConfigId: configId,
              subField: "avgDiameter",
              sampleKey,
              collectorKey: target.collectorKey,
            });
            columns.push({
              header: buildSampleHeader(
                traitName,
                sampleNum,
                target.maxSamples,
                "Avg. Width (cm)",
                target.forceSampleLabel,
                target.collectorLabel
              ),
              entryConfigId: configId,
              subField: "avgWidth",
              sampleKey,
              collectorKey: target.collectorKey,
            });
            columns.push({
              header: buildSampleHeader(
                traitName,
                sampleNum,
                target.maxSamples,
                "Avg. Greenness",
                target.forceSampleLabel,
                target.collectorLabel
              ),
              entryConfigId: configId,
              subField: "avgGreenness",
              sampleKey,
              collectorKey: target.collectorKey,
            });
          }

          const rpscIndividualColumns = generateIndividualMeasurementColumns(
            config,
            target.maxSamples,
            false,
            [],
            target.collectorKey,
            target.collectorLabel,
            target.forceSampleLabel
          );
          columns.push(...rpscIndividualColumns);
        }

        columns.push({
          header: `${traitName} Total Plant Count`,
          entryConfigId: configId,
          subField: "totalPlantCount",
        });
        columns.push({
          header: `${traitName} Total Walk Distance (m)`,
          entryConfigId: configId,
          subField: "totalWalkDistance",
        });
        columns.push({
          header: `${traitName} Total Plants/m`,
          entryConfigId: configId,
          subField: "plotPlantsPerMeter",
        });
        columns.push({
          header: `${traitName} Total Avg. Spacing (cm)`,
          entryConfigId: configId,
          subField: "plotAvgSpacing",
        });
        columns.push({
          header: `${traitName} Total Avg. Length (cm)`,
          entryConfigId: configId,
          subField: "plotAvgDiameter",
        });
        columns.push({
          header: `${traitName} Total Avg. Width (cm)`,
          entryConfigId: configId,
          subField: "plotAvgWidth",
        });
        columns.push({
          header: `${traitName} Total Avg. Greenness`,
          entryConfigId: configId,
          subField: "plotAvgGreenness",
        });

        break;
      }

      default:
        // Fallback for unknown types
        columns.push({ header: traitName, entryConfigId: configId });
    }
  }

  // Second pass: Add all image/media path columns. Every media column is gated by
  // its own media option in the filter at the end of this function (original images
  // -> includeOriginalImages, annotated -> includeAnnotatedImages, audio -> includeAudio),
  // so build them all here regardless of selection.
  {
    for (const config of entryConfigs) {
      const traitName = config.name;
      const configId = config.id || "";
      const layout = traitLayouts.get(configId);

      // Skip if no samples exist for this trait
      if (!layout || layout.maxSamples === 0) continue;

      const columnTargets = getCollectorColumnTargets(layout);

      switch (config.type) {
        case "text":
        case "number":
        case "category":
        case "boolean":
        case "percentage":
        case "date": {
          // Create image path columns
          for (const target of columnTargets) {
            for (let sampleNum = 1; sampleNum <= target.maxSamples; sampleNum++) {
              const sampleKey = String(sampleNum);
              columns.push({
                header: buildSampleHeader(
                  traitName,
                  sampleNum,
                  target.maxSamples,
                  "Image Path",
                  target.forceSampleLabel,
                  target.collectorLabel
                ),
                entryConfigId: configId,
                subField: "imagePath",
                sampleKey,
                collectorKey: target.collectorKey,
                isImagePath: true,
              });
            }
          }
          break;
        }

        case "photo": {
          // Create image path column per sample
          for (const target of columnTargets) {
            for (let sampleNum = 1; sampleNum <= target.maxSamples; sampleNum++) {
              const sampleKey = String(sampleNum);
              columns.push({
                header: buildSampleHeader(
                  traitName,
                  sampleNum,
                  target.maxSamples,
                  "Image Path",
                  target.forceSampleLabel,
                  target.collectorLabel
                ),
                entryConfigId: configId,
                subField: "imagePath",
                sampleKey,
                collectorKey: target.collectorKey,
                isImagePath: true,
              });
            }
          }
          break;
        }

        case "transcription": {
          // Already added a single combined audio path column in first pass
          break;
        }

        case "leafAreaIndex": {
          // Create image path columns
          for (const target of columnTargets) {
            for (let sampleNum = 1; sampleNum <= target.maxSamples; sampleNum++) {
              const sampleKey = String(sampleNum);
              columns.push({
                header: buildSampleHeader(
                  traitName,
                  sampleNum,
                  target.maxSamples,
                  "Image Path",
                  target.forceSampleLabel,
                  target.collectorLabel
                ),
                entryConfigId: configId,
                subField: "originalImage",
                sampleKey,
                collectorKey: target.collectorKey,
                isImagePath: true,
              });
              columns.push({
                header: buildSampleHeader(
                  traitName,
                  sampleNum,
                  target.maxSamples,
                  "Annotated Image Path",
                  target.forceSampleLabel,
                  target.collectorLabel
                ),
                entryConfigId: configId,
                subField: "annotatedImage",
                sampleKey,
                collectorKey: target.collectorKey,
                isImagePath: true,
              });
            }
          }
          break;
        }

        case "ruler": {
          // Create image path columns
          for (const target of columnTargets) {
            for (let sampleNum = 1; sampleNum <= target.maxSamples; sampleNum++) {
              const sampleKey = String(sampleNum);
              columns.push({
                header: buildSampleHeader(
                  traitName,
                  sampleNum,
                  target.maxSamples,
                  "Image Path",
                  target.forceSampleLabel,
                  target.collectorLabel
                ),
                entryConfigId: configId,
                subField: "imagePath",
                sampleKey,
                collectorKey: target.collectorKey,
                isImagePath: true,
              });
            }
          }
          break;
        }

        case "imageMeasurement":
        case "standCountMeasurement": {
          for (const target of columnTargets) {
            for (let sampleNum = 1; sampleNum <= target.maxSamples; sampleNum++) {
              const sampleKey = String(sampleNum);
              columns.push({
                header: buildSampleHeader(
                  traitName,
                  sampleNum,
                  target.maxSamples,
                  "Image Path",
                  target.forceSampleLabel,
                  target.collectorLabel
                ),
                entryConfigId: configId,
                subField: "imagePath",
                sampleKey,
                collectorKey: target.collectorKey,
                isImagePath: true,
              });
            }
          }
          break;
        }

        case "rowPlantedStandCount": {
          // rowPlantedStandCount stores a dictionary of media captures per sample.
          // Emit a single semicolon-delimited "Image Paths" column per sample.
          for (const target of columnTargets) {
            for (let sampleNum = 1; sampleNum <= target.maxSamples; sampleNum++) {
              const sampleKey = String(sampleNum);
              columns.push({
                header: buildSampleHeader(
                  traitName,
                  sampleNum,
                  target.maxSamples,
                  "Image Paths",
                  target.forceSampleLabel,
                  target.collectorLabel
                ),
                entryConfigId: configId,
                subField: "imagePaths",
                sampleKey,
                collectorKey: target.collectorKey,
                isImagePath: true,
              });
            }
          }
          break;
        }

        default:
          // No image paths for unknown types
          break;
      }
    }
  }

  // Third pass: Add extracted text columns for entry types that support OCR
  // Placed after image path columns so extracted text appears next to its associated image
  for (const config of entryConfigs) {
    const traitName = config.name;
    const configId = config.id || "";
    const layout = traitLayouts.get(configId);

    // Skip if no samples, or if type doesn't support OCR
    if (!layout || layout.maxSamples === 0) continue;
    if (config.type === "transcription" || config.type === "rowPlantedStandCount") continue;
    if (!layout.hasAnyExtractedText) continue;

    for (const target of getCollectorColumnTargets(layout)) {
      for (let sampleNum = 1; sampleNum <= target.maxSamples; sampleNum++) {
        const sampleKey = String(sampleNum);
        columns.push({
          header: buildSampleHeader(
            traitName,
            sampleNum,
            target.maxSamples,
            "Extracted Text",
            target.forceSampleLabel,
            target.collectorLabel
          ),
          entryConfigId: configId,
          subField: "extractedText",
          sampleKey,
          collectorKey: target.collectorKey,
          isExtractedText: true,
        });
      }
    }
  }

  // Last Updated column
  columns.push({ header: "Last Updated" });

  // Gate each media path column by its own media option so the CSV never references
  // files that were not added to the ZIP (e.g. dangling media/images paths when the
  // user exports annotated images only). Non-media columns are always kept.
  // Every media/images path column gates on includeOriginalImages. Note "annotatedImage"
  // is the LAI "Annotated Image Path" column: its laiAnnotated artifact is bundled by
  // collectMediaPaths as a normal media/images image (gated by includeOriginalImages).
  // includeAnnotatedImages governs only the separately rendered media/annotated_images
  // files, which have no CSV path column.
  return columns.filter((column) => {
    switch (column.subField) {
      case "imagePath":
      case "originalImage":
      case "imagePaths":
      case "annotatedImage":
        return mediaOptions.includeOriginalImages;
      case "combinedAudioPath":
        return mediaOptions.includeAudio;
      default:
        return true;
    }
  });
}

/**
 * Extract plot metadata fields from a plot based on experiment's plotMap headers
 */
function extractPlotMetadata(plot: Plot, experiment: Experiment): Record<string, string | number> {
  const metadata: Record<string, string | number> = {};

  if (!experiment.plotMap?.headers) {
    if (experiment.plotMap?.plotIDName) {
      metadata[experiment.plotMap.plotIDName] = plot.plotID;
    } else {
      // No plotMap at all — use plot fields directly
      metadata["Plot ID"] = plot.plotID;
      if (plot.row != null) metadata["Row"] = plot.row;
      if (plot.column != null) metadata["Column"] = plot.column;
    }
    return metadata;
  }

  // For each header in the plotMap, extract the corresponding value
  for (const header of experiment.plotMap.headers) {
    // Check if this is the plotIDName
    if (header === experiment.plotMap.plotIDName) {
      metadata[header] = plot.plotID;
    }
    // Check if this is the rowIDName
    else if (header === experiment.plotMap.rowIDName && plot.row != null) {
      metadata[header] = plot.row;
    }
    // Check if this is the colIDName
    else if (header === experiment.plotMap.colIDName && plot.column != null) {
      metadata[header] = plot.column;
    }
    // Otherwise, look in plot.metadata
    else if (plot.metadata && header in plot.metadata) {
      metadata[header] = plot.metadata[header];
    }
    // Default to empty string if not found
    else {
      metadata[header] = "";
    }
  }

  return metadata;
}

/**
 * Get all entry collections for a specific plot
 */
function getPlotEntryCollections(plotId: string, entryCollections: EntryCollection[]): EntryCollection[] {
  return entryCollections.filter((ec) => {
    const plotRefId = typeof ec.plotRef === "string" ? ec.plotRef : ec.plotRef.id;
    return plotRefId === plotId;
  });
}

/**
 * Find all entry collections matching a specific entry config
 * Returns all collections to support multi-user data aggregation
 */
function findEntryCollections(entryCollections: EntryCollection[], entryConfigId: string): EntryCollection[] {
  return entryCollections.filter((ec) => {
    return getEntryConfigIdFromCollection(ec) === entryConfigId;
  });
}

function calculateStandCountTotalsForCsv(
  entries: Array<{
    imageMeasurement?: { objectCount?: number };
    standCountBoxLengthMeters?: number;
    standCountBoxWidthMeters?: number;
    countPerSquareMeter?: number;
  }>
): { rawCount: number | null; countPerM2: number | null } {
  const weightedParts = computeWeightedStandCountParts(
    entries.map((entry) => ({
      countPerSquareMeter: entry.countPerSquareMeter,
      objectCount: entry.imageMeasurement?.objectCount,
      boxLengthM: entry.standCountBoxLengthMeters,
      boxWidthM: entry.standCountBoxWidthMeters,
    }))
  );

  let countPerM2 = weightedParts.totalArea > 0 ? weightedParts.totalCount / weightedParts.totalArea : null;
  if (countPerM2 == null) {
    const simpleValues = entries.map((entry) => entry.countPerSquareMeter).filter((value) => value != null) as number[];
    if (simpleValues.length > 0) {
      countPerM2 = simpleValues.reduce((sum, value) => sum + value, 0) / simpleValues.length;
    }
  }

  let rawCount: number | null = null;
  const rawCounts = entries
    .map((entry) => entry.imageMeasurement?.objectCount)
    .filter((value) => value != null) as number[];
  if (rawCounts.length > 0) {
    rawCount = rawCounts.reduce((sum, value) => sum + value, 0);
  } else if (weightedParts.validEntries > 0) {
    rawCount = Math.round(weightedParts.totalCount);
  } else {
    const simpleValuesWithArea = entries
      .filter(
        (entry) =>
          entry.countPerSquareMeter != null &&
          entry.standCountBoxLengthMeters != null &&
          entry.standCountBoxLengthMeters > 0 &&
          entry.standCountBoxWidthMeters != null &&
          entry.standCountBoxWidthMeters > 0
      )
      .map((entry) => entry.countPerSquareMeter! * entry.standCountBoxLengthMeters! * entry.standCountBoxWidthMeters!);
    if (simpleValuesWithArea.length > 0) {
      rawCount = Math.round(simpleValuesWithArea.reduce((sum, value) => sum + value, 0));
    }
  }

  return {
    rawCount,
    countPerM2,
  };
}

/**
 * Extract value from entry collections based on type, subfield, and sample key
 * Aggregates across all collections to support multi-user data
 */
function extractValue(
  collections: EntryCollection[],
  entryConfigId?: string,
  subField?: string,
  sampleKey?: string,
  metadataCache?: Map<string, CaptureMetadata | null>,
  plotId?: string,
  transcriptionAggregates?: Map<string, TranscriptionAggregate>,
  entryConfigsById?: Map<string, EntryConfig>
): string | number | boolean {
  if (collections.length === 0) return "";

  // Helper to get all entries from the map
  const getAllEntries = <T>(entriesMap: Record<string, T> | undefined): T[] => {
    if (!entriesMap) return [];
    return Object.values(entriesMap);
  };

  // Get a specific entry by sample key from a single collection
  const getEntry = <T>(entriesMap: Record<string, T> | undefined): T | undefined => {
    if (!entriesMap) return undefined;
    const keys = Object.keys(entriesMap);
    if (keys.length === 0) return undefined;

    // If sample key is specified, return that specific sample (undefined if not found)
    if (sampleKey) {
      return entriesMap[sampleKey];
    }

    // Only use fallback when NO sample key specified (aggregation scenarios)
    return entriesMap["1"] || entriesMap[keys[0]];
  };

  // Helper to find most recent collection
  const getMostRecentCollection = (): EntryCollection => {
    return collections.reduce((mostRecent, current) => {
      const mostRecentTime = mostRecent.createdAt?.seconds || 0;
      const currentTime = current.createdAt?.seconds || 0;
      return currentTime > mostRecentTime ? current : mostRecent;
    });
  };

  // Helper to calculate simple average
  const calculateAverage = (values: number[]): number | null => {
    if (values.length === 0) return null;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return sum / values.length;
  };

  const formatFixed = (value: number | null | undefined, digits: number): string => {
    return value != null ? value.toFixed(digits) : "";
  };

  /**
   * Helper to calculate weighted average with single-entry and error handling.
   * Returns formatted string or empty string for error case.
   */
  const calculateWeightedAverageForCsv = <T>(
    entries: T[],
    getMetricValue: (e: T) => number | null | undefined,
    getWeight: (e: T) => number | null | undefined,
    formatValue: (value: number) => string
  ): string => {
    const entriesWithMetric = entries.filter((e) => getMetricValue(e) != null);
    const entriesWithWeight = entries.filter(
      (e) => getMetricValue(e) != null && getWeight(e) != null && getWeight(e)! > 0
    );

    // Single entry case: return value directly without weighting
    if (entriesWithMetric.length === 1) {
      return formatValue(getMetricValue(entriesWithMetric[0])!);
    }

    // Multiple entries with no valid weights: return empty (error case in UI)
    if (entriesWithMetric.length > 1 && entriesWithWeight.length === 0) {
      return "";
    }

    // Multiple entries with valid weights: calculate weighted average
    if (entriesWithWeight.length > 0) {
      const totalWeight = entriesWithWeight.reduce((sum, e) => sum + getWeight(e)!, 0);
      const weightedSum = entriesWithWeight.reduce((sum, e) => sum + getMetricValue(e)! * getWeight(e)!, 0);
      if (totalWeight > 0) {
        return formatValue(weightedSum / totalWeight);
      }
    }

    return "";
  };

  const entryType = collections[0].type;

  switch (entryType) {
    case "text": {
      if (subField === "imagePath") {
        const mostRecent = getMostRecentCollection();
        const textEntry = getEntry(mostRecent.textEntries);
        if (textEntry?.imgCloudPath) {
          const fileName = extractFileNameFromPath(textEntry.imgCloudPath, mostRecent, "text", sampleKey);
          return getImageCsvPath(fileName);
        }
        return "";
      }
      if (subField === "extractedText") {
        const mostRecent = getMostRecentCollection();
        return sanitizeExtractedTextForCsv(getEntry(mostRecent.textEntries)?.extractedImageText);
      }
      // Use most recent value
      const mostRecent = getMostRecentCollection();
      return getEntry(mostRecent.textEntries)?.text || "";
    }

    case "number": {
      if (subField === "imagePath") {
        const mostRecent = getMostRecentCollection();
        const numberEntry = getEntry(mostRecent.numberEntries);
        if (numberEntry?.imgCloudPath) {
          const fileName = extractFileNameFromPath(numberEntry.imgCloudPath, mostRecent, "number", sampleKey);
          return getImageCsvPath(fileName);
        }
        return "";
      }
      if (subField === "extractedText") {
        const mostRecent = getMostRecentCollection();
        return sanitizeExtractedTextForCsv(getEntry(mostRecent.numberEntries)?.extractedImageText);
      }
      // Average values from all collections for this sample key
      const values = collections
        .map((collection) => getEntry(collection.numberEntries)?.val)
        .filter((v) => v != null) as number[];
      const avg = calculateAverage(values);
      return avg != null ? avg : "";
    }

    case "category": {
      if (subField === "imagePath") {
        const mostRecent = getMostRecentCollection();
        const categoryEntry = getEntry(mostRecent.categoryEntries);
        if (categoryEntry?.imgCloudPath) {
          const fileName = extractFileNameFromPath(categoryEntry.imgCloudPath, mostRecent, "category", sampleKey);
          return getImageCsvPath(fileName);
        }
        return "";
      }
      if (subField === "extractedText") {
        const mostRecent = getMostRecentCollection();
        return sanitizeExtractedTextForCsv(getEntry(mostRecent.categoryEntries)?.extractedImageText);
      }
      // Use most recent value - export as JSON array for multi-select support
      const mostRecent = getMostRecentCollection();
      const selections = getEntry(mostRecent.categoryEntries)?.selections;
      if (!selections || selections.length === 0) return "";
      return JSON.stringify(selections);
    }

    case "boolean": {
      if (subField === "imagePath") {
        const mostRecent = getMostRecentCollection();
        const booleanEntry = getEntry(mostRecent.booleanEntries);
        if (booleanEntry?.imgCloudPath) {
          const fileName = extractFileNameFromPath(booleanEntry.imgCloudPath, mostRecent, "boolean", sampleKey);
          return getImageCsvPath(fileName);
        }
        return "";
      }
      if (subField === "extractedText") {
        const mostRecent = getMostRecentCollection();
        return sanitizeExtractedTextForCsv(getEntry(mostRecent.booleanEntries)?.extractedImageText);
      }
      // Use most recent value
      const mostRecent = getMostRecentCollection();
      const boolVal = getEntry(mostRecent.booleanEntries)?.val;
      return boolVal != null ? (boolVal ? "Yes" : "No") : "";
    }

    case "percentage": {
      if (subField === "imagePath") {
        const mostRecent = getMostRecentCollection();
        const percentageEntry = getEntry(mostRecent.percentageEntries);
        if (percentageEntry?.imgCloudPath) {
          const fileName = extractFileNameFromPath(percentageEntry.imgCloudPath, mostRecent, "percentage", sampleKey);
          return getImageCsvPath(fileName);
        }
        return "";
      }
      if (subField === "extractedText") {
        const mostRecent = getMostRecentCollection();
        return sanitizeExtractedTextForCsv(getEntry(mostRecent.percentageEntries)?.extractedImageText);
      }
      // Average values from all collections for this sample key
      const values = collections
        .map((collection) => getEntry(collection.percentageEntries)?.val)
        .filter((v) => v != null) as number[];
      const avg = calculateAverage(values);
      return avg != null ? avg : "";
    }

    case "timer": {
      // Timer has no media or extracted text. Emit the final elapsed duration as
      // a number of SECONDS (the column header carries the "(s)" unit label),
      // ranked by entry updatedAt → createdAt → collection createdAt so the
      // per-sample column agrees with the heatmap display path even when a sample
      // is edited/re-stopped in place in an older collection. Idle (elapsedMs
      // absent/0) and running (startedAtEpochMs set) samples have no final value.
      const elapsedMs = sampleKey
        ? getMostRecentStoppedTimerMsForSample(collections, sampleKey)
        : getMostRecentStoppedTimerMs(collections);
      return elapsedMs != null ? timerMsToSeconds(elapsedMs) : "";
    }

    case "date": {
      if (subField === "imagePath") {
        const mostRecent = getMostRecentCollection();
        const dateEntry = getEntry(mostRecent.dateEntries);
        if (dateEntry?.imgCloudPath) {
          const fileName = extractFileNameFromPath(dateEntry.imgCloudPath, mostRecent, "date", sampleKey);
          return getImageCsvPath(fileName);
        }
        return "";
      }
      if (subField === "extractedText") {
        const mostRecent = getMostRecentCollection();
        return sanitizeExtractedTextForCsv(getEntry(mostRecent.dateEntries)?.extractedImageText);
      }
      // Use most recent value
      const mostRecent = getMostRecentCollection();
      const dateEntry = getEntry(mostRecent.dateEntries);
      if (dateEntry?.date) {
        const timestamp = dateEntry.date as Timestamp;
        const date = timestamp.toDate();
        return `${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()}`;
      }
      return "";
    }

    case "photo": {
      if (subField === "imagePath") {
        const mostRecent = getMostRecentCollection();
        const photoEntry = getEntry(mostRecent.photoEntries);
        if (photoEntry?.imgCloudPath) {
          const fileName = extractFileNameFromPath(photoEntry.imgCloudPath, mostRecent, "photo", sampleKey);
          return getImageCsvPath(fileName);
        }
      }
      if (subField === "extractedText") {
        const mostRecent = getMostRecentCollection();
        return sanitizeExtractedTextForCsv(getEntry(mostRecent.photoEntries)?.extractedImageText);
      }
      return "";
    }

    case "transcription": {
      if (!plotId) return "";
      const aggregate = transcriptionAggregates?.get(plotId);
      if (!aggregate) return "";

      if (subField === "combinedText") {
        return aggregate.combinedText;
      } else if (subField === "combinedAudioPath") {
        if (aggregate.combinedAudioFilename) {
          return getAudioCsvPath(aggregate.combinedAudioFilename);
        }
      }
      return "";
    }

    case "leafAreaIndex": {
      if (subField === "extractedText") {
        const mostRecent = getMostRecentCollection();
        return sanitizeExtractedTextForCsv(getEntry(mostRecent.leafAreaIndexEntries)?.extractedImageText);
      }
      if (subField === "value") {
        // Average values from all collections for this sample key
        const values = collections
          .map((collection) => getEntry(collection.leafAreaIndexEntries)?.val)
          .filter((v) => v != null) as number[];
        const avg = calculateAverage(values);
        return avg != null ? avg : "";
      } else if (subField === "originalImage" || subField === "annotatedImage") {
        const mostRecent = getMostRecentCollection();
        const laiEntry = getEntry(mostRecent.leafAreaIndexEntries);
        const path = subField === "originalImage" ? laiEntry?.imgCloudPath : laiEntry?.annotatedImageCloudPath;
        if (path) {
          const fileName = extractFileNameFromPath(
            path,
            mostRecent,
            subField === "originalImage" ? "leafAreaIndex" : "laiAnnotated",
            sampleKey
          );
          return getImageCsvPath(fileName);
        }
      }
      return "";
    }

    case "ruler": {
      if (subField === "extractedText") {
        const mostRecent = getMostRecentCollection();
        return sanitizeExtractedTextForCsv(getEntry(mostRecent.rulerEntries)?.extractedImageText);
      }
      if (subField === "measurement") {
        // Average first measurement from all collections for this sample key
        const measurements = collections
          .map((collection) => {
            const rulerEntry = getEntry(collection.rulerEntries);
            return rulerEntry?.measurementsMeters?.[0];
          })
          .filter((m) => m != null) as number[];
        const avg = calculateAverage(measurements);
        return avg != null ? avg : "";
      } else if (subField === "imagePath") {
        const mostRecent = getMostRecentCollection();
        const rulerEntry = getEntry(mostRecent.rulerEntries);
        if (rulerEntry?.imgCloudPath) {
          const fileName = extractFileNameFromPath(rulerEntry.imgCloudPath, mostRecent, "ruler", sampleKey);
          return getImageCsvPath(fileName);
        }
        return "";
      }
      return "";
    }

    case "imageMeasurement": {
      const imageMeasurementConfig = entryConfigId ? entryConfigsById?.get(entryConfigId) : undefined;

      if (entryConfigId && subField === "plotCount") {
        const value = getImageMeasurementValue(collections, entryConfigId, "count", imageMeasurementConfig);
        return value != null ? value : "";
      }
      if (entryConfigId && subField === "plotLength") {
        return formatFixed(
          getImageMeasurementValueWithStatus(collections, entryConfigId, "length", imageMeasurementConfig).value,
          CENTIMETER_DECIMAL_PLACES
        );
      }
      if (entryConfigId && subField === "plotWidth") {
        return formatFixed(
          getImageMeasurementValueWithStatus(collections, entryConfigId, "width", imageMeasurementConfig).value,
          CENTIMETER_DECIMAL_PLACES
        );
      }
      if (entryConfigId && subField === "plotWeight") {
        const value = getImageMeasurementValue(collections, entryConfigId, "weight", imageMeasurementConfig);
        return value != null ? value : "";
      }
      const plotAdditionalMeasurementKey = parseAdditionalMeasurementSubField(
        subField,
        ADDITIONAL_MEASUREMENT_PLOT_PREFIX
      );
      const plotAdditionalMeasurementDescriptor = findUnboundDescriptor(
        imageMeasurementConfig,
        plotAdditionalMeasurementKey
      );
      if (entryConfigId && plotAdditionalMeasurementDescriptor) {
        const value = getImageMeasurementValueWithStatus(
          collections,
          entryConfigId,
          plotAdditionalMeasurementDescriptor.key,
          imageMeasurementConfig
        ).value;
        return value != null ? formatDescriptorDisplayValueForCsv(value, plotAdditionalMeasurementDescriptor) : "";
      }

      // Grade subFields require the trait's grading config and per-object measurements
      // from each entry's CaptureMetadata.
      if (entryConfigId && subField && subField.startsWith("plotGrade")) {
        const config = entryConfigsById?.get(entryConfigId) as ImageMeasurementConfig | undefined;
        const grading = config?.imageMeasurementConfig?.grading;
        if (!grading) return "";

        // Gather all CaptureMetadata for this plot's image-measurement entries that
        // belong to the trait. Reuse the CSV-wide metadataCache keyed by
        // `${plotId}-${collectionId}-${sampleKey}`.
        const metadataList: CaptureMetadata[] = [];
        if (metadataCache && plotId) {
          for (const collection of collections) {
            if (collection.type !== "imageMeasurement") continue;
            const sampleKeys = collection.imageMeasurementEntries
              ? Object.keys(collection.imageMeasurementEntries)
              : [];
            for (const key of sampleKeys) {
              const metadata = metadataCache.get(`${plotId}-${collection.id}-${key}`);
              if (metadata) metadataList.push(metadata);
            }
          }
        }
        if (metadataList.length === 0) return "";

        const dist = computeGradeDistribution(metadataList, grading);
        if (!dist) return "";

        if (subField === "plotGradeUngraded") return dist.ungraded;
        if (subField === "plotGradeDominant") return dist.dominantLabel ?? "";
        if (subField === "plotGradeDominantPct") {
          if (!dist.dominantLabel) return "";
          return (dist.dominantFraction * 100).toFixed(1);
        }
        if (subField.startsWith("plotGradeCount:")) {
          const bucketId = subField.slice("plotGradeCount:".length);
          const bucket = grading.buckets.find((b) => b.id === bucketId);
          if (!bucket) return "";
          return dist.counts[bucket.label] ?? 0;
        }
        return "";
      }

      // Get entries for this specific sample key from all collections
      const entries = collections
        .map((collection) => getEntry(collection.imageMeasurementEntries))
        .filter((e) => e != null);

      if (entries.length === 0) return "";

      const sampleAdditionalMeasurementKey = parseAdditionalMeasurementSubField(
        subField,
        ADDITIONAL_MEASUREMENT_SAMPLE_PREFIX
      );
      const sampleAdditionalMeasurementDescriptor = findUnboundDescriptor(
        imageMeasurementConfig,
        sampleAdditionalMeasurementKey
      );
      if (sampleAdditionalMeasurementDescriptor) {
        if (sampleAdditionalMeasurementDescriptor.aggregation === "sum") {
          const values = entries
            .map((entry) => entry.additionalMeasurements?.[sampleAdditionalMeasurementDescriptor.key]?.total)
            .filter((value): value is number => value != null);
          if (values.length === 0) return "";
          const total = values.reduce((sum, value) => sum + value, 0);
          return formatDescriptorValueForCsv(total, sampleAdditionalMeasurementDescriptor);
        }

        return calculateWeightedAverageForCsv(
          entries,
          (entry) => {
            const value = entry.additionalMeasurements?.[sampleAdditionalMeasurementDescriptor.key]?.avg;
            return value != null
              ? convertDescriptorValueForDisplay(value, sampleAdditionalMeasurementDescriptor)
              : null;
          },
          (entry) => entry.objectCount,
          (value) => String(formatDescriptorDisplayValueForCsv(value, sampleAdditionalMeasurementDescriptor))
        );
      }

      const objectAdditionalMeasurementKey = parseAdditionalMeasurementSubField(
        subField,
        ADDITIONAL_MEASUREMENT_OBJECT_PREFIX
      );
      const objectAdditionalMeasurementDescriptor = findUnboundDescriptor(
        imageMeasurementConfig,
        objectAdditionalMeasurementKey
      );
      if (objectAdditionalMeasurementDescriptor) {
        if (!metadataCache || !plotId || !sampleKey) return "";

        const allMeasurements: number[] = [];
        for (const collection of collections) {
          const metadata = metadataCache.get(`${plotId}-${collection.id}-${sampleKey}`);
          if (!metadata) continue;
          const measurementsStr = extractAdditionalMeasurementsFromMetadata(
            metadata,
            objectAdditionalMeasurementDescriptor
          );
          if (!measurementsStr) continue;
          try {
            const measurements = JSON.parse(measurementsStr);
            allMeasurements.push(...measurements);
          } catch {
            // Skip invalid JSON
          }
        }

        return allMeasurements.length > 0 ? JSON.stringify(allMeasurements) : "";
      }

      switch (subField) {
        case "extractedText": {
          const mostRecent = getMostRecentCollection();
          return sanitizeExtractedTextForCsv(getEntry(mostRecent.imageMeasurementEntries)?.extractedImageText);
        }
        case "count": {
          // Sum all counts
          const counts = entries.map((e) => e.objectCount).filter((v) => v != null) as number[];
          if (counts.length === 0) return "";
          return counts.reduce((sum, count) => sum + count, 0);
        }
        case "length":
        case "width": {
          // Weighted average by objectCount, converted from meters to cm
          const field = subField === "length" ? "avgLengthMeters" : "avgWidthMeters";
          return calculateWeightedAverageForCsv(
            entries,
            (e) => (e[field] != null ? e[field]! * 100 : null),
            (e) => e.objectCount,
            formatCentimetersForCsv
          );
        }
        case "weight": {
          // Sum all weights
          const weights = entries.map((e) => e.totalWeightGrams).filter((v) => v != null) as number[];
          if (weights.length === 0) return "";
          return weights.reduce((sum, weight) => sum + weight, 0);
        }
        case "objLengths":
        case "objWidths":
        case "objWeights": {
          // Extract individual measurements from metadata - aggregate across all collections
          if (!metadataCache || !plotId || !sampleKey) return "";

          const allMeasurements: number[] = [];

          for (const collection of collections) {
            // Include collectionId in key to match collectMeasurementMetadataPaths
            // This ensures we get each collector's unique metadata instead of duplicating one
            const metadataKey = `${plotId}-${collection.id}-${sampleKey}`;
            const metadata = metadataCache.get(metadataKey);

            if (metadata) {
              const measurementType =
                subField === "objLengths" ? "length" : subField === "objWidths" ? "width" : "mass";
              const measurementsStr = extractIndividualMeasurementsFromMetadata(metadata, measurementType);
              if (measurementsStr) {
                try {
                  const measurements = JSON.parse(measurementsStr);
                  allMeasurements.push(...measurements);
                } catch (e) {
                  // Skip invalid JSON
                }
              }
            }
          }

          return allMeasurements.length > 0 ? JSON.stringify(allMeasurements) : "";
        }
        case "imagePath": {
          // Get image path from most recent collection
          const mostRecent = getMostRecentCollection();
          const imgEntry = getEntry(mostRecent.imageMeasurementEntries);
          if (imgEntry?.imgCloudPath) {
            const fileName = extractFileNameFromPath(imgEntry.imgCloudPath, mostRecent, "imageMeasurement", sampleKey);
            return getImageCsvPath(fileName);
          }
          return "";
        }
        default:
          return "";
      }
    }

    case "standCountMeasurement": {
      if (entryConfigId && subField === "totalRawCount") {
        const allEntries = collections.flatMap((collection) => getAllEntries(collection.standCountMeasurementEntries));
        const totals = calculateStandCountTotalsForCsv(allEntries);
        return totals.rawCount != null ? totals.rawCount : "";
      }
      if (entryConfigId && subField === "totalCountPerM2") {
        const allEntries = collections.flatMap((collection) => getAllEntries(collection.standCountMeasurementEntries));
        return formatFixed(calculateStandCountTotalsForCsv(allEntries).countPerM2, 1);
      }
      if (entryConfigId && subField === "totalPlantsPerAcre") {
        const allEntries = collections.flatMap((collection) => getAllEntries(collection.standCountMeasurementEntries));
        const value = calculateStandCountTotalsForCsv(allEntries).countPerM2;
        return value != null ? Math.round(countPerSquareMeterToPerAcre(value)) : "";
      }
      if (entryConfigId && subField === "plotAvgLength") {
        return formatFixed(
          getStandCountValueWithStatus(collections, entryConfigId, "avgLength").value,
          CENTIMETER_DECIMAL_PLACES
        );
      }
      if (entryConfigId && subField === "plotAvgWidth") {
        return formatFixed(
          getStandCountValueWithStatus(collections, entryConfigId, "avgWidth").value,
          CENTIMETER_DECIMAL_PLACES
        );
      }
      if (entryConfigId && subField === "plotWeight") {
        const value = getStandCountValue(collections, entryConfigId, "totalWeight");
        return value != null ? value : "";
      }

      // Get entries for this specific sample key from all collections
      const entries = collections
        .map((collection) => getEntry(collection.standCountMeasurementEntries))
        .filter((e) => e != null);

      if (entries.length === 0) return "";

      switch (subField) {
        case "extractedText": {
          const mostRecent = getMostRecentCollection();
          return sanitizeExtractedTextForCsv(getEntry(mostRecent.standCountMeasurementEntries)?.extractedImageText);
        }
        case "rawCount": {
          // Sum all raw counts
          const counts = entries.map((e) => e.imageMeasurement?.objectCount).filter((v) => v != null) as number[];
          if (counts.length === 0) return "";
          return counts.reduce((sum, count) => sum + count, 0);
        }
        case "countPerM2": {
          const weighted = computeWeightedCountPerM2(
            entries.map((entry) => ({
              countPerSquareMeter: entry.countPerSquareMeter,
              objectCount: entry.imageMeasurement?.objectCount,
              boxLengthM: entry.standCountBoxLengthMeters,
              boxWidthM: entry.standCountBoxWidthMeters,
            }))
          );
          if (weighted != null) {
            return weighted.toFixed(1);
          }

          // Fallback to simple average for legacy data without raw measurements
          const values = entries.map((e) => e.countPerSquareMeter).filter((v) => v != null) as number[];
          const avg = calculateAverage(values);
          return avg != null ? avg.toFixed(1) : "";
        }
        case "plantsPerAcre": {
          // Mirror the weighted aggregation from countPerM2
          let m2Value = computeWeightedCountPerM2(
            entries.map((entry) => ({
              countPerSquareMeter: entry.countPerSquareMeter,
              objectCount: entry.imageMeasurement?.objectCount,
              boxLengthM: entry.standCountBoxLengthMeters,
              boxWidthM: entry.standCountBoxWidthMeters,
            }))
          );
          if (m2Value == null) {
            const m2Values = entries.map((e) => e.countPerSquareMeter).filter((v) => v != null) as number[];
            m2Value = calculateAverage(m2Values);
          }
          return m2Value != null ? Math.round(countPerSquareMeterToPerAcre(m2Value)) : "";
        }
        case "avgLength":
        case "avgWidth": {
          const field = subField === "avgLength" ? "avgLengthMeters" : "avgWidthMeters";
          return calculateWeightedAverageForCsv(
            entries,
            (e) => (e.imageMeasurement?.[field] != null ? e.imageMeasurement[field]! * 100 : null),
            (e) => e.imageMeasurement?.objectCount,
            formatCentimetersForCsv
          );
        }
        case "weight": {
          const weights = entries
            .map((e) => e.imageMeasurement?.totalWeightGrams)
            .filter((value) => value != null) as number[];
          if (weights.length === 0) return "";
          return weights.reduce((sum, value) => sum + value, 0);
        }
        case "imagePath": {
          const mostRecent = getMostRecentCollection();
          const imgEntry = getEntry(mostRecent.standCountMeasurementEntries);
          if (imgEntry?.imgCloudPath) {
            const fileName = extractFileNameFromPath(
              imgEntry.imgCloudPath,
              mostRecent,
              "standCountMeasurement",
              sampleKey
            );
            return getImageCsvPath(fileName);
          }
          return "";
        }
        default:
          return "";
      }
    }

    case "rowPlantedStandCount": {
      if (subField === "totalPlantCount" || subField === "totalWalkDistance") {
        const allEntries = collections.flatMap((collection) => getAllEntries(collection.rowPlantedStandCountEntries));
        if (allEntries.length === 0) return "";

        if (subField === "totalPlantCount") {
          const totalPlantCount = allEntries
            .map((entry) => entry.plantCount)
            .filter((value) => value != null) as number[];
          if (totalPlantCount.length === 0) return "";
          return totalPlantCount.reduce((sum, value) => sum + value, 0);
        }

        const totalWalkDistance = allEntries
          .map((entry) => entry.walkDistanceMeters)
          .filter((value) => value != null) as number[];
        if (totalWalkDistance.length === 0) return "";
        return totalWalkDistance.reduce((sum, value) => sum + value, 0).toFixed(2);
      }
      if (entryConfigId && subField === "plotPlantsPerMeter") {
        return formatFixed(getRowPlantedStandCountValue(collections, entryConfigId, "plantsPerMeter"), 2);
      }
      if (entryConfigId && subField === "plotAvgSpacing") {
        return formatFixed(
          getRowPlantedStandCountValue(collections, entryConfigId, "averageSpacing"),
          CENTIMETER_DECIMAL_PLACES
        );
      }
      if (entryConfigId && subField === "plotAvgDiameter") {
        return formatFixed(
          getRowPlantedStandCountValue(collections, entryConfigId, "averageDiameter"),
          CENTIMETER_DECIMAL_PLACES
        );
      }
      if (entryConfigId && subField === "plotAvgWidth") {
        return formatFixed(
          getRowPlantedStandCountValue(collections, entryConfigId, "averageWidth"),
          CENTIMETER_DECIMAL_PLACES
        );
      }
      if (entryConfigId && subField === "plotAvgGreenness") {
        return formatFixed(getRowPlantedStandCountValue(collections, entryConfigId, "averageGreenness"), 3);
      }

      // Get entries for this specific sample key from all collections
      const entries = collections
        .map((collection) => getEntry(collection.rowPlantedStandCountEntries))
        .filter((e) => e != null);

      if (entries.length === 0) return "";

      switch (subField) {
        case "plantCount": {
          // Sum all plant counts
          const counts = entries.map((e) => e.plantCount).filter((v) => v != null) as number[];
          if (counts.length === 0) return "";
          return counts.reduce((sum, count) => sum + count, 0);
        }
        case "walkDistance": {
          // Average walk distances
          const values = entries.map((e) => e.walkDistanceMeters).filter((v) => v != null) as number[];
          const avg = calculateAverage(values);
          return avg != null ? avg.toFixed(2) : "";
        }
        case "plantsPerMeter": {
          // Try to recalculate from raw data (sum of plants / sum of distances) for weighted accuracy
          const entriesWithRawData = entries.filter(
            (e) => e.plantCount != null && e.walkDistanceMeters != null && e.walkDistanceMeters > 0
          );

          if (entriesWithRawData.length > 0) {
            const totalPlants = entriesWithRawData.reduce((sum, e) => sum + e.plantCount!, 0);
            const totalDistance = entriesWithRawData.reduce((sum, e) => sum + e.walkDistanceMeters!, 0);
            if (totalDistance > 0) {
              return (totalPlants / totalDistance).toFixed(2);
            }
          }

          // Fallback to simple average for legacy data
          const values = entries.map((e) => e.plantsPerMeter).filter((v) => v != null) as number[];
          const avg = calculateAverage(values);
          return avg != null ? avg.toFixed(2) : "";
        }
        case "avgSpacing":
          // Weighted average spacing by plantCount, converted from meters to cm
          return calculateWeightedAverageForCsv(
            entries,
            (e) => (e.averageSpacingMeters != null ? e.averageSpacingMeters * 100 : null),
            (e) => e.plantCount,
            formatCentimetersForCsv
          );
        case "avgDiameter":
          // Weighted average length by plantCount, converted from meters to cm
          return calculateWeightedAverageForCsv(
            entries,
            (e) => (e.averageDiameterMeters != null ? e.averageDiameterMeters * 100 : null),
            (e) => e.plantCount,
            formatCentimetersForCsv
          );
        case "avgWidth":
          // Weighted average width by plantCount, converted from meters to cm
          return calculateWeightedAverageForCsv(
            entries,
            (e) => (e.averageWidthMeters != null ? e.averageWidthMeters * 100 : null),
            (e) => e.plantCount,
            formatCentimetersForCsv
          );
        case "avgGreenness":
          // Weighted average greenness by plantCount
          return calculateWeightedAverageForCsv(
            entries,
            (e) => e.averageVari,
            (e) => e.plantCount,
            (v) => v.toFixed(3)
          );
        case "imagePaths": {
          if (!plotId || !sampleKey) return "";
          const paths: string[] = [];
          for (const collection of collections) {
            const entry = getEntry(collection.rowPlantedStandCountEntries);
            const images = getRowPlantedImageFilenames(
              entry,
              plotId,
              sampleKey,
              collection.createdAt as Timestamp,
              collection.id || "unknown"
            );
            for (const { filename } of images) {
              paths.push(getImageCsvPath(filename));
            }
          }
          return paths.join("; ");
        }
        case "objLengths":
        case "objWidths": {
          // Extract individual plant measurements from metadata files
          // Row planted stand count has multiple metadata files per sample (one per AR capture)
          if (!metadataCache || !plotId || !sampleKey) return "";

          const allMeasurements: number[] = [];

          // Iterate over collections (not entries) to maintain access to collection.id
          // This ensures we can look up metadata with the collection-specific key
          for (const collection of collections) {
            const entry = getEntry(collection.rowPlantedStandCountEntries);
            if (!entry?.standCountMedia) continue;

            // Iterate through all media entries for this sample
            for (const mediaKey of Object.keys(entry.standCountMedia)) {
              // Include collectionId in key to match collectMeasurementMetadataPaths
              const metadataKey = `${plotId}-${collection.id}-${sampleKey}-rpsc-${mediaKey}`;
              const metadata = metadataCache.get(metadataKey);

              if (metadata) {
                const measurementType = subField === "objLengths" ? "length" : "width";
                const measurementsStr = extractIndividualMeasurementsFromMetadata(metadata, measurementType);
                if (measurementsStr) {
                  try {
                    const measurements = JSON.parse(measurementsStr);
                    allMeasurements.push(...measurements);
                  } catch (e) {
                    // Skip invalid JSON
                  }
                }
              }
            }
          }

          return allMeasurements.length > 0 ? JSON.stringify(allMeasurements) : "";
        }
        default:
          return "";
      }
    }

    default:
      return "";
  }
}

/**
 * Extract a filename from a storage path for use in the CSV
 * Format: {plotId}--{type}--sample{sampleKey}--{timestamp}.{ext}
 */
function extractFileNameFromPath(
  cloudPath: string,
  collection: EntryCollection,
  imageType: string,
  sampleKey?: string
): string {
  // Get plot ID
  const plotRef = collection.plotRef;
  const plotId = typeof plotRef === "string" ? plotRef : plotRef.id;

  // Get file extension from original path
  const extension = getExtensionFromPath(cloudPath);

  // Use shared utility for consistent filename generation
  // Note: sampleKey might be undefined, default to "0" for backwards compatibility
  return generateMediaFilename(
    plotId,
    imageType,
    sampleKey || "0",
    collection.createdAt as Timestamp,
    extension,
    collection.id || "unknown"
  );
}

/**
 * Get the latest update timestamp from a plot's entry collections
 * First checks collection-level updatedAt, then falls back to checking individual entry updatedAt
 */
function getLastUpdatedTimestamp(plotId: string, entryCollections: EntryCollection[]): string {
  const plotCollections = getPlotEntryCollections(plotId, entryCollections);

  if (plotCollections.length === 0) return "";

  let latestTimestamp: Timestamp | null = null;

  // Iterate through all collections to find the most recent updatedAt
  for (const collection of plotCollections) {
    // First, check collection-level updatedAt
    if (collection.updatedAt) {
      const ts = collection.updatedAt as Timestamp;
      if (!latestTimestamp || ts.seconds > latestTimestamp.seconds) {
        latestTimestamp = ts;
      }
    }

    // Fallback: check individual entry updatedAt fields
    const checkEntries = <T extends { updatedAt?: Timestamp }>(entriesMap: Record<string, T> | undefined) => {
      if (!entriesMap) return;
      for (const entry of Object.values(entriesMap)) {
        if (entry.updatedAt) {
          const ts = entry.updatedAt as Timestamp;
          if (!latestTimestamp || ts.seconds > latestTimestamp.seconds) {
            latestTimestamp = ts;
          }
        }
      }
    };

    // Check all entry types
    switch (collection.type) {
      case "text":
        checkEntries(collection.textEntries);
        break;
      case "number":
        checkEntries(collection.numberEntries);
        break;
      case "category":
        checkEntries(collection.categoryEntries);
        break;
      case "date":
        checkEntries(collection.dateEntries);
        break;
      case "boolean":
        checkEntries(collection.booleanEntries);
        break;
      case "percentage":
        checkEntries(collection.percentageEntries);
        break;
      case "photo":
        checkEntries(collection.photoEntries);
        break;
      case "transcription":
        checkEntries(collection.transcriptionEntries);
        break;
      case "leafAreaIndex":
        checkEntries(collection.leafAreaIndexEntries);
        break;
      case "ruler":
        checkEntries(collection.rulerEntries);
        break;
      case "imageMeasurement":
        checkEntries(collection.imageMeasurementEntries);
        break;
      case "standCountMeasurement":
        checkEntries(collection.standCountMeasurementEntries);
        break;
      case "rowPlantedStandCount":
        checkEntries(collection.rowPlantedStandCountEntries);
        break;
      case "timer":
        checkEntries(collection.timerEntries);
        break;
    }
  }

  if (!latestTimestamp) return "";

  const date = latestTimestamp.toDate();
  return `${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

/**
 * Extract data for a single plot row
 */
function extractPlotRowData(
  plot: Plot,
  experiment: Experiment,
  columns: CSVColumn[],
  entryCollections: EntryCollection[],
  entryConfigs: EntryConfig[],
  metadataCache?: Map<string, CaptureMetadata | null>,
  transcriptionAggregates?: Map<string, TranscriptionAggregate>
): Record<string, string | number | boolean> {
  const row: Record<string, string | number | boolean> = {};

  // Get all entry collections for this plot
  const plotId = plot.plotID || "";
  const plotCollections = getPlotEntryCollections(plotId, entryCollections);

  // Build a lookup map once per plot so extractValue can resolve trait configs
  // (e.g. for grading metadata) without re-scanning the array per column.
  const entryConfigsById = new Map<string, EntryConfig>();
  for (const config of entryConfigs) {
    if (config.id) entryConfigsById.set(config.id, config);
  }

  for (const column of columns) {
    const header = column.header;

    // Handle plot metadata columns
    if (!column.entryConfigId) {
      if (header === "Last Updated") {
        row[header] = getLastUpdatedTimestamp(plotId, entryCollections);
      } else {
        const metadata = extractPlotMetadata(plot, experiment);
        row[header] = metadata[header] || "";
      }
    } else {
      // Handle trait columns - get ALL collections for this entry config
      let collections = findEntryCollections(plotCollections, column.entryConfigId);
      if (column.collectorKey) {
        collections = collections.filter((collection) => {
          const creatorPath = getCollectionCreatorPath(collection);
          return (creatorPath || `collection:${collection.id || "unknown"}`) === column.collectorKey;
        });
      }
      row[header] = extractValue(
        collections,
        column.entryConfigId,
        column.subField,
        column.sampleKey,
        metadataCache,
        plotId,
        transcriptionAggregates,
        entryConfigsById
      );
    }
  }

  return row;
}

/**
 * Remove image path columns that are completely empty (no images uploaded for that trait/sample)
 */
function removeEmptyImagePathColumns(
  columns: CSVColumn[],
  rows: Record<string, string | number | boolean>[]
): { columns: CSVColumn[]; rows: Record<string, string | number | boolean>[] } {
  // Identify which image path columns are completely empty
  const columnsToRemove = new Set<string>();

  for (const column of columns) {
    if (column.isImagePath || column.isExtractedText) {
      // Check if all rows have empty values for this column
      const allEmpty = rows.every((row) => {
        const value = row[column.header];
        return value === "" || value == null;
      });

      if (allEmpty) {
        columnsToRemove.add(column.header);
      }
    }
  }

  // Filter out empty image path columns
  const filteredColumns = columns.filter((col) => !columnsToRemove.has(col.header));

  // Remove those column fields from all rows
  const filteredRows = rows.map((row) => {
    const newRow: Record<string, string | number | boolean> = {};
    for (const key in row) {
      if (!columnsToRemove.has(key)) {
        newRow[key] = row[key];
      }
    }
    return newRow;
  });

  return { columns: filteredColumns, rows: filteredRows };
}

/**
 * Main function to generate CSV from task data
 * Now async to support fetching metadata from Firebase Storage
 */
export async function generateTaskCSV(
  data: TaskExportData,
  mediaOptions: MediaDownloadOptions = ALL_MEDIA_OPTIONS,
  transcriptionAggregates?: Map<string, TranscriptionAggregate>,
  metadataByCloudPath?: Map<string, CaptureMetadata | null>
): Promise<string> {
  const { experiment, plots, entryCollections } = data;
  const entryConfigs = addMissingTranscriptionConfigs(data.entryConfigs, entryCollections);
  const aggregates = transcriptionAggregates || buildTranscriptionAggregates(entryCollections);

  // Fetch metadata for imageMeasurement entries (for individual object measurements)
  const metadataPaths = collectMeasurementMetadataPaths(entryCollections);
  const metadataCache = await fetchAllMetadataForCSV(metadataPaths, metadataByCloudPath);
  const userEmailCache = await fetchUserEmailsForCollections(entryCollections);

  // Generate column definitions (with metadata cache for accurate column counts).
  // Media path columns are gated per media type inside generateHeaderColumns.
  let columns = generateHeaderColumns(
    experiment,
    entryConfigs,
    entryCollections,
    mediaOptions,
    metadataCache,
    userEmailCache
  );

  // Generate data rows (yield periodically to keep UI responsive)
  let rows: Record<string, string | number | boolean>[] = [];
  for (let i = 0; i < plots.length; i++) {
    const rowData = extractPlotRowData(
      plots[i],
      experiment,
      columns,
      entryCollections,
      entryConfigs,
      metadataCache,
      aggregates
    );
    rows.push(rowData);

    // Yield every 20 plots to let UI update
    if (i > 0 && i % 20 === 0) {
      await yieldToMain();
    }
  }

  // Remove empty image path columns
  const filtered = removeEmptyImagePathColumns(columns, rows);
  columns = filtered.columns;
  rows = filtered.rows;

  // Generate header row
  const headers = columns.map((col) => col.header);

  // Convert to CSV using Papa Parse
  const csv = unparse({
    fields: headers,
    data: rows,
  });

  return csv;
}

/**
 * Execute async tasks with a concurrency limit
 * Processes tasks in batches to avoid overwhelming the browser/network
 */
async function executeConcurrently<T>(
  tasks: (() => Promise<T>)[],
  concurrencyLimit: number = 5,
  onProgress?: (completed: number, total: number) => void,
  shouldCancel?: () => boolean
): Promise<T[]> {
  const results: T[] = [];
  const executing: Promise<void>[] = [];
  let completed = 0;
  const total = tasks.length;
  let lastYield = 0;

  for (const task of tasks) {
    // Check for cancellation before starting new tasks
    if (shouldCancel?.()) {
      throw new Error("Download cancelled");
    }

    const promise = task().then((result) => {
      results.push(result);
      completed++;
      onProgress?.(completed, total);
      // Remove this promise from executing array when done
      executing.splice(executing.indexOf(promise), 1);
    });

    executing.push(promise);

    // If we've hit the concurrency limit, wait for one to finish
    if (executing.length >= concurrencyLimit) {
      await Promise.race(executing);
      // Yield every few completions to let UI update
      if (completed - lastYield >= 3) {
        await yieldToMain();
        lastYield = completed;
      }
    }
  }

  // Wait for all remaining tasks to complete
  await Promise.all(executing);
  return results;
}

/**
 * Collect metadata paths from imageMeasurement, standCountMeasurement, and
 * rowPlantedStandCount entries.
 * Returns a map of:
 * - "{plotId}-{collectionId}-{sampleKey}" -> captureMetadataCloudPath (for imageMeasurement / standCountMeasurement)
 * - "{plotId}-{collectionId}-{sampleKey}-rpsc-{mediaKey}" -> captureMetadataCloudPath (for rowPlantedStandCount)
 * Used to pre-fetch metadata for extracting individual object measurements
 *
 * NOTE: Collection ID is included in keys to avoid collisions when multiple collectors
 * submit data for the same plot/sample. Without it, later collections would overwrite
 * earlier ones in the map, causing measurements to be silently dropped.
 *
 * standCountMeasurement is collected here so both the long-format per-object
 * stand-count branch and the per-object CSV resolve their `{plotId}-{collectionId}-{sampleKey}`
 * cache lookups. It was historically omitted, which left the long-format stand-count
 * "Object Count" branch permanently dead (cache always missed → Raw Count fallback
 * always fired).
 */
function collectMeasurementMetadataPaths(entryCollections: EntryCollection[]): Map<string, string> {
  const metadataPaths = new Map<string, string>();

  for (const collection of entryCollections) {
    const plotRef = collection.plotRef;
    const plotId = typeof plotRef === "string" ? plotRef : plotRef.id;
    const collectionId = collection.id;

    // Process imageMeasurement entries
    if (collection.type === "imageMeasurement") {
      const sampleKeys = collection.imageMeasurementEntries ? Object.keys(collection.imageMeasurementEntries) : [];

      for (const sampleKey of sampleKeys) {
        const entry = collection.imageMeasurementEntries?.[sampleKey];
        if (entry?.captureMetadataCloudPath) {
          // Include collectionId to avoid key collisions across multiple collectors
          const key = `${plotId}-${collectionId}-${sampleKey}`;
          metadataPaths.set(key, entry.captureMetadataCloudPath);
        }
      }
    }

    // Process standCountMeasurement entries. Use the top-level captureMetadataCloudPath
    // (what the seed and iOS write), NOT the nested entry.imageMeasurement path.
    if (collection.type === "standCountMeasurement") {
      const sampleKeys = collection.standCountMeasurementEntries
        ? Object.keys(collection.standCountMeasurementEntries)
        : [];

      for (const sampleKey of sampleKeys) {
        const entry = collection.standCountMeasurementEntries?.[sampleKey];
        if (entry?.captureMetadataCloudPath) {
          const key = `${plotId}-${collectionId}-${sampleKey}`;
          metadataPaths.set(key, entry.captureMetadataCloudPath);
        }
      }
    }

    // Process rowPlantedStandCount entries
    if (collection.type === "rowPlantedStandCount") {
      const sampleKeys = collection.rowPlantedStandCountEntries
        ? Object.keys(collection.rowPlantedStandCountEntries)
        : [];

      for (const sampleKey of sampleKeys) {
        const entry = collection.rowPlantedStandCountEntries?.[sampleKey];
        // Row planted stand count has multiple media entries in standCountMedia
        if (entry?.standCountMedia) {
          for (const [mediaKey, mediaEntry] of Object.entries(entry.standCountMedia)) {
            if (mediaEntry.dataCollectionOnly !== true && mediaEntry.captureMetadataCloudPath) {
              // Include collectionId to avoid key collisions across multiple collectors
              const key = `${plotId}-${collectionId}-${sampleKey}-rpsc-${mediaKey}`;
              metadataPaths.set(key, mediaEntry.captureMetadataCloudPath);
            }
          }
        }
      }
    }
  }

  return metadataPaths;
}

/**
 * Fetch and parse all metadata JSON files concurrently
 * Returns a map of "{plotId}-{sampleKey}" -> parsed CaptureMetadata (or null if fetch fails)
 */
async function fetchAllMetadataForCSV(
  metadataPaths: Map<string, string>,
  metadataByCloudPath?: Map<string, CaptureMetadata | null>
): Promise<Map<string, CaptureMetadata | null>> {
  const metadataCache = new Map<string, CaptureMetadata | null>();

  if (metadataPaths.size === 0) {
    return metadataCache;
  }

  // If pre-fetched cache is available, look up each cloud path from it
  if (metadataByCloudPath) {
    for (const [key, cloudPath] of metadataPaths) {
      metadataCache.set(key, metadataByCloudPath.get(cloudPath) ?? null);
    }
    return metadataCache;
  }

  // Otherwise fetch individually
  const fetchTasks = Array.from(metadataPaths.entries()).map(([key, cloudPath]) => {
    return async () => {
      try {
        const metadata = await getAnnotationMetadata(cloudPath);
        metadataCache.set(key, metadata);
      } catch (err) {
        console.error(`Error fetching metadata for key ${key}:`, err);
        metadataCache.set(key, null);
      }
    };
  });

  await executeConcurrently(fetchTasks, 10);

  return metadataCache;
}

/**
 * Fetch user emails for all unique createdBy references in entry collections
 * Returns a map of user document path -> email
 *
 * Uses userPermissions subcollection instead of users collection because
 * security rules allow editors to read userPermissions but not other users' profiles.
 */
async function fetchUserEmailsForCollections(entryCollections: EntryCollection[]): Promise<Map<string, string>> {
  const userEmails = new Map<string, string>();

  if (entryCollections.length === 0) return userEmails;

  // Extract org ID from first entry collection's plotRef
  // plotRef path format: organizations/{orgId}/trials/{trialId}/experiments/{expId}/plots/{plotId}
  const firstPlotRef = entryCollections[0].plotRef;
  const plotPath = typeof firstPlotRef === "string" ? firstPlotRef : firstPlotRef.path;
  const pathParts = plotPath.split("/");
  const orgId = pathParts[1]; // organizations/{orgId}/...

  if (!orgId) return userEmails;

  // Collect unique user IDs (extract from user path: users/{uid})
  const userIds = new Set<string>();
  for (const collection of entryCollections) {
    if (collection.createdBy) {
      const userPath = typeof collection.createdBy === "string" ? collection.createdBy : collection.createdBy.path;
      const uid = userPath.split("/")[1];
      if (uid) userIds.add(uid);
    }
  }

  // Fetch emails from userPermissions subcollection (accessible by editors)
  const fetchTasks = Array.from(userIds).map(async (uid) => {
    const userPath = `users/${uid}`;
    try {
      const permissionDoc = await getDoc(doc(db, `organizations/${orgId}/userPermissions/${uid}`));
      if (permissionDoc.exists()) {
        const permData = permissionDoc.data();
        userEmails.set(userPath, permData.email || "Unknown");
      } else {
        userEmails.set(userPath, "Unknown");
      }
    } catch (err) {
      console.error(`Error fetching email for uid ${uid}:`, err);
      userEmails.set(userPath, "Unknown");
    }
  });

  await Promise.all(fetchTasks);
  return userEmails;
}

/**
 * Get user email from collection's createdBy reference
 */
function getCollectorEmail(collection: EntryCollection, userEmailCache: Map<string, string>): string {
  if (!collection.createdBy) return "";
  const userPath = typeof collection.createdBy === "string" ? collection.createdBy : collection.createdBy.path;
  return userEmailCache.get(userPath) || "";
}

/**
 * Filter annotations to only visible ones
 * Returns array of visible annotations based on userVisibility.isVisible flag
 */
function extractVisibleAnnotations(metadata: CaptureMetadata | null): Annotation[] {
  if (!metadata?.annotations?.annotations) {
    return [];
  }

  return metadata.annotations.annotations.filter((annotation) => annotation.userVisibility.isVisible);
}

/**
 * Standard media entry interface for entries with image, depth, and metadata paths
 */
interface StandardMediaEntry {
  imgCloudPath?: string;
  depthCloudPath?: string;
  captureMetadataCloudPath?: string;
}

/**
 * Add standard media paths (image, depth, metadata) to the media paths map
 */
function addStandardMediaPaths(
  mediaPaths: Map<string, MediaFileInfo>,
  entry: StandardMediaEntry | undefined,
  plotId: string,
  entryType: string,
  sampleKey: string,
  createdAt: Timestamp,
  collectionId: string
): void {
  if (!entry) return;

  if (entry.imgCloudPath) {
    const ext = getExtensionFromPath(entry.imgCloudPath);
    const filename = generateMediaFilename(plotId, entryType, sampleKey, createdAt, ext, collectionId);
    mediaPaths.set(getMediaZipPath("image", filename), { cloudPath: entry.imgCloudPath, type: "image" });
  }
  if (entry.depthCloudPath) {
    const ext = getExtensionFromPath(entry.depthCloudPath, "png");
    const filename = generateMediaFilename(plotId, entryType, sampleKey, createdAt, ext, collectionId);
    mediaPaths.set(getMediaZipPath("depth", filename), { cloudPath: entry.depthCloudPath, type: "depth" });
  }
  if (entry.captureMetadataCloudPath) {
    const filename = generateMediaFilename(plotId, entryType, sampleKey, createdAt, "json", collectionId);
    mediaPaths.set(getMediaZipPath("metadata", filename), { cloudPath: entry.captureMetadataCloudPath, type: "metadata" });
  }
}

/**
 * Compute ZIP image filenames for a rowPlantedStandCount sample.
 * Sorted by mediaKey so CSV "Image Paths" columns and ZIP entries agree byte-for-byte.
 * Callers: wide-format CSV extractor and per-object CSV (both wrap with getImageCsvPath)
 * and the ZIP bundler. `mediaKey` is returned so the per-object CSV can align each row's
 * image path to the same capture it reads metadata for.
 */
function getRowPlantedImageFilenames(
  entry: { standCountMedia?: Record<string, { imgCloudPath?: string }> } | undefined,
  plotId: string,
  sampleKey: string,
  createdAt: Timestamp,
  collectionId: string
): Array<{ mediaKey: string; filename: string; cloudPath: string }> {
  if (!entry?.standCountMedia) return [];
  return Object.keys(entry.standCountMedia)
    .sort()
    .flatMap((mediaKey) => {
      const cloudPath = entry.standCountMedia![mediaKey].imgCloudPath;
      if (!cloudPath) return [];
      const ext = getExtensionFromPath(cloudPath);
      const mediaIndex = `${sampleKey}-${mediaKey}`;
      const filename = generateMediaFilename(plotId, "rowPlantedStandCount", mediaIndex, createdAt, ext, collectionId);
      return [{ mediaKey, filename, cloudPath }];
    });
}

/**
 * Collect all media file paths from entry collections
 * Returns a map of ZIP path -> MediaFileInfo (cloud path + file type).
 * Including the folder in the key keeps same-basename PNG images and depth maps distinct.
 *
 * Exported for unit tests that verify the unified naming contract (image, depth,
 * and capture-metadata share the same base name; see export-filename-pairing.test.ts).
 */
export function collectMediaPaths(entryCollections: EntryCollection[]): Map<string, MediaFileInfo> {
  const mediaPaths = new Map<string, MediaFileInfo>();

  for (const collection of entryCollections) {
    const plotRef = collection.plotRef;
    const plotId = typeof plotRef === "string" ? plotRef : plotRef.id;
    const createdAt = collection.createdAt as Timestamp;
    const collectionId = collection.id || "unknown";

    // Transcription audio is handled via combined audio generation; skip individual files
    if (collection.type === "transcription") continue;

    const sampleKeys = getCollectionSampleKeys(collection);

    for (const sampleKey of sampleKeys) {
      switch (collection.type) {
        case "text":
          addStandardMediaPaths(
            mediaPaths,
            collection.textEntries?.[sampleKey],
            plotId,
            "text",
            sampleKey,
            createdAt,
            collectionId
          );
          break;
        case "number":
          addStandardMediaPaths(
            mediaPaths,
            collection.numberEntries?.[sampleKey],
            plotId,
            "number",
            sampleKey,
            createdAt,
            collectionId
          );
          break;
        case "category":
          addStandardMediaPaths(
            mediaPaths,
            collection.categoryEntries?.[sampleKey],
            plotId,
            "category",
            sampleKey,
            createdAt,
            collectionId
          );
          break;
        case "date":
          addStandardMediaPaths(
            mediaPaths,
            collection.dateEntries?.[sampleKey],
            plotId,
            "date",
            sampleKey,
            createdAt,
            collectionId
          );
          break;
        case "boolean":
          addStandardMediaPaths(
            mediaPaths,
            collection.booleanEntries?.[sampleKey],
            plotId,
            "boolean",
            sampleKey,
            createdAt,
            collectionId
          );
          break;
        case "percentage":
          addStandardMediaPaths(
            mediaPaths,
            collection.percentageEntries?.[sampleKey],
            plotId,
            "percentage",
            sampleKey,
            createdAt,
            collectionId
          );
          break;
        case "photo":
          addStandardMediaPaths(
            mediaPaths,
            collection.photoEntries?.[sampleKey],
            plotId,
            "photo",
            sampleKey,
            createdAt,
            collectionId
          );
          break;
        case "ruler":
          addStandardMediaPaths(
            mediaPaths,
            collection.rulerEntries?.[sampleKey],
            plotId,
            "ruler",
            sampleKey,
            createdAt,
            collectionId
          );
          break;
        case "imageMeasurement":
          addStandardMediaPaths(
            mediaPaths,
            collection.imageMeasurementEntries?.[sampleKey],
            plotId,
            "imageMeasurement",
            sampleKey,
            createdAt,
            collectionId
          );
          break;
        case "standCountMeasurement":
          addStandardMediaPaths(
            mediaPaths,
            collection.standCountMeasurementEntries?.[sampleKey],
            plotId,
            "standCountMeasurement",
            sampleKey,
            createdAt,
            collectionId
          );
          break;
        case "leafAreaIndex": {
          const entry = collection.leafAreaIndexEntries?.[sampleKey];
          if (entry?.imgCloudPath) {
            const ext = getExtensionFromPath(entry.imgCloudPath);
            const filename = generateMediaFilename(plotId, "leafAreaIndex", sampleKey, createdAt, ext, collectionId);
            mediaPaths.set(getMediaZipPath("image", filename), { cloudPath: entry.imgCloudPath, type: "image" });
          }
          if (entry?.annotatedImageCloudPath) {
            // Pre-existing annotated artifact uploaded by the client. Keep the
            // distinct `laiAnnotated` middle segment so it doesn't collide with
            // the original image (both land in media/images/).
            const ext = getExtensionFromPath(entry.annotatedImageCloudPath);
            const filename = generateMediaFilename(plotId, "laiAnnotated", sampleKey, createdAt, ext, collectionId);
            mediaPaths.set(getMediaZipPath("image", filename), { cloudPath: entry.annotatedImageCloudPath, type: "image" });
          }
          if (entry?.depthCloudPath) {
            const ext = getExtensionFromPath(entry.depthCloudPath, "png");
            const filename = generateMediaFilename(plotId, "leafAreaIndex", sampleKey, createdAt, ext, collectionId);
            mediaPaths.set(getMediaZipPath("depth", filename), { cloudPath: entry.depthCloudPath, type: "depth" });
          }
          if (entry?.captureMetadataCloudPath) {
            const filename = generateMediaFilename(plotId, "leafAreaIndex", sampleKey, createdAt, "json", collectionId);
            mediaPaths.set(getMediaZipPath("metadata", filename), { cloudPath: entry.captureMetadataCloudPath, type: "metadata" });
          }
          break;
        }
        case "rowPlantedStandCount": {
          const entry = collection.rowPlantedStandCountEntries?.[sampleKey];
          if (!entry?.standCountMedia) break;
          // Images go through the shared helper so CSV "Image Paths" filenames match exactly.
          for (const { filename, cloudPath } of getRowPlantedImageFilenames(
            entry,
            plotId,
            sampleKey,
            createdAt,
            collectionId
          )) {
            mediaPaths.set(getMediaZipPath("image", filename), { cloudPath, type: "image" });
          }
          for (const [mediaKey, mediaEntry] of Object.entries(entry.standCountMedia)) {
            const mediaIndex = `${sampleKey}-${mediaKey}`;
            if (mediaEntry.depthCloudPath) {
              const ext = getExtensionFromPath(mediaEntry.depthCloudPath, "png");
              const filename = generateMediaFilename(
                plotId,
                "rowPlantedStandCount",
                mediaIndex,
                createdAt,
                ext,
                collectionId
              );
              mediaPaths.set(getMediaZipPath("depth", filename), { cloudPath: mediaEntry.depthCloudPath, type: "depth" });
            }
            if (mediaEntry.captureMetadataCloudPath) {
              const filename = generateMediaFilename(
                plotId,
                "rowPlantedStandCount",
                mediaIndex,
                createdAt,
                "json",
                collectionId
              );
              mediaPaths.set(getMediaZipPath("metadata", filename), { cloudPath: mediaEntry.captureMetadataCloudPath, type: "metadata" });
            }
          }
          break;
        }
      }
    }
  }

  return mediaPaths;
}

/**
 * Collect entries that have annotation metadata (for rendering annotated images)
 */
function collectAnnotatedEntries(entryCollections: EntryCollection[]): AnnotatedEntryInfo[] {
  const entries: AnnotatedEntryInfo[] = [];

  for (const collection of entryCollections) {
    const plotRef = collection.plotRef;
    const plotId = typeof plotRef === "string" ? plotRef : plotRef.id;
    const createdAt = collection.createdAt as Timestamp;
    const collectionId = collection.id || "unknown";
    const entryConfigId = collection.entryConfigRef?.id;

    // Helper to check entries and add if they have annotation metadata
    const checkEntries = (
      entriesMap: Record<string, { imgCloudPath?: string; captureMetadataCloudPath?: string }> | undefined,
      entryType: string
    ) => {
      if (!entriesMap) return;
      for (const [sampleKey, entry] of Object.entries(entriesMap)) {
        if (entry.imgCloudPath && entry.captureMetadataCloudPath) {
          const filename = generateMediaFilename(plotId, entryType, sampleKey, createdAt, "jpg", collectionId);
          entries.push({
            plotId,
            sampleKey,
            imgCloudPath: entry.imgCloudPath,
            captureMetadataCloudPath: entry.captureMetadataCloudPath,
            filename: filename.replace(/\.[^.]+$/, "--annotated.jpg"), // Replace extension with --annotated.jpg
            entryType,
            entryConfigId,
          });
        }
      }
    };

    // Check all entry types that can have images with annotations
    checkEntries(collection.textEntries, "text");
    checkEntries(collection.numberEntries, "number");
    checkEntries(collection.categoryEntries, "category");
    checkEntries(collection.dateEntries, "date");
    checkEntries(collection.booleanEntries, "boolean");
    checkEntries(collection.percentageEntries, "percentage");
    checkEntries(collection.photoEntries, "photo");
    checkEntries(collection.leafAreaIndexEntries, "leafAreaIndex");
    checkEntries(collection.rulerEntries, "ruler");
    checkEntries(collection.imageMeasurementEntries, "imageMeasurement");
    checkEntries(collection.standCountMeasurementEntries, "standCountMeasurement");

    // Handle rowPlantedStandCount with nested standCountMedia
    if (collection.rowPlantedStandCountEntries) {
      for (const [sampleKey, entry] of Object.entries(collection.rowPlantedStandCountEntries)) {
        if (entry.standCountMedia) {
          for (const [mediaKey, mediaEntry] of Object.entries(entry.standCountMedia)) {
            if (mediaEntry.dataCollectionOnly === true) continue;
            if (mediaEntry.imgCloudPath && mediaEntry.captureMetadataCloudPath) {
              const mediaIndex = `${sampleKey}-${mediaKey}`;
              const filename = generateMediaFilename(
                plotId,
                "rowPlantedStandCount",
                mediaIndex,
                createdAt,
                "jpg",
                collectionId
              );
              entries.push({
                plotId,
                sampleKey: mediaIndex,
                imgCloudPath: mediaEntry.imgCloudPath,
                captureMetadataCloudPath: mediaEntry.captureMetadataCloudPath,
                filename: filename.replace(/\.[^.]+$/, "--annotated.jpg"),
                entryType: "rowPlantedStandCount",
                entryConfigId,
              });
            }
          }
        }
      }
    }
  }

  return entries;
}

/**
 * Generate README content explaining the download folder structure and files
 */
function generateReadmeContent(taskName: string, mediaOptions: MediaDownloadOptions, withinBudget: boolean): string {
  const includeAnyMedia = hasAnyMediaOption(mediaOptions);
  const lines: string[] = [
    "README - GleanAg Data Export",
    "============================",
    "",
    "This ZIP file contains data exported from GleanAg.",
    "",
    "FOLDER STRUCTURE",
    "----------------",
    "Files sit at the top of the ZIP. Extracting it creates a folder named after the ZIP.",
    "",
    `${taskName}--{timestamp}.zip`,
    `├── one_row_per_plot--${taskName}--{timestamp}.csv           <- One row per plot (wide format)`,
    `├── one_row_per_observation--${taskName}--{timestamp}.csv    <- One row per observation (long format)`,
    `├── one_row_per_object--${taskName}--{timestamp}.csv         <- One row per detected object`,
    "├── export_info.json                                         <- Which GleanAg task this export came from",
    `${includeAnyMedia ? "├" : "└"}── README.txt                                               <- This file`,
  ];

  if (includeAnyMedia) {
    const mediaFolders: string[] = [];
    mediaFolders.push("└── media/                                                  <- Media files");
    if (mediaOptions.includeOriginalImages) {
      mediaFolders.push(
        "    ├── images/                                             <- Original captured images",
        "    │   └── annotations_coco.json                           <- COCO format annotations (if present)"
      );
    }
    if (mediaOptions.includeAnnotatedImages) {
      mediaFolders.push(
        "    ├── annotated_images/                                   <- Images with measurement overlays"
      );
    }
    if (mediaOptions.includeDepthAndMetadata) {
      mediaFolders.push(
        "    ├── depth_and_metadata/                                 <- Depth maps and capture metadata"
      );
    }
    if (mediaOptions.includeAudio) {
      mediaFolders.push("    └── audio/                                              <- Audio recordings");
    }
    lines.push(...mediaFolders);
  }

  lines.push(
    "",
    "UNANNOTATED COLLECTION CAPTURES",
    "-------------------------------",
    "When present, collection_captures.json lists every marked walking capture by stable captureId,",
    "collectionId, plotId and sampleKey, with image/metadata/depth storage and ZIP paths.",
    "Purpose is unannotated: no model was run and these captures are not COCO negatives.",
    "Status is complete, pending, failed or missing-files; null paths are missing associations.",
    "Complete means uploaded associations exist; selected required files must also download successfully.",
    "Each file has an included flag stating whether it is bundled in this ZIP; CSV-only exports keep it false.",
    "CSV-only exports can inventory pending/failed captures. Media exports stop on incomplete selected captures.",
    "RGB-only captures have depthAvailable=false and no depth file. Media options still control included files.",
    "",
    "IDENTIFYING THIS EXPORT",
    "-----------------------",
    "export_info.json records where this export came from:",
    "  - firebaseProjectId  Source database (glean-ag = production, gleanag-staging = staging,",
    "                       demo-test = local emulators)",
    "  - organization, trial, experiment, task  Firestore IDs and full names",
    "  - taskPath           organizations/{orgId}/trials/{trialId}/experiments/{experimentId}/tasks/{taskId}",
    "                       (the same value as info.gleanag_task_path in annotations_coco.json)",
    "  - appUrl             Link to the task in the GleanAg web app",
    "  - fileNames          The task name as used in file names, whether it was shortened, and any",
    "                       plot IDs written differently in media file names (see below)",
    "  - pathCheck          Length of the longest path in this ZIP against the limit below",
    "  - mediaOptions       Which media types were included",
    "Scripts that combine several exports should match them to tasks by firebaseProjectId + taskPath,",
    "not by file name: task names are not unique, and long ones are shortened in file names.",
    "Values that could not be confirmed when the export was made are null.",
    "",
    "FILE NAME LENGTH AND CHARACTERS",
    "-------------------------------",
    "Windows cannot open paths longer than 259 characters, counting the folder the ZIP is saved or",
    `extracted to. GleanAg keeps every "{ZIP name}/{path inside the ZIP}" within ${EXPORT_PATH_BUDGET} characters,`,
    "shortening the task name in file names when needed (export_info.json has the full name).",
    'Characters Windows does not allow in file names (< > : " \\ | ? * and control characters), and %,',
    'are percent-encoded in the plot ID part of media file names, e.g. plot "Salinas: 1-1" becomes',
    '"Salinas%3A 1-1". Decode with urllib.parse.unquote (Python) or decodeURIComponent (JavaScript).',
    "The CSV plot ID columns always show the real plot ID.",
    ...(withinBudget
      ? []
      : [
          "",
          "LONG FILE PATHS",
          "---------------",
          `Some paths in this export are longer than ${EXPORT_PATH_BUDGET} characters because of long plot or entry IDs.`,
          'On Windows, use "Extract all" to a short folder such as C:\\GleanAg rather than opening files',
          "from inside the ZIP, or use 7-Zip.",
        ]),
    "",
    "FILE NAMING CONVENTION",
    "----------------------",
    "Media files follow this pattern:",
    "  {plotId}--{entryType}--sample{N}--{timestamp}--{collectionId}.{ext}",
    "",
    "Example: plot_1_2--imageMeasurement--sample1--20240315-143022--abc123def456.jpg",
    "",
    "Components:",
    "  - plotId       Plot identifier (Firestore doc ID; characters Windows forbids are percent-encoded)",
    "  - entryType    The trait/measurement type (photo, imageMeasurement,",
    "                 leafAreaIndex, rowPlantedStandCount, etc.). For row-planted",
    "                 stand count this segment looks like sample{N}-media-{K}.",
    "  - sampleN      Sample slot within the plot (sample1, sample2, ...)",
    "  - timestamp    Local capture time, formatted YYYYMMDD-HHMMSS",
    "  - collectionId Firestore EntryCollection ID (disambiguates submissions",
    "                 from multiple collectors within the same second)",
    "",
    "PAIRING IMAGE + DEPTH + METADATA",
    "--------------------------------",
    "For entry types that capture depth and camera metadata (photo,",
    "imageMeasurement, leafAreaIndex, rowPlantedStandCount, ruler,",
    "standCountMeasurement, plus the text/number/category/date/boolean/percentage",
    "entries when they include an attached photo), all three files for one",
    "capture share the SAME base name and differ only by folder + extension:",
    "",
    "  media/images/{base}.jpg                <- Original image",
    "  media/depth_and_metadata/{base}.png    <- Depth map",
    "  media/depth_and_metadata/{base}.json   <- Capture metadata",
    "",
    "where {base} = {plotId}--{entryType}--sample{N}--{timestamp}--{collectionId}.",
    "",
    "Quick check on the command line:",
    "  ls media/images media/depth_and_metadata | grep abc123def456",
    "(returns the matching .jpg / .png / .json triplet)",
    "",
    "Python — pair every photo with its depth + metadata:",
    "  from pathlib import Path",
    "  for img in Path('media/images').glob('*.jpg'):",
    "      base = img.stem  # e.g. 'plot_1_2--imageMeasurement--sample1--20240315-143022--abc123'",
    "      depth = Path('media/depth_and_metadata') / f'{base}.png'",
    "      meta  = Path('media/depth_and_metadata') / f'{base}.json'",
    "      if depth.exists() and meta.exists():",
    "          ...  # process the triplet",
    "",
    "Notes:",
    "  - leafAreaIndex entries that include a pre-existing annotated image",
    "    upload it as {plotId}--laiAnnotated--sample{N}--{timestamp}--{collectionId}.jpg",
    "    in media/images/ alongside the original. The original / depth / metadata",
    "    triplet still pairs by the rule above.",
    "  - Annotated images rendered at export time live in media/annotated_images/",
    "    with a trailing --annotated.jpg suffix on the same base.",
    "",
    "CSV FILES",
    "---------",
    "",
    "This export includes three CSV files with the same data in different formats:",
    "",
    "1. ONE ROW PER PLOT (one_row_per_plot--{taskName}--{timestamp}.csv)",
    "   - One row per plot",
    "   - Columns for plot identification (plotID, row, column, custom fields)",
    "   - Trait measurements spread across columns (one column per trait/sample slot)",
    "   - For multi-collector traits: separate sample columns for each collector, labeled with the collector email",
    "   - For image, stand count, and row planted measurements: includes total summary columns across all samples",
    "   - For image and row planted measurements with individual objects: measurements stored as JSON arrays",
    "   - Media file paths (relative paths matching the ZIP folder structure)",
    "   - Best for: spreadsheet viewing, quick data overview",
    "",
    "2. ONE ROW PER OBSERVATION (one_row_per_observation--{taskName}--{timestamp}.csv)",
    "   - One row per observation by a single collector (not per plot)",
    "   - Columns:",
    "     - Plot identification (same as wide format)",
    "     - Trait Name: name of the trait/measurement",
    "     - Entry Type: type of measurement (number, text, imageMeasurement, etc.)",
    "     - Sample Number: which sample (1, 2, 3, etc.)",
    "     - Observation Number: for image measurements, each detected object gets its own row",
    "     - Value columns appropriate for each entry type",
    "     - Image Path and Last Updated",
    "   - For image measurements: each metric of each detected object is its own row,",
    '     with the metric appended to the Trait Name (e.g. " -- Length (cm)")',
    "   - Extracted Text: OCR text extracted from captured images (if available)",
    "   - Empty columns are automatically removed",
    "   - Best for: statistical analysis, R/Python data processing, machine learning",
    "",
    "3. ONE ROW PER OBJECT (one_row_per_object--{taskName}--{timestamp}.csv)",
    "   - One row per detected object (image, stand count, and row-planted",
    "     measurements), one row per ruler reading, and one row per sample for",
    "     simple traits (number, text, category, date, boolean, percentage, timer,",
    "     leaf area index)",
    "   - Object #: numbers objects within a sample (continuous across the multiple",
    "     captures of a row-planted sample; Image Path identifies the capture)",
    "   - Value: the reading for simple single-value traits; blank for detected-object",
    "     and ruler rows, whose data is in the metric columns (count objects via the",
    "     Object # column or a row count). The column is omitted entirely when no",
    "     simple trait populates it",
    "   - Length (cm), Width (cm), Weight (g), Grade, and Maturity are separate",
    "     columns, filled per object when available (Weight only for traits with",
    "     weight measurement enabled; Grade only for image measurements with grading",
    "     configured; Maturity from the model's per-object class label when present)",
    "   - Samples with no per-object metadata are skipped (their aggregates remain in",
    "     the other two CSVs); photo and transcription entries are not included",
    "   - Empty columns are automatically removed",
    "   - Best for: per-object size/grade distributions and pivot tables",
    "",
    "IMAGE MEASUREMENT GRADING",
    "-------------------------",
    "When an image measurement trait has grading configured, detected objects are",
    "bucketed by the trait's selected measurement dimension.",
    "",
    "Wide-format CSV columns:",
    "  - {Trait Name} Grade {Bucket Label} Count: object count in each grade bucket",
    "  - {Trait Name} Grade Ungraded Count: objects missing the graded dimension or",
    "    not matching any configured bucket",
    "  - {Trait Name} Grade Dominant: bucket with the highest object count",
    "  - {Trait Name} Grade Dominant %: dominant bucket share of visible objects",
    "    (empty when there is no dominant bucket)",
    "",
    "Long-format CSV rows:",
    "  - {Trait Name} -- Grade: per-object grade label, or Ungraded",
    "  - {Trait Name} -- Grade {Bucket Label} Count: aggregate count per bucket",
    "  - {Trait Name} -- Grade Ungraded Count: aggregate ungraded object count",
    "",
    "Per-object CSV column:",
    "  - Grade: per-object grade label, or Ungraded",
    "",
    "TIMER (STOPWATCH) TRAIT",
    "-----------------------",
    "Timer traits record an elapsed stopwatch duration. Both CSV files export the",
    "final duration as a number of SECONDS (elapsed milliseconds / 1000), with the",
    "unit appended to the column so the number is never ambiguous:",
    "  - One row per plot:        the trait column header ends with (s),",
    '                             e.g. "Field Time Sample 1 (s)"',
    "  - One row per observation: the Trait Name value ends with (s),",
    '                             e.g. "Field Time (s)"',
    "Example: a stopwatch reading of 03:05.34 (mm:ss.cc) exports as 185.34.",
    "Only stopped timers have a value; idle and still-running samples are left blank."
  );

  if (mediaOptions.includeOriginalImages) {
    lines.push(
      "",
      "COCO ANNOTATIONS (annotations_coco.json)",
      "----------------------------------------",
      "COCO (Common Objects in Context) is a standard format for computer vision",
      "datasets. This file can be used for training machine learning models.",
      "Its images array includes processed frames with zero visible annotations so",
      "intentional negative examples are preserved. Pending deferred-analysis frames",
      "are omitted until processing writes an annotation summary. When task context is available,",
      "info.gleanag_task_path identifies the exact GleanAg task that produced the export.",
      "For curated or relabel tasks, each images[].gleanag_review records the entry identity, media paths, priority,",
      "review status, reviewer, and exact review/update timestamps from the exported snapshot.",
      "Processed does not mean reviewed. Unknown or unreviewed images are not training ground truth.",
      "The Planner training stager matches a fresh --include-review-progress inventory and keeps only explicit reviews.",
      "Reviewed images also require matching annotationMetadataSha256 hashes from the export and current Storage metadata.",
      "",
      "For details on the COCO format, see: https://cocodataset.org/#format-data"
    );
  }
  if (mediaOptions.includeDepthAndMetadata) {
    lines.push(
      "",
      "DEPTH AND METADATA FILES",
      "------------------------",
      "Found in media/depth_and_metadata/. Pair to the corresponding image in",
      "media/images/ by the shared base filename (see PAIRING IMAGE + DEPTH +",
      "METADATA above).",
      "",
      "Depth map (.png):",
      "  - 16-bit single-channel PNG produced from the iOS LiDAR/depth sensor.",
      "  - Resolution and alignment-to-image are reported per file in the",
      "    metadata JSON 'depth' block (see schema below).",
      "  - To convert raw pixel values to meters, read 'depth.scale',",
      "    'depth.units', and 'depth.invalidValue' from the metadata JSON.",
      "    A pixel value equal to invalidValue means depth was not estimated.",
      "  - Whether a given depth map is registered to the image's pixel grid is",
      "    given by 'depth.alignedToImage' (boolean).",
      "",
      "Metadata JSON (.json) — top-level fields:",
      "  - createdAt           ISO-8601 timestamp of capture",
      "  - device              { model, osVersion, cameraType }",
      "  - image               { file, width, height, colorSpace, orientation }",
      "  - depth               { file, width, height, units, invalidValue, scale,",
      "                          alignedToImage } (omitted if no depth map)",
      "  - intrinsics          { fx, fy, cx, cy, referenceWidth, referenceHeight }",
      "                        Camera intrinsics; rescale to actual image size",
      "                        using referenceWidth/Height if needed.",
      "  - distortion          { center, lookupTable, inverseLookupTable,",
      "                          referenceWidth, referenceHeight }",
      "  - extras              { cameraPitchAngleDegrees, distanceToCenterMeters }",
      "  - annotations         { schemaVersion, measureBoundaryPixels,",
      "                          boundaryAreaSquareMeters, annotations[], summary }",
      "                        See COCO ANNOTATIONS section for the export-side",
      "                        flat representation of annotations[].",
      "",
      "Python — load image, depth (in meters), and metadata for one capture:",
      "  import json, numpy as np",
      "  from pathlib import Path",
      "  from PIL import Image",
      "  base = 'plot_1_2--imageMeasurement--sample1--20240315-143022--abc123'",
      "  img   = Image.open(Path('media/images') / f'{base}.jpg')",
      "  meta  = json.loads((Path('media/depth_and_metadata') / f'{base}.json').read_text())",
      "  depth_raw = np.array(Image.open(Path('media/depth_and_metadata') / f'{base}.png'))",
      "  scale, invalid = meta['depth']['scale'], meta['depth']['invalidValue']",
      "  depth_m = np.where(depth_raw == invalid, np.nan, depth_raw * scale)"
    );
  }
  if (mediaOptions.includeAnnotatedImages) {
    lines.push(
      "",
      "ANNOTATED IMAGES",
      "----------------",
      "Images with visual overlays showing measurements (length, width, mass),",
      "keypoints, and skeleton connections."
    );
  }

  lines.push(
    "",
    "EXTRACTED TEXT (OCR)",
    "--------------------",
    "Text is automatically extracted from captured images via OCR on the device.",
    "In the wide format CSV, extracted text appears as 'Extracted Text' columns",
    "associated with image-capable traits. In the long format CSV, it appears in",
    "the 'Extracted Text' column. If a user edited the extracted text in the app,",
    "the corrected version is exported. Columns are omitted when no extracted text",
    "exists for a given trait.",
    "",
    "SUPPORT",
    "-------",
    "For questions about this export, reach out to support@gleanag.com",
    ""
  );

  return lines.join("\n");
}

/**
 * Collect all unique captureMetadataCloudPath values from all entry types.
 * This is a superset of what collectMeasurementMetadataPaths collects —
 * it covers all entry types that can have annotation metadata.
 */
function collectAllMetadataCloudPaths(entryCollections: EntryCollection[]): Set<string> {
  const cloudPaths = new Set<string>();

  for (const collection of entryCollections) {
    const extractPaths = (entriesMap: Record<string, { captureMetadataCloudPath?: string }> | undefined) => {
      if (!entriesMap) return;
      for (const entry of Object.values(entriesMap)) {
        if (entry.captureMetadataCloudPath) {
          cloudPaths.add(entry.captureMetadataCloudPath);
        }
      }
    };

    extractPaths(collection.textEntries);
    extractPaths(collection.numberEntries);
    extractPaths(collection.categoryEntries);
    extractPaths(collection.dateEntries);
    extractPaths(collection.booleanEntries);
    extractPaths(collection.percentageEntries);
    extractPaths(collection.photoEntries);
    extractPaths(collection.leafAreaIndexEntries);
    extractPaths(collection.rulerEntries);
    extractPaths(collection.imageMeasurementEntries);
    extractPaths(collection.standCountMeasurementEntries);
    extractPaths(collection.transcriptionEntries);

    // Handle rowPlantedStandCount with nested standCountMedia
    if (collection.rowPlantedStandCountEntries) {
      for (const entry of Object.values(collection.rowPlantedStandCountEntries)) {
        if (entry.standCountMedia) {
          for (const mediaEntry of Object.values(entry.standCountMedia)) {
            if (mediaEntry.dataCollectionOnly !== true && mediaEntry.captureMetadataCloudPath) {
              cloudPaths.add(mediaEntry.captureMetadataCloudPath);
            }
          }
        }
      }
    }
  }

  return cloudPaths;
}

async function fetchAnnotationMetadataForExport(
  cloudPath: string,
  shouldCancel?: () => boolean,
  timeoutMs = METADATA_PREFETCH_TIMEOUT_MS,
  cancelPollMs = METADATA_PREFETCH_CANCEL_POLL_MS
): Promise<CaptureMetadata | null> {
  if (shouldCancel?.()) {
    throw new Error(DOWNLOAD_CANCELLED_MESSAGE);
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let cancelIntervalId: ReturnType<typeof setInterval> | undefined;

  const metadataPromise = getAnnotationMetadata(cloudPath);
  const timeoutPromise = new Promise<typeof METADATA_FETCH_TIMED_OUT>((resolve) => {
    timeoutId = setTimeout(() => {
      resolve(METADATA_FETCH_TIMED_OUT);
    }, timeoutMs);
  });
  const cancelPromise = new Promise<typeof METADATA_FETCH_CANCELLED>((resolve) => {
    if (!shouldCancel) return;

    cancelIntervalId = setInterval(() => {
      if (shouldCancel()) {
        resolve(METADATA_FETCH_CANCELLED);
      }
    }, cancelPollMs);
  });

  try {
    const result = await Promise.race([metadataPromise, timeoutPromise, cancelPromise]);
    if (result === METADATA_FETCH_CANCELLED) {
      throw new Error(DOWNLOAD_CANCELLED_MESSAGE);
    }
    if (result === METADATA_FETCH_TIMED_OUT) {
      throw new Error(`Timed out fetching annotation metadata: ${cloudPath}`);
    }
    return result;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
    if (cancelIntervalId) clearInterval(cancelIntervalId);
  }
}

/**
 * Fetch all annotation metadata for a set of cloud paths, with progress reporting.
 * Returns a map of cloudPath -> CaptureMetadata (or null if fetch fails).
 */
async function fetchAllMetadataByCloudPath(
  cloudPaths: Set<string>,
  shouldCancel?: () => boolean,
  onProgress?: (current: number, total: number) => void,
  timeoutMs = METADATA_PREFETCH_TIMEOUT_MS,
  cancelPollMs = METADATA_PREFETCH_CANCEL_POLL_MS
): Promise<Map<string, CaptureMetadata | null>> {
  const cache = new Map<string, CaptureMetadata | null>();
  if (cloudPaths.size === 0) return cache;

  let completed = 0;
  const total = cloudPaths.size;

  const fetchTasks = Array.from(cloudPaths).map((cloudPath) => {
    return async () => {
      try {
        const metadata = await fetchAnnotationMetadataForExport(cloudPath, shouldCancel, timeoutMs, cancelPollMs);
        cache.set(cloudPath, metadata);
      } catch (err) {
        if (err instanceof Error && err.message === DOWNLOAD_CANCELLED_MESSAGE) {
          throw err;
        }
        console.warn(`Skipping annotation metadata for ${cloudPath}:`, err);
        cache.set(cloudPath, null);
      }
      completed++;
      onProgress?.(completed, total);
    };
  });

  await executeConcurrently(fetchTasks, 10, undefined, shouldCancel);
  return cache;
}

/**
 * The files an export will contain besides its generated text files, collected up front so the
 * ZIP and CSV names can be chosen to fit EXPORT_PATH_BUDGET.
 */
interface ExportMediaPlan {
  mediaFiles: ExportInputs["mediaFiles"];
  collectionCaptures: CollectionCaptureRecord[];
  annotatedEntries: Array<AnnotatedEntryInfo & { zipPath: string }>;
  combinedAudio: Array<{ zipPath: string; aggregate: TranscriptionAggregate }>;
}

function planExportMedia(
  entryCollections: EntryCollection[],
  mediaOptions: MediaDownloadOptions,
  transcriptionAggregates: Map<string, TranscriptionAggregate>
): ExportMediaPlan {
  const collectionCaptures = collectCollectionCaptureInventory(entryCollections);
  const requiredFiles = new Map(collectionCaptures.flatMap((capture) =>
    Object.values(capture.files).flatMap((file) => file.zipPath
      ? [[file.zipPath, capture.captureId] as const] : [])));
  const mediaFiles =
    mediaOptions.includeOriginalImages || mediaOptions.includeDepthAndMetadata
      ? Array.from(collectMediaPaths(entryCollections).entries())
          .filter(([, fileInfo]) => isMediaFileIncluded(fileInfo.type, mediaOptions))
          .map(([zipPath, fileInfo]) => ({
            zipPath,
            storagePath: fileInfo.cloudPath,
            ...(requiredFiles.has(zipPath)
              ? { required: true, captureId: requiredFiles.get(zipPath) }
              : {}),
          }))
      : [];
  const includedPaths = new Set(mediaFiles.map((file) => file.zipPath));
  for (const capture of collectionCaptures) {
    for (const file of Object.values(capture.files)) {
      file.included = file.zipPath !== null && includedPaths.has(file.zipPath);
    }
  }
  const annotatedEntries = mediaOptions.includeAnnotatedImages
    ? collectAnnotatedEntries(entryCollections).map((entry) => ({
        ...entry,
        zipPath: getAnnotatedImageZipPath(entry.filename),
      }))
    : [];
  const combinedAudio = mediaOptions.includeAudio
    ? Array.from(transcriptionAggregates.values())
        .filter((aggregate) => aggregate.hasAudio && aggregate.combinedAudioFilename)
        .map((aggregate) => ({ zipPath: getAudioZipPath(aggregate.combinedAudioFilename!), aggregate }))
    : [];
  return { mediaFiles, annotatedEntries, combinedAudio, collectionCaptures };
}

function collectCollectionCaptureInventory(entryCollections: EntryCollection[]): CollectionCaptureRecord[] {
  return entryCollections.flatMap((collection) => {
    if (collection.type !== "rowPlantedStandCount") return [];
    const plotId = typeof collection.plotRef === "string" ? collection.plotRef : collection.plotRef.id;
    const collectionId = collection.id || "unknown";
    return Object.entries(collection.rowPlantedStandCountEntries || {}).flatMap(([sampleKey, sample]) =>
      Object.entries(sample.standCountMedia || {}).flatMap(([captureId, media]) => {
        if (media.dataCollectionOnly !== true) return [];
        return [collectionCaptureRecord({ captureId, collectionId, plotId, sampleKey }, media,
          (kind, storagePath) => getMediaZipPath(kind, generateMediaFilename(
            plotId, "rowPlantedStandCount", `${sampleKey}-${captureId}`, collection.createdAt,
            kind === "metadata" ? "json" : getExtensionFromPath(storagePath, kind === "depth" ? "png" : "jpg"),
            collectionId
          )))];
      }));
  });
}

function collectionManifest(captures: CollectionCaptureRecord[]): string {
  return JSON.stringify({ schemaVersion: 1, captures }, null, 2);
}

/**
 * Plan an export's files: the media to bundle, ZIP and CSV names that keep every path within
 * EXPORT_PATH_BUDGET, and export_info.json.
 *
 * @param source - The task identity, already checked against the route; null when unverified
 */
function planExport({
  data,
  taskName,
  mediaOptions,
  transcriptionAggregates,
  source,
}: {
  data: TaskExportData;
  taskName: string;
  mediaOptions: MediaDownloadOptions;
  transcriptionAggregates: Map<string, TranscriptionAggregate>;
  source: CocoExportSource | null;
}): { media: ExportMediaPlan; names: ExportFileNames; exportInfoJson: string } {
  const media = planExportMedia(data.entryCollections, mediaOptions, transcriptionAggregates);
  assertCollectionCapturesExportable(media.collectionCaptures, mediaOptions);
  const exportedAt = new Date();
  const names = buildExportFileNames({
    taskName,
    timestamp: formatLocalDateTime(exportedAt),
    allowShortTaskLabel: media.collectionCaptures.length > 0,
    contentPaths: [
      ...media.mediaFiles.map((file) => file.zipPath),
      ...media.annotatedEntries.map((entry) => entry.zipPath),
      // The worker writes WAVs it can't join as separate parts, the longest name being the last
      ...media.combinedAudio.map(({ zipPath, aggregate }) =>
        combinedAudioPartZipPath(zipPath, aggregate.audioCloudPaths.length)
      ),
      ...(mediaOptions.includeOriginalImages ? [getCocoAnnotationsZipPath()] : []),
    ],
  });
  if (!names.withinBudget) {
    console.warn(
      `Export path is ${names.longestPath.length} characters, over the ${EXPORT_PATH_BUDGET}-character limit for Windows: ${names.longestPath}`
    );
  }

  const exportInfo = buildExportInfo({
    exportedAt,
    fileNames: names,
    firebaseProjectId: storage.app?.options?.projectId ?? null,
    appOrigin: typeof window === "undefined" ? null : window.location.origin,
    source,
    experiment: data.experiment,
    trial: data.trial,
    organization: data.organization,
    taskName,
    // Plot IDs appear in file names only when media is included
    plotIds: hasAnyMediaOption(mediaOptions)
      ? data.entryCollections.map((collection) =>
          typeof collection.plotRef === "string" ? collection.plotRef : collection.plotRef?.id
        )
      : [],
    mediaOptions,
  });
  return { media, names, exportInfoJson: JSON.stringify(exportInfo, null, 2) };
}

/**
 * Generate a ZIP file containing the CSV export and media files
 * Returns a Blob that can be downloaded
 */
export async function generateTaskZip(
  data: TaskExportData,
  taskName: string,
  taskStartDate: Timestamp,
  mediaOptions: MediaDownloadOptions,
  onProgress?: ProgressCallback,
  shouldCancel?: () => boolean,
  cocoExportSource?: CocoExportSource,
  routeContext?: TaskExportRouteContext
): Promise<{ blob: Blob; filename: string }> {
  let validatedCocoExportSource = cocoExportSource;
  if (mediaOptions.includeOriginalImages) {
    if (
      !routeContext?.trialId ||
      !routeContext.experimentId ||
      !routeContext.taskId ||
      !data.experiment.orgId ||
      !data.experiment.id ||
      data.experiment.id !== routeContext.experimentId
    ) {
      throw new Error("Export context changed while task data was loading. Close the dialog and try again.");
    }
    validatedCocoExportSource = assertCocoExportSourceMatches(cocoExportSource, {
      organizationId: data.experiment.orgId,
      trialId: routeContext.trialId,
      experimentId: routeContext.experimentId,
      taskId: routeContext.taskId,
    });
  }
  const checkCancelled = () => {
    if (shouldCancel?.()) {
      throw new Error("Download cancelled");
    }
  };

  const transcriptionAggregates = buildTranscriptionAggregates(data.entryCollections);
  assertCollectionCapturesExportable(collectCollectionCaptureInventory(data.entryCollections), mediaOptions);
  const zip = new JSZip();

  const includeAnyMedia = hasAnyMediaOption(mediaOptions);

  // Step 1: Fetch all annotation metadata once (shared across CSV, COCO, and annotated images)
  // Only needed when including media — CSV-only exports fetch the narrow subset they need
  let metadataByCloudPath: Map<string, CaptureMetadata | null> | undefined;

  if (mediaOptions.includeAnnotatedImages || mediaOptions.includeOriginalImages) {
    const allCloudPaths = collectAllMetadataCloudPaths(data.entryCollections);
    if (allCloudPaths.size > 0) {
      onProgress?.({
        phase: "preparing",
        current: 0,
        total: allCloudPaths.size,
        message: `Fetching annotation metadata (0/${allCloudPaths.size})...`,
      });
      await yieldToMain();

      metadataByCloudPath = await fetchAllMetadataByCloudPath(allCloudPaths, shouldCancel, (current, total) => {
        onProgress?.({
          phase: "preparing",
          current,
          total,
          message: `Fetching annotation metadata (${current}/${total})...`,
        });
      });
      checkCancelled();
    }
  }

  // Report preparing phase
  onProgress?.({ phase: "preparing", current: 0, total: 1, message: "Generating CSV data..." });
  await yieldToMain();
  checkCancelled();

  // Files go at the top of the ZIP: extracting it already creates a folder named after the ZIP,
  // and a second folder would repeat the task name in every path.
  const { media, names, exportInfoJson } = planExport({
    data,
    taskName,
    mediaOptions,
    transcriptionAggregates,
    source:
      routeContext &&
      cocoExportSourceMatches(cocoExportSource, { organizationId: data.experiment.orgId || undefined, ...routeContext })
        ? cocoExportSource
        : null,
  });

  // Generate and add one-row-per-plot CSV. The wide format has original-image,
  // annotated-image, and audio path columns, each gated by its own media option.
  const csv = await generateTaskCSV(data, mediaOptions, transcriptionAggregates, metadataByCloudPath);
  zip.file(names.csvFilenames.plot, csv);

  // Generate and add one-row-per-observation CSV. The long/per-object formats only
  // reference original images, so their image-path columns gate on includeOriginalImages.
  onProgress?.({ phase: "preparing", current: 0, total: 1, message: "Generating observation data..." });
  await yieldToMain();
  const longFormatCsv = await generateLongFormatCSV(
    data,
    mediaOptions.includeOriginalImages,
    transcriptionAggregates,
    metadataByCloudPath
  );
  zip.file(names.csvFilenames.observation, longFormatCsv);

  // Generate and add one-row-per-object CSV
  onProgress?.({ phase: "preparing", current: 0, total: 1, message: "Generating per-object data..." });
  await yieldToMain();
  const perObjectCsv = await generatePerObjectCSV(data, mediaOptions.includeOriginalImages, metadataByCloudPath);
  zip.file(names.csvFilenames.object, perObjectCsv);

  zip.file(getReadmeZipPath(), generateReadmeContent(names.taskLabel, mediaOptions, names.withinBudget));
  zip.file(getExportInfoZipPath(), exportInfoJson);
  if (media.collectionCaptures.length) {
    zip.file(getCollectionCapturesZipPath(), collectionManifest(media.collectionCaptures));
  }

  onProgress?.({ phase: "preparing", current: 1, total: 1, message: "CSV generation complete" });
  await yieldToMain();
  checkCancelled();

  // Add media files to ZIP if requested
  if (includeAnyMedia) {
    // Generate COCO annotations JSON (only when original images are included)
    if (mediaOptions.includeOriginalImages) {
      onProgress?.({ phase: "preparing", current: 0, total: 1, message: "Building annotations..." });
      await yieldToMain();
      const cocoJson = await generateCocoAnnotations(
        data.entryCollections,
        taskName,
        shouldCancel,
        metadataByCloudPath,
        validatedCocoExportSource,
        getTaskAnnotationCategorySchemas(data.entryConfigs),
        buildAnnotationClassNamesByEntryConfigId(data.entryConfigs)
      );
      checkCancelled();
      if (cocoJson) {
        zip.file(getCocoAnnotationsZipPath(), cocoJson);
      }
    }

    // Create download tasks for each media file, filtered by options
    const downloadTasks = media.mediaFiles.map(({ zipPath, storagePath, required, captureId }) => async () => {
      try {
        const downloadURL = await getDownloadURL(ref(storage, storagePath));

        const response = await fetch(downloadURL);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${zipPath}: HTTP ${response.status} ${response.statusText}`);
        }

        zip.file(zipPath, await response.blob());
      } catch (error) {
        console.error(`Error downloading ${zipPath}:`, error);
        if (required) throw requiredCaptureDownloadError(captureId, zipPath);
      }
    });

    // Add combined transcription audio files (if enabled)
    for (const { zipPath, aggregate } of media.combinedAudio) {
      downloadTasks.push(async () => {
        const result = await buildCombinedAudioBlob(aggregate);
        if (result) {
          zip.file(zipPath, result.blob);
        }
      });
    }

    // Add annotated image rendering tasks (if enabled, use cached metadata)
    const annotationClassColorOrder = getTaskAnnotationClassColorOrder(data.entryConfigs);
    const entryConfigById = new Map(
      data.entryConfigs.flatMap((entryConfig) => (entryConfig.id ? [[entryConfig.id, entryConfig] as const] : []))
    );
    for (const entry of media.annotatedEntries) {
      downloadTasks.push(async () => {
        try {
          const imageUrl = await getDownloadURL(ref(storage, entry.imgCloudPath));

          const metadata =
            metadataByCloudPath?.get(entry.captureMetadataCloudPath) ??
            (await getAnnotationMetadata(entry.captureMetadataCloudPath));
          if (!metadata) {
            console.warn(`No metadata found for: ${entry.captureMetadataCloudPath}`);
            return;
          }
          const annotationClassNames = getAnnotationClassNamesForEntryConfig(
            entry.entryConfigId ? entryConfigById.get(entry.entryConfigId) : undefined
          );

          const annotatedBlob = await renderAnnotatedImage(
            imageUrl,
            metadata,
            buildAnnotatedImageRenderOptions({
              annotationClassNames,
              annotationClassColorOrder,
            })
          );

          zip.file(entry.zipPath, annotatedBlob);
        } catch (error) {
          console.error(`Error rendering annotated image for ${entry.filename}:`, error);
        }
      });
    }

    // Download files with concurrency limit
    await executeConcurrently(
      downloadTasks,
      10,
      (completed, total) => {
        onProgress?.({ phase: "downloading", current: completed, total });
      },
      shouldCancel
    );
  }

  checkCancelled();

  // Generate the ZIP file with progress reporting
  onProgress?.({ phase: "compressing", current: 0, total: 100 });
  await yieldToMain();

  const blob = await zip.generateAsync({ type: "blob" }, (metadata) => {
    onProgress?.({ phase: "compressing", current: Math.round(metadata.percent), total: 100 });
  });
  return { blob, filename: names.zipFilename };
}

/**
 * Count the total number of media files that would be included in a download.
 * Used for threshold-based routing between client-side and server-side paths.
 */
export function countMediaFiles(entryCollections: EntryCollection[], mediaOptions: MediaDownloadOptions): number {
  const transcriptionAggregates = mediaOptions.includeAudio
    ? buildTranscriptionAggregates(entryCollections)
    : new Map();
  const { mediaFiles, annotatedEntries, combinedAudio } = planExportMedia(
    entryCollections,
    mediaOptions,
    transcriptionAggregates
  );
  return mediaFiles.length + annotatedEntries.length + combinedAudio.length;
}

export interface StartTaskExportJobInput {
  data: TaskExportData;
  trialId: string;
  experimentId: string;
  taskId: string;
  cocoExportSource?: CocoExportSource;
  taskName: string;
  taskStartDate: Timestamp;
  mediaOptions: MediaDownloadOptions;
  onProgress?: ProgressCallback;
  shouldCancel?: () => boolean;
}

/**
 * Kick off a persistent task-export job and return its identifiers.
 *
 * Text content (CSVs, COCO JSON, README) is generated client-side and sent to
 * the `startExportJob` callable, which:
 *   1. Writes a job doc at `organizations/{orgId}/exportJobs/{jobId}`,
 *   2. Uploads the inputs payload to GCS, and
 *   3. Enqueues a Cloud Tasks worker (`processExportJob`) that builds the ZIP.
 *
 * The caller subscribes to the job doc via `subscribeToExportJob` for live
 * status and uses `getExportDownloadUrl` to mint a fresh signed URL when the
 * job completes. This means the export survives the user closing the tab or
 * navigating away, unlike the previous streaming `streamZipExport` path.
 */
export async function generateTaskZipViaCloudFunction(
  input: StartTaskExportJobInput
): Promise<{ jobId: string; runId: string; reusedExistingJob: boolean }> {
  const {
    data,
    trialId,
    experimentId,
    taskId,
    cocoExportSource,
    taskName,
    taskStartDate,
    mediaOptions,
    onProgress,
    shouldCancel,
  } = input;
  const validatedCocoExportSource = mediaOptions.includeOriginalImages
    ? assertCocoExportSourceMatches(cocoExportSource, {
        organizationId: data.experiment.orgId,
        trialId,
        experimentId,
        taskId,
      })
    : undefined;
  if (data.experiment.id !== experimentId) {
    throw new Error("Export context changed while task data was loading. Close the dialog and try again.");
  }
  const checkCancelled = () => {
    if (shouldCancel?.()) {
      throw new Error("Download cancelled");
    }
  };

  const transcriptionAggregates = buildTranscriptionAggregates(data.entryCollections);

  assertCollectionCapturesExportable(collectCollectionCaptureInventory(data.entryCollections), mediaOptions);

  // Step 1: Fetch all annotation metadata (reuses existing client-side logic)
  // Only needed when annotated images or original images (for COCO) are included
  let metadataByCloudPath: Map<string, CaptureMetadata | null> | undefined;

  if (mediaOptions.includeAnnotatedImages || mediaOptions.includeOriginalImages) {
    const allCloudPaths = collectAllMetadataCloudPaths(data.entryCollections);

    if (allCloudPaths.size > 0) {
      onProgress?.({
        phase: "preparing",
        current: 0,
        total: allCloudPaths.size,
        message: `Fetching annotation metadata (0/${allCloudPaths.size})...`,
      });

      metadataByCloudPath = await fetchAllMetadataByCloudPath(allCloudPaths, shouldCancel, (current, total) => {
        onProgress?.({
          phase: "preparing",
          current,
          total,
          message: `Fetching annotation metadata (${current}/${total})...`,
        });
      });
      checkCancelled();
    }
  }

  // Step 2: Generate text content locally
  onProgress?.({ phase: "preparing", current: 0, total: 1, message: "Generating CSV data..." });

  const { media, names, exportInfoJson } = planExport({
    data,
    taskName,
    mediaOptions,
    transcriptionAggregates,
    source: cocoExportSourceMatches(cocoExportSource, {
      organizationId: data.experiment.orgId || undefined,
      trialId,
      experimentId,
      taskId,
    })
      ? cocoExportSource
      : null,
  });

  const csv = await generateTaskCSV(data, mediaOptions, transcriptionAggregates, metadataByCloudPath);
  checkCancelled();

  const longFormatCsv = await generateLongFormatCSV(
    data,
    mediaOptions.includeOriginalImages,
    transcriptionAggregates,
    metadataByCloudPath
  );
  checkCancelled();

  const perObjectCsv = await generatePerObjectCSV(data, mediaOptions.includeOriginalImages, metadataByCloudPath);
  checkCancelled();

  const readme = generateReadmeContent(names.taskLabel, mediaOptions, names.withinBudget);

  // COCO JSON only when original images are included (references image file names)
  let cocoJson: string | null = null;
  if (mediaOptions.includeOriginalImages) {
    cocoJson = await generateCocoAnnotations(
      data.entryCollections,
      taskName,
      shouldCancel,
      metadataByCloudPath,
      validatedCocoExportSource!,
      getTaskAnnotationCategorySchemas(data.entryConfigs),
      buildAnnotationClassNamesByEntryConfigId(data.entryConfigs)
    );
    checkCancelled();
  }

  // Build text files array
  const textFiles: Array<{ zipPath: string; content: string }> = [
    { zipPath: names.csvFilenames.plot, content: csv },
    { zipPath: names.csvFilenames.observation, content: longFormatCsv },
    { zipPath: names.csvFilenames.object, content: perObjectCsv },
    { zipPath: getReadmeZipPath(), content: readme },
    { zipPath: getExportInfoZipPath(), content: exportInfoJson },
  ];
  if (cocoJson) {
    textFiles.push({ zipPath: getCocoAnnotationsZipPath(), content: cocoJson });
  }
  if (media.collectionCaptures.length) {
    textFiles.push({ zipPath: getCollectionCapturesZipPath(), content: collectionManifest(media.collectionCaptures) });
  }

  // Step 3: Media, annotated image, and audio paths (filtered by options)
  const { mediaFiles } = media;

  const annotationClassColorOrder = getTaskAnnotationClassColorOrder(data.entryConfigs);
  const entryConfigById = new Map(
    data.entryConfigs.flatMap((entryConfig) => (entryConfig.id ? [[entryConfig.id, entryConfig] as const] : []))
  );
  const annotatedImages = media.annotatedEntries.map((entry) => ({
    zipPath: entry.zipPath,
    imgStoragePath: entry.imgCloudPath,
    metadataStoragePath: entry.captureMetadataCloudPath,
    entryType: entry.entryType,
    annotationClassNames: getAnnotationClassNamesForEntryConfig(
      entry.entryConfigId ? entryConfigById.get(entry.entryConfigId) : undefined
    ),
    annotationClassColorOrder,
  }));

  const combinedAudio = media.combinedAudio.map(({ zipPath, aggregate }) => ({
    zipPath,
    audioStoragePaths: aggregate.audioCloudPaths,
  }));

  // Step 4: Determine org ID from first available storage path
  let orgId = "";
  const firstStoragePath =
    mediaFiles[0]?.storagePath || annotatedImages[0]?.imgStoragePath || combinedAudio[0]?.audioStoragePaths[0] || "";
  const orgMatch = firstStoragePath.match(/^organizations\/([^/]+)\//);
  if (orgMatch) {
    orgId = orgMatch[1];
  }

  if (!orgId) {
    throw new Error("Could not determine organization ID from storage paths");
  }
  const expectedOrganizationId = mediaOptions.includeOriginalImages
    ? validatedCocoExportSource!.organizationId
    : data.experiment.orgId;
  if (expectedOrganizationId && orgId !== expectedOrganizationId) {
    throw new Error("Export context changed while task data was loading. Close the dialog and try again.");
  }

  // Step 5: Kick off the persistent export job. The callable returns as soon
  // as the job doc is written + the worker is enqueued; live status comes
  // from the Firestore subscription owned by the caller.
  // The inputs go to Cloud Storage, not into the callable body: Cloud Run
  // rejects any request over 32 MiB at the frontend with a bare 413 that
  // carries no CORS headers, and an image-heavy task's generated CSV + COCO
  // text alone clears that on its own.
  onProgress?.({ phase: "server", current: 0, total: 1, message: "Uploading export inputs..." });
  checkCancelled();

  const inputsUploadId = newExportInputsUploadId();
  await uploadExportInputs(orgId, inputsUploadId, {
    orgId,
    trialId,
    experimentId,
    taskId,
    // Older function deployments ignore flatLayout and nest everything under rootFolder.
    rootFolder: names.zipFilename.replace(/\.zip$/, ""),
    flatLayout: true,
    zipFilename: names.zipFilename,
    textFiles,
    mediaFiles,
    annotatedImages,
    combinedAudio,
  } satisfies ExportInputs);

  onProgress?.({ phase: "server", current: 0, total: 1, message: "Starting export..." });
  checkCancelled();

  const response = await startExportJob({ orgId, inputsUploadId });

  return {
    jobId: response.jobId,
    runId: response.runId,
    reusedExistingJob: response.reusedExistingJob,
  };
}

/**
 * Interface for a long format CSV row
 * Each row represents a single observation/sample
 */
interface LongFormatRow {
  [key: string]: string | number | boolean;
}

// Separator used for appending metric suffixes to trait names in long format
const TRAIT_NAME_SEPARATOR = " -- ";

/**
 * Generate long format CSV column headers
 * Returns static columns that will be present in every long format export
 * True long format: all values go in "Value" column, metrics appended to "Trait Name"
 */
function generateLongFormatHeaders(experiment: Experiment): string[] {
  const headers: string[] = [];

  // Dynamic plot metadata columns from experiment plotMap
  if (experiment.plotMap?.headers) {
    for (const header of experiment.plotMap.headers) {
      headers.push(header);
    }
  } else if (experiment.plotMap?.plotIDName) {
    headers.push(experiment.plotMap.plotIDName);
  } else {
    // Fallback: no plotMap at all (grid/sequence experiments created before fix)
    headers.push("Plot ID");
  }

  // Universal long format columns
  headers.push("Trait Name");
  headers.push("Entry Type");
  headers.push("Sample Number");
  headers.push("Observation #");
  headers.push("Value");
  headers.push("Data Collector");
  headers.push("Image Path");
  headers.push("Extracted Text");
  headers.push("Last Updated");

  return headers;
}

/**
 * Create a long format row with consistent structure
 * All values go in "Value" column, metrics appended to "Trait Name" via suffix
 */
function createLongFormatRow(
  plotMetadata: Record<string, string | number>,
  traitName: string,
  metricSuffix: string | null,
  entryType: string,
  sampleNumber: string,
  observationNumber: number,
  value: string | number,
  collectorEmail: string,
  imagePath: string,
  lastUpdated: string,
  extractedText: string = ""
): LongFormatRow {
  return {
    ...plotMetadata,
    "Trait Name": metricSuffix ? `${traitName}${TRAIT_NAME_SEPARATOR}${metricSuffix}` : traitName,
    "Entry Type": entryType,
    "Sample Number": sampleNumber,
    "Observation #": observationNumber,
    Value: value,
    "Data Collector": collectorEmail,
    "Image Path": imagePath,
    "Extracted Text": extractedText,
    "Last Updated": lastUpdated,
  };
}

/**
 * Get last updated timestamp from a specific entry collection
 */
function getCollectionLastUpdated(collection: EntryCollection, sampleKey?: string): string {
  let latestTimestamp: Timestamp | null = null;

  // Check collection-level updatedAt
  if (collection.updatedAt) {
    latestTimestamp = collection.updatedAt as Timestamp;
  }

  // Check entry-level updatedAt if sample key provided
  const checkEntry = (entry: { updatedAt?: Timestamp } | undefined) => {
    if (entry?.updatedAt) {
      const ts = entry.updatedAt as Timestamp;
      if (!latestTimestamp || ts.seconds > latestTimestamp.seconds) {
        latestTimestamp = ts;
      }
    }
  };

  if (sampleKey) {
    switch (collection.type) {
      case "text":
        checkEntry(collection.textEntries?.[sampleKey]);
        break;
      case "number":
        checkEntry(collection.numberEntries?.[sampleKey]);
        break;
      case "category":
        checkEntry(collection.categoryEntries?.[sampleKey]);
        break;
      case "date":
        checkEntry(collection.dateEntries?.[sampleKey]);
        break;
      case "boolean":
        checkEntry(collection.booleanEntries?.[sampleKey]);
        break;
      case "percentage":
        checkEntry(collection.percentageEntries?.[sampleKey]);
        break;
      case "photo":
        checkEntry(collection.photoEntries?.[sampleKey]);
        break;
      case "leafAreaIndex":
        checkEntry(collection.leafAreaIndexEntries?.[sampleKey]);
        break;
      case "ruler":
        checkEntry(collection.rulerEntries?.[sampleKey]);
        break;
      case "imageMeasurement":
        checkEntry(collection.imageMeasurementEntries?.[sampleKey]);
        break;
      case "standCountMeasurement":
        checkEntry(collection.standCountMeasurementEntries?.[sampleKey]);
        break;
      case "rowPlantedStandCount":
        checkEntry(collection.rowPlantedStandCountEntries?.[sampleKey]);
        break;
      case "timer":
        checkEntry(collection.timerEntries?.[sampleKey]);
        break;
    }
  }

  if (!latestTimestamp) return "";

  const date = latestTimestamp.toDate();
  return `${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

/**
 * Generate long format rows for a single plot
 * Creates one row per sample/observation
 */
function generateLongFormatRowsForPlot(
  plot: Plot,
  experiment: Experiment,
  entryCollections: EntryCollection[],
  entryConfigs: EntryConfig[],
  metadataCache: Map<string, CaptureMetadata | null>,
  transcriptionAggregates: Map<string, TranscriptionAggregate>,
  includeMedia: boolean,
  userEmailCache: Map<string, string>
): LongFormatRow[] {
  const rows: LongFormatRow[] = [];
  const plotId = plot.plotID || "";
  const plotMetadata = extractPlotMetadata(plot, experiment);

  // Get all entry collections for this plot
  const plotCollections = getPlotEntryCollections(plotId, entryCollections);

  // Group collections by entry config ID
  const collectionsByConfig = new Map<string, EntryCollection[]>();
  for (const collection of plotCollections) {
    const configId = getEntryConfigIdFromCollection(collection);
    if (!collectionsByConfig.has(configId)) {
      collectionsByConfig.set(configId, []);
    }
    collectionsByConfig.get(configId)!.push(collection);
  }

  // Process each entry config
  for (const config of entryConfigs) {
    const configId = config.id || "";
    const traitName = config.name;
    const entryType = config.type;
    const collections = collectionsByConfig.get(configId) || [];

    if (collections.length === 0) continue;

    // Get all unique sample keys across all collections
    const sampleKeys = new Set<string>();
    for (const collection of collections) {
      getCollectionSampleKeys(collection).forEach((k) => sampleKeys.add(k));
    }

    // Sort sample keys numerically
    const sortedSampleKeys = Array.from(sampleKeys).sort((a, b) => Number(a) - Number(b));

    // Special handling for transcription - one row per collector's entry
    if (entryType === "transcription") {
      const transcriptionCollections = collections.filter((c) => c.type === "transcription");
      for (const collection of transcriptionCollections) {
        if (!collection.transcriptionEntries) continue;
        for (const [sampleKey, entry] of Object.entries(collection.transcriptionEntries)) {
          if (!entry?.text) continue;
          rows.push(
            createLongFormatRow(
              plotMetadata,
              traitName,
              null, // no metric suffix
              entryType,
              sampleKey,
              1, // Observation # = 1
              entry.text,
              getCollectorEmail(collection, userEmailCache),
              "", // audio path handled separately
              getCollectionLastUpdated(collection, sampleKey)
            )
          );
        }
      }
      continue;
    }

    // Process each sample
    for (const sampleKey of sortedSampleKeys) {
      // Filter to collections that have this sample key
      const collectionsWithSample = collections.filter((c) => getCollectionSampleKeys(c).includes(sampleKey));

      if (collectionsWithSample.length === 0) continue;

      switch (entryType) {
        case "text": {
          // One row per collector
          for (const collection of collectionsWithSample) {
            const entry = collection.textEntries?.[sampleKey];
            if (!entry) continue;
            let imagePath = "";
            if (includeMedia && entry.imgCloudPath) {
              const fileName = extractFileNameFromPath(entry.imgCloudPath, collection, "text", sampleKey);
              imagePath = getImageCsvPath(fileName);
            }
            rows.push(
              createLongFormatRow(
                plotMetadata,
                traitName,
                null,
                entryType,
                sampleKey,
                1,
                entry.text || "",
                getCollectorEmail(collection, userEmailCache),
                imagePath,
                getCollectionLastUpdated(collection, sampleKey),
                sanitizeExtractedTextForCsv(entry.extractedImageText)
              )
            );
          }
          break;
        }

        case "number": {
          // One row per collector (no averaging)
          for (const collection of collectionsWithSample) {
            const entry = collection.numberEntries?.[sampleKey];
            if (!entry || entry.val == null) continue;
            let imagePath = "";
            if (includeMedia && entry.imgCloudPath) {
              const fileName = extractFileNameFromPath(entry.imgCloudPath, collection, "number", sampleKey);
              imagePath = getImageCsvPath(fileName);
            }
            rows.push(
              createLongFormatRow(
                plotMetadata,
                traitName,
                null,
                entryType,
                sampleKey,
                1,
                entry.val,
                getCollectorEmail(collection, userEmailCache),
                imagePath,
                getCollectionLastUpdated(collection, sampleKey),
                sanitizeExtractedTextForCsv(entry.extractedImageText)
              )
            );
          }
          break;
        }

        case "category": {
          // One row per collector
          for (const collection of collectionsWithSample) {
            const entry = collection.categoryEntries?.[sampleKey];
            if (!entry || !entry.selections || entry.selections.length === 0) continue;
            let imagePath = "";
            if (includeMedia && entry.imgCloudPath) {
              const fileName = extractFileNameFromPath(entry.imgCloudPath, collection, "category", sampleKey);
              imagePath = getImageCsvPath(fileName);
            }
            // Export selections as JSON array for multi-select support
            rows.push(
              createLongFormatRow(
                plotMetadata,
                traitName,
                null,
                entryType,
                sampleKey,
                1,
                JSON.stringify(entry.selections),
                getCollectorEmail(collection, userEmailCache),
                imagePath,
                getCollectionLastUpdated(collection, sampleKey),
                sanitizeExtractedTextForCsv(entry.extractedImageText)
              )
            );
          }
          break;
        }

        case "boolean": {
          // One row per collector
          for (const collection of collectionsWithSample) {
            const entry = collection.booleanEntries?.[sampleKey];
            if (!entry || entry.val == null) continue;
            let imagePath = "";
            if (includeMedia && entry.imgCloudPath) {
              const fileName = extractFileNameFromPath(entry.imgCloudPath, collection, "boolean", sampleKey);
              imagePath = getImageCsvPath(fileName);
            }
            rows.push(
              createLongFormatRow(
                plotMetadata,
                traitName,
                null,
                entryType,
                sampleKey,
                1,
                entry.val ? "Yes" : "No",
                getCollectorEmail(collection, userEmailCache),
                imagePath,
                getCollectionLastUpdated(collection, sampleKey),
                sanitizeExtractedTextForCsv(entry.extractedImageText)
              )
            );
          }
          break;
        }

        case "percentage": {
          // One row per collector (no averaging)
          for (const collection of collectionsWithSample) {
            const entry = collection.percentageEntries?.[sampleKey];
            if (!entry || entry.val == null) continue;
            let imagePath = "";
            if (includeMedia && entry.imgCloudPath) {
              const fileName = extractFileNameFromPath(entry.imgCloudPath, collection, "percentage", sampleKey);
              imagePath = getImageCsvPath(fileName);
            }
            rows.push(
              createLongFormatRow(
                plotMetadata,
                traitName,
                null,
                entryType,
                sampleKey,
                1,
                entry.val,
                getCollectorEmail(collection, userEmailCache),
                imagePath,
                getCollectionLastUpdated(collection, sampleKey),
                sanitizeExtractedTextForCsv(entry.extractedImageText)
              )
            );
          }
          break;
        }

        case "timer": {
          // One row per collector (no averaging). Timer has no media/extracted
          // text. Emit the elapsed duration in seconds, labelling the unit by
          // appending "(s)" to the Trait Name (timer is a scalar with no metric
          // sub-name, so it uses a plain space, not the " -- " metric separator).
          // Skip idle (elapsedMs absent/0) and running (startedAtEpochMs set,
          // elapsedMs absent) samples — no value.
          for (const collection of collectionsWithSample) {
            const elapsedMs = getStoppedTimerMs(collection.timerEntries?.[sampleKey]);
            if (elapsedMs == null) continue;
            rows.push(
              createLongFormatRow(
                plotMetadata,
                `${traitName} (s)`,
                null,
                entryType,
                sampleKey,
                1,
                timerMsToSeconds(elapsedMs),
                getCollectorEmail(collection, userEmailCache),
                "", // timer has no image path
                getCollectionLastUpdated(collection, sampleKey)
              )
            );
          }
          break;
        }

        case "date": {
          // One row per collector
          for (const collection of collectionsWithSample) {
            const entry = collection.dateEntries?.[sampleKey];
            if (!entry?.date) continue;
            const timestamp = entry.date as Timestamp;
            const date = timestamp.toDate();
            const dateStr = `${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()}`;
            let imagePath = "";
            if (includeMedia && entry.imgCloudPath) {
              const fileName = extractFileNameFromPath(entry.imgCloudPath, collection, "date", sampleKey);
              imagePath = getImageCsvPath(fileName);
            }
            rows.push(
              createLongFormatRow(
                plotMetadata,
                traitName,
                null,
                entryType,
                sampleKey,
                1,
                dateStr,
                getCollectorEmail(collection, userEmailCache),
                imagePath,
                getCollectionLastUpdated(collection, sampleKey),
                sanitizeExtractedTextForCsv(entry.extractedImageText)
              )
            );
          }
          break;
        }

        case "photo": {
          // One row per collector
          for (const collection of collectionsWithSample) {
            const entry = collection.photoEntries?.[sampleKey];
            if (!entry) continue;
            let imagePath = "";
            if (includeMedia && entry.imgCloudPath) {
              const fileName = extractFileNameFromPath(entry.imgCloudPath, collection, "photo", sampleKey);
              imagePath = getImageCsvPath(fileName);
            }
            rows.push(
              createLongFormatRow(
                plotMetadata,
                traitName,
                null,
                entryType,
                sampleKey,
                1,
                "", // photo has no value
                getCollectorEmail(collection, userEmailCache),
                imagePath,
                getCollectionLastUpdated(collection, sampleKey),
                sanitizeExtractedTextForCsv(entry.extractedImageText)
              )
            );
          }
          break;
        }

        case "leafAreaIndex": {
          // One row per collector (no averaging)
          for (const collection of collectionsWithSample) {
            const entry = collection.leafAreaIndexEntries?.[sampleKey];
            if (!entry || entry.val == null) continue;
            let imagePath = "";
            if (includeMedia && entry.imgCloudPath) {
              const fileName = extractFileNameFromPath(entry.imgCloudPath, collection, "leafAreaIndex", sampleKey);
              imagePath = getImageCsvPath(fileName);
            }
            rows.push(
              createLongFormatRow(
                plotMetadata,
                traitName,
                null,
                entryType,
                sampleKey,
                1,
                entry.val,
                getCollectorEmail(collection, userEmailCache),
                imagePath,
                getCollectionLastUpdated(collection, sampleKey),
                sanitizeExtractedTextForCsv(entry.extractedImageText)
              )
            );
          }
          break;
        }

        case "ruler": {
          // One row per collector (no averaging)
          for (const collection of collectionsWithSample) {
            const entry = collection.rulerEntries?.[sampleKey];
            const measurement = entry?.measurementsMeters?.[0];
            if (measurement == null) continue;
            let imagePath = "";
            if (includeMedia && entry?.imgCloudPath) {
              const fileName = extractFileNameFromPath(entry.imgCloudPath, collection, "ruler", sampleKey);
              imagePath = getImageCsvPath(fileName);
            }
            rows.push(
              createLongFormatRow(
                plotMetadata,
                traitName,
                null,
                entryType,
                sampleKey,
                1,
                measurement,
                getCollectorEmail(collection, userEmailCache),
                imagePath,
                getCollectionLastUpdated(collection, sampleKey),
                sanitizeExtractedTextForCsv(entry?.extractedImageText)
              )
            );
          }
          break;
        }

        case "imageMeasurement": {
          // Check if weight measurement is enabled
          const hasWeightMeasurement =
            config.imageMeasurementConfig?.imageMeasurementModelOptions?.hasWeightMeasurement ?? false;
          const imGrading = (config as ImageMeasurementConfig).imageMeasurementConfig?.grading;

          // Process each collection separately for per-collector rows
          for (const collection of collectionsWithSample) {
            const entry = collection.imageMeasurementEntries?.[sampleKey];
            if (!entry) continue;

            const collectorEmail = getCollectorEmail(collection, userEmailCache);
            const lastUpdated = getCollectionLastUpdated(collection, sampleKey);
            const extractedText = sanitizeExtractedTextForCsv(entry.extractedImageText);

            let imagePath = "";
            if (includeMedia && entry.imgCloudPath) {
              const fileName = extractFileNameFromPath(entry.imgCloudPath, collection, "imageMeasurement", sampleKey);
              imagePath = getImageCsvPath(fileName);
            }

            // FIX: Use correct metadata key format with collection.id
            const metadataKey = `${plotId}-${collection.id}-${sampleKey}`;
            const metadata = metadataCache.get(metadataKey);
            const visibleAnnotations = extractVisibleAnnotations(metadata);

            if (visibleAnnotations.length > 0) {
              // Individual observation rows - one row per metric per observation
              for (let obsIdx = 0; obsIdx < visibleAnnotations.length; obsIdx++) {
                const annotation = visibleAnnotations[obsIdx];
                const measurements = annotation.measurements;
                const obsNum = obsIdx + 1;

                // Length row
                if (measurements?.lengthM != null) {
                  rows.push(
                    createLongFormatRow(
                      plotMetadata,
                      traitName,
                      "Length (cm)",
                      entryType,
                      sampleKey,
                      obsNum,
                      formatMetersAsCentimetersForCsv(measurements.lengthM),
                      collectorEmail,
                      imagePath,
                      lastUpdated,
                      extractedText
                    )
                  );
                }

                // Width row
                if (measurements?.widthM != null) {
                  rows.push(
                    createLongFormatRow(
                      plotMetadata,
                      traitName,
                      "Width (cm)",
                      entryType,
                      sampleKey,
                      obsNum,
                      formatMetersAsCentimetersForCsv(measurements.widthM),
                      collectorEmail,
                      imagePath,
                      lastUpdated,
                      extractedText
                    )
                  );
                }

                // Weight row (if enabled)
                if (hasWeightMeasurement && measurements?.massG != null) {
                  rows.push(
                    createLongFormatRow(
                      plotMetadata,
                      traitName,
                      "Weight (g)",
                      entryType,
                      sampleKey,
                      obsNum,
                      Math.round(measurements.massG * 10) / 10,
                      collectorEmail,
                      imagePath,
                      lastUpdated,
                      extractedText
                    )
                  );
                }

                // Grade row (if grading is configured on the trait).
                // Value is the bucket label this object falls into, or "Ungraded"
                // when the dimension is missing / no bucket matches.
                if (imGrading && imGrading.buckets.length > 0) {
                  const dimValue = imGrading.dimension === "length" ? measurements?.lengthM : measurements?.widthM;
                  const bucket = dimValue != null ? assignBucket(dimValue, imGrading) : null;
                  rows.push(
                    createLongFormatRow(
                      plotMetadata,
                      traitName,
                      "Grade",
                      entryType,
                      sampleKey,
                      obsNum,
                      bucket?.label ?? "Ungraded",
                      collectorEmail,
                      imagePath,
                      lastUpdated,
                      extractedText
                    )
                  );
                }
              }
            } else {
              // Fallback: aggregate values as separate rows when no metadata
              if (entry.objectCount != null) {
                rows.push(
                  createLongFormatRow(
                    plotMetadata,
                    traitName,
                    "Count",
                    entryType,
                    sampleKey,
                    1,
                    entry.objectCount,
                    collectorEmail,
                    imagePath,
                    lastUpdated,
                    extractedText
                  )
                );
              }
              if (entry.avgLengthMeters != null) {
                rows.push(
                  createLongFormatRow(
                    plotMetadata,
                    traitName,
                    "Mean Length (cm)",
                    entryType,
                    sampleKey,
                    1,
                    formatMetersAsCentimetersForCsv(entry.avgLengthMeters),
                    collectorEmail,
                    imagePath,
                    lastUpdated,
                    extractedText
                  )
                );
              }
              if (entry.avgWidthMeters != null) {
                rows.push(
                  createLongFormatRow(
                    plotMetadata,
                    traitName,
                    "Mean Width (cm)",
                    entryType,
                    sampleKey,
                    1,
                    formatMetersAsCentimetersForCsv(entry.avgWidthMeters),
                    collectorEmail,
                    imagePath,
                    lastUpdated,
                    extractedText
                  )
                );
              }
              if (hasWeightMeasurement && entry.totalWeightGrams != null) {
                rows.push(
                  createLongFormatRow(
                    plotMetadata,
                    traitName,
                    "Total Weight (g)",
                    entryType,
                    sampleKey,
                    1,
                    Math.round(entry.totalWeightGrams * 10) / 10,
                    collectorEmail,
                    imagePath,
                    lastUpdated,
                    extractedText
                  )
                );
              }
            }
          }
          break;
        }

        case "standCountMeasurement": {
          // Process each collection separately for per-collector rows
          for (const collection of collectionsWithSample) {
            const entry = collection.standCountMeasurementEntries?.[sampleKey];
            if (!entry) continue;

            const collectorEmail = getCollectorEmail(collection, userEmailCache);
            const lastUpdated = getCollectionLastUpdated(collection, sampleKey);
            const extractedText = sanitizeExtractedTextForCsv(entry.extractedImageText);

            // Try to get individual observations from CaptureMetadata
            const metadataKey = `${plotId}-${collection.id}-${sampleKey}`;
            const metadata = metadataCache.get(metadataKey);
            const visibleAnnotations = extractVisibleAnnotations(metadata);

            if (visibleAnnotations.length > 0) {
              // One row per detected object
              for (let obsIdx = 0; obsIdx < visibleAnnotations.length; obsIdx++) {
                rows.push(
                  createLongFormatRow(
                    plotMetadata,
                    traitName,
                    "Object Count",
                    entryType,
                    sampleKey,
                    obsIdx + 1,
                    1, // Each object = 1
                    collectorEmail,
                    "",
                    lastUpdated,
                    extractedText
                  )
                );
              }
            } else if (entry.imageMeasurement?.objectCount != null) {
              // Fallback: aggregate count
              rows.push(
                createLongFormatRow(
                  plotMetadata,
                  traitName,
                  "Raw Count",
                  entryType,
                  sampleKey,
                  1,
                  entry.imageMeasurement.objectCount,
                  collectorEmail,
                  "",
                  lastUpdated,
                  extractedText
                )
              );
            }

            // Compute countPerM2 — prefer stored value, fall back to raw data
            let countPerM2 = entry.countPerSquareMeter;
            if (
              countPerM2 == null &&
              entry.imageMeasurement?.objectCount != null &&
              entry.standCountBoxLengthMeters != null &&
              entry.standCountBoxWidthMeters != null &&
              entry.standCountBoxLengthMeters > 0 &&
              entry.standCountBoxWidthMeters > 0
            ) {
              countPerM2 =
                entry.imageMeasurement.objectCount / (entry.standCountBoxLengthMeters * entry.standCountBoxWidthMeters);
            }

            if (countPerM2 != null) {
              rows.push(
                createLongFormatRow(
                  plotMetadata,
                  traitName,
                  "Count / m²",
                  entryType,
                  sampleKey,
                  1,
                  Number(countPerM2.toFixed(1)),
                  collectorEmail,
                  "",
                  lastUpdated,
                  extractedText
                )
              );
              rows.push(
                createLongFormatRow(
                  plotMetadata,
                  traitName,
                  "Plants / acre",
                  entryType,
                  sampleKey,
                  1,
                  Math.round(countPerSquareMeterToPerAcre(countPerM2)),
                  collectorEmail,
                  "",
                  lastUpdated,
                  extractedText
                )
              );
            }
          }

          break;
        }

        case "rowPlantedStandCount": {
          // Process each collection separately for per-collector rows
          for (const collection of collectionsWithSample) {
            const entry = collection.rowPlantedStandCountEntries?.[sampleKey];
            if (!entry) continue;

            const collectorEmail = getCollectorEmail(collection, userEmailCache);
            const lastUpdated = getCollectionLastUpdated(collection, sampleKey);

            // Create one row per metric (6 rows max), all with Observation # = 1
            if (entry.plantCount != null) {
              rows.push(
                createLongFormatRow(
                  plotMetadata,
                  traitName,
                  "Plant Count",
                  entryType,
                  sampleKey,
                  1,
                  entry.plantCount,
                  collectorEmail,
                  "",
                  lastUpdated
                )
              );
            }

            if (entry.walkDistanceMeters != null) {
              rows.push(
                createLongFormatRow(
                  plotMetadata,
                  traitName,
                  "Walk Distance (m)",
                  entryType,
                  sampleKey,
                  1,
                  Number(entry.walkDistanceMeters.toFixed(2)),
                  collectorEmail,
                  "",
                  lastUpdated
                )
              );
            }

            if (entry.plantsPerMeter != null) {
              rows.push(
                createLongFormatRow(
                  plotMetadata,
                  traitName,
                  "Plants/m",
                  entryType,
                  sampleKey,
                  1,
                  Number(entry.plantsPerMeter.toFixed(2)),
                  collectorEmail,
                  "",
                  lastUpdated
                )
              );
            }

            if (entry.averageSpacingMeters != null) {
              rows.push(
                createLongFormatRow(
                  plotMetadata,
                  traitName,
                  "Avg. Spacing (cm)",
                  entryType,
                  sampleKey,
                  1,
                  formatMetersAsCentimetersForCsv(entry.averageSpacingMeters),
                  collectorEmail,
                  "",
                  lastUpdated
                )
              );
            }

            if (entry.averageDiameterMeters != null) {
              rows.push(
                createLongFormatRow(
                  plotMetadata,
                  traitName,
                  "Avg. Length (cm)",
                  entryType,
                  sampleKey,
                  1,
                  formatMetersAsCentimetersForCsv(entry.averageDiameterMeters),
                  collectorEmail,
                  "",
                  lastUpdated
                )
              );
            }

            if (entry.averageWidthMeters != null) {
              rows.push(
                createLongFormatRow(
                  plotMetadata,
                  traitName,
                  "Avg. Width (cm)",
                  entryType,
                  sampleKey,
                  1,
                  formatMetersAsCentimetersForCsv(entry.averageWidthMeters),
                  collectorEmail,
                  "",
                  lastUpdated
                )
              );
            }

            if (entry.averageVari != null) {
              rows.push(
                createLongFormatRow(
                  plotMetadata,
                  traitName,
                  "Avg. Greenness",
                  entryType,
                  sampleKey,
                  1,
                  Number(entry.averageVari.toFixed(3)),
                  collectorEmail,
                  "",
                  lastUpdated
                )
              );
            }
          }
          break;
        }
      }
    }

    // Stand count total rows — once per trait, after all samples
    if (entryType === "standCountMeasurement" && sortedSampleKeys.length > 1) {
      const mostRecentCollection = collections.sort(
        (a, b) => (b.updatedAt?.toMillis() ?? 0) - (a.updatedAt?.toMillis() ?? 0)
      )[0];
      if (mostRecentCollection?.standCountMeasurementEntries) {
        const allEntries = Object.values(mostRecentCollection.standCountMeasurementEntries);
        const weighted = computeWeightedCountPerM2(
          allEntries.map((e) => ({
            countPerSquareMeter: e.countPerSquareMeter,
            objectCount: e.imageMeasurement?.objectCount,
            boxLengthM: e.standCountBoxLengthMeters,
            boxWidthM: e.standCountBoxWidthMeters,
          }))
        );
        if (weighted != null) {
          const collectorEmail = getCollectorEmail(mostRecentCollection, userEmailCache);
          rows.push(
            createLongFormatRow(
              plotMetadata,
              traitName,
              "Total Count / m²",
              entryType,
              "total",
              1,
              Number(weighted.toFixed(1)),
              collectorEmail,
              "",
              "",
              ""
            )
          );
          rows.push(
            createLongFormatRow(
              plotMetadata,
              traitName,
              "Total Plants / acre",
              entryType,
              "total",
              1,
              Math.round(countPerSquareMeterToPerAcre(weighted)),
              collectorEmail,
              "",
              "",
              ""
            )
          );
        }
      }
    }

    // Image measurement grading total rows — one per bucket plus Ungraded,
    // emitted once per trait at the end so the per-plot counts are easy to pivot.
    if (entryType === "imageMeasurement") {
      const imGrading = (config as ImageMeasurementConfig).imageMeasurementConfig?.grading;
      if (imGrading && imGrading.buckets.length > 0) {
        const metadataList: CaptureMetadata[] = [];
        for (const collection of collections) {
          const sampleKeys = collection.imageMeasurementEntries ? Object.keys(collection.imageMeasurementEntries) : [];
          for (const key of sampleKeys) {
            const metadata = metadataCache.get(`${plotId}-${collection.id}-${key}`);
            if (metadata) metadataList.push(metadata);
          }
        }
        const dist = computeGradeDistribution(metadataList, imGrading);
        if (dist) {
          let obsIdx = 1;
          for (const bucket of imGrading.buckets) {
            rows.push(
              createLongFormatRow(
                plotMetadata,
                traitName,
                `Grade ${bucket.label} Count`,
                entryType,
                "total",
                obsIdx++,
                dist.counts[bucket.label] ?? 0,
                "",
                "",
                "",
                ""
              )
            );
          }
          rows.push(
            createLongFormatRow(
              plotMetadata,
              traitName,
              "Grade Ungraded Count",
              entryType,
              "total",
              obsIdx,
              dist.ungraded,
              "",
              "",
              "",
              ""
            )
          );
        }
      }
    }
  }

  return rows;
}

/**
 * Remove columns that are completely empty across all rows, except any column
 * named in `alwaysKeep` (kept even when every value is empty).
 */
function removeEmptyColumns(
  headers: string[],
  rows: LongFormatRow[],
  alwaysKeep: Set<string>
): { headers: string[]; rows: LongFormatRow[] } {
  // Find columns that have at least one non-empty value
  const columnsWithData = new Set<string>();

  for (const row of rows) {
    for (const header of headers) {
      const value = row[header];
      if (value !== "" && value != null) {
        columnsWithData.add(header);
      }
    }
  }

  // Filter headers to only include non-empty columns or always-keep columns
  const filteredHeaders = headers.filter((h) => columnsWithData.has(h) || alwaysKeep.has(h));

  // Filter rows to only include non-empty columns
  const filteredRows = rows.map((row) => {
    const newRow: LongFormatRow = {};
    for (const header of filteredHeaders) {
      newRow[header] = row[header];
    }
    return newRow;
  });

  return { headers: filteredHeaders, rows: filteredRows };
}

// Always-keep columns for the long format (universal observation columns).
const LONG_FORMAT_ALWAYS_KEEP = new Set([
  "Trait Name",
  "Entry Type",
  "Sample Number",
  "Observation #",
  "Value",
  "Data Collector",
  "Last Updated",
]);

/**
 * Remove columns that are completely empty across all rows
 */
function removeEmptyColumnsFromLongFormat(
  headers: string[],
  rows: LongFormatRow[]
): { headers: string[]; rows: LongFormatRow[] } {
  return removeEmptyColumns(headers, rows, LONG_FORMAT_ALWAYS_KEEP);
}

/**
 * Generate long format CSV from task data
 * Each row represents a single observation/sample rather than a plot
 */
export async function generateLongFormatCSV(
  data: TaskExportData,
  includeMedia: boolean = true,
  transcriptionAggregates?: Map<string, TranscriptionAggregate>,
  metadataByCloudPath?: Map<string, CaptureMetadata | null>
): Promise<string> {
  const { experiment, plots, entryCollections } = data;
  const entryConfigs = addMissingTranscriptionConfigs(data.entryConfigs, entryCollections);
  const aggregates = transcriptionAggregates || buildTranscriptionAggregates(entryCollections);

  // Fetch metadata for imageMeasurement entries
  const metadataPaths = collectMeasurementMetadataPaths(entryCollections);
  const metadataCache = await fetchAllMetadataForCSV(metadataPaths, metadataByCloudPath);

  // Fetch user emails for Data Collector column
  const userEmailCache = await fetchUserEmailsForCollections(entryCollections);

  // Generate headers
  let headers = generateLongFormatHeaders(experiment);

  // Generate data rows for each plot
  let rows: LongFormatRow[] = [];
  for (let i = 0; i < plots.length; i++) {
    const plotRows = generateLongFormatRowsForPlot(
      plots[i],
      experiment,
      entryCollections,
      entryConfigs,
      metadataCache,
      aggregates,
      includeMedia,
      userEmailCache
    );
    rows.push(...plotRows);

    if (i > 0 && i % 20 === 0) {
      await yieldToMain();
    }
  }

  // Remove empty columns
  const filtered = removeEmptyColumnsFromLongFormat(headers, rows);
  headers = filtered.headers;
  rows = filtered.rows;

  // Convert to CSV using Papa Parse
  const csv = unparse({
    fields: headers,
    data: rows,
  });

  return csv;
}

// ============================================================================
// Per-object CSV (one_row_per_object) — one row per detected object, with the
// object's metrics (length/width/weight/grade/maturity) as columns.
// Scalar traits degenerate to one row per sample; ruler emits one row per
// reading. photo and transcription are excluded.
// ============================================================================

// Always-keep columns for the per-object CSV (never dropped by removeEmptyColumns).
// Value is intentionally NOT here: detected-object rows leave it blank (their data
// lives in the metric columns), so on an export with no single-value trait to fill
// it, the whole column drops out.
const PER_OBJECT_ALWAYS_KEEP = new Set([
  "Trait Name",
  "Entry Type",
  "Sample Number",
  "Object #",
  "Data Collector",
  "Last Updated",
]);

/**
 * Generate per-object CSV column headers.
 * Plot-metadata columns first (identical to the long format), then one column
 * per metric so a detected object's measurements sit on a single row.
 */
function generatePerObjectHeaders(experiment: Experiment): string[] {
  const headers: string[] = [];

  // Dynamic plot metadata columns from experiment plotMap (same as long format)
  if (experiment.plotMap?.headers) {
    for (const header of experiment.plotMap.headers) {
      headers.push(header);
    }
  } else if (experiment.plotMap?.plotIDName) {
    headers.push(experiment.plotMap.plotIDName);
  } else {
    headers.push("Plot ID");
    headers.push("Row");
    headers.push("Column");
  }

  headers.push("Trait Name");
  headers.push("Entry Type");
  headers.push("Sample Number");
  headers.push("Object #");
  headers.push("Value");
  headers.push("Length (cm)");
  headers.push("Width (cm)");
  headers.push("Weight (g)");
  headers.push("Grade");
  headers.push("Maturity");
  headers.push("Data Collector");
  headers.push("Image Path");
  headers.push("Extracted Text");
  headers.push("Last Updated");

  return headers;
}

interface PerObjectCells {
  value: string | number;
  lengthCm: string;
  widthCm: string;
  weightG: string | number;
  grade: string;
  maturity: string;
}

const EMPTY_PER_OBJECT_CELLS: PerObjectCells = {
  value: "",
  lengthCm: "",
  widthCm: "",
  weightG: "",
  grade: "",
  maturity: "",
};

/**
 * Create a per-object CSV row. Wide sibling to createLongFormatRow: metrics are
 * columns, and Trait Name never carries a " -- " metric suffix.
 */
function createPerObjectRow(
  plotMetadata: Record<string, string | number>,
  traitName: string,
  entryType: string,
  sampleNumber: string,
  objectNumber: number,
  cells: PerObjectCells,
  collectorEmail: string,
  imagePath: string,
  extractedText: string,
  lastUpdated: string
): LongFormatRow {
  return {
    ...plotMetadata,
    "Trait Name": traitName,
    "Entry Type": entryType,
    "Sample Number": sampleNumber,
    "Object #": objectNumber,
    Value: cells.value,
    "Length (cm)": cells.lengthCm,
    "Width (cm)": cells.widthCm,
    "Weight (g)": cells.weightG,
    Grade: cells.grade,
    Maturity: cells.maturity,
    "Data Collector": collectorEmail,
    "Image Path": imagePath,
    "Extracted Text": extractedText,
    "Last Updated": lastUpdated,
  };
}

/**
 * Build the per-object metric cells for a single detected-object annotation.
 * `weightEnabled` gates imageMeasurement weight (config-driven `hasWeightMeasurement`);
 * standCount / rowPlanted pass true and rely on `massG` presence (data-driven,
 * matching the wide format). The -1 massG sentinel (squash/cucumber
 * "weight unavailable") is filtered to a blank Weight.
 */
function buildAnnotationCells(
  annotation: Annotation,
  weightEnabled: boolean,
  grading: ImageMeasurementGrading | undefined
): PerObjectCells {
  const measurements = annotation.measurements;
  const cells: PerObjectCells = {
    // Detected objects have no single "value" — their data is in the metric columns.
    // Count objects via the Object # column or a row count.
    value: "",
    lengthCm: measurements?.lengthM != null ? formatMetersAsCentimetersForCsv(measurements.lengthM) : "",
    widthCm: measurements?.widthM != null ? formatMetersAsCentimetersForCsv(measurements.widthM) : "",
    weightG: "",
    grade: "",
    maturity: annotation.attributes?.class_label ?? "",
  };

  if (weightEnabled && measurements?.massG != null && measurements.massG !== -1) {
    cells.weightG = Math.round(measurements.massG * 10) / 10;
  }

  if (grading && grading.buckets.length > 0) {
    const dimValue = grading.dimension === "length" ? measurements?.lengthM : measurements?.widthM;
    const bucket = dimValue != null ? assignBucket(dimValue, grading) : null;
    cells.grade = bucket?.label ?? "Ungraded";
  }

  return cells;
}

/** Resolve the CSV image-path cell for a media-bearing entry (empty when media is excluded). */
function perObjectImagePath(
  entry: { imgCloudPath?: string } | undefined,
  collection: EntryCollection,
  entryType: string,
  sampleKey: string,
  includeMedia: boolean
): string {
  if (!includeMedia || !entry?.imgCloudPath) return "";
  return getImageCsvPath(extractFileNameFromPath(entry.imgCloudPath, collection, entryType, sampleKey));
}

/**
 * Generate per-object rows for a single plot.
 * Mirrors generateLongFormatRowsForPlot's plot → config → sample → collection
 * scaffolding so the two CSVs stay row-aligned, but pivots metrics into columns
 * and emits one row per detected object.
 */
function generatePerObjectRowsForPlot(
  plot: Plot,
  experiment: Experiment,
  entryCollections: EntryCollection[],
  entryConfigs: EntryConfig[],
  metadataCache: Map<string, CaptureMetadata | null>,
  includeMedia: boolean,
  userEmailCache: Map<string, string>
): LongFormatRow[] {
  const rows: LongFormatRow[] = [];
  const plotId = plot.plotID || "";
  const plotMetadata = extractPlotMetadata(plot, experiment);

  const plotCollections = getPlotEntryCollections(plotId, entryCollections);

  // Group collections by entry config ID
  const collectionsByConfig = new Map<string, EntryCollection[]>();
  for (const collection of plotCollections) {
    const configId = getEntryConfigIdFromCollection(collection);
    if (!collectionsByConfig.has(configId)) {
      collectionsByConfig.set(configId, []);
    }
    collectionsByConfig.get(configId)!.push(collection);
  }

  for (const config of entryConfigs) {
    const configId = config.id || "";
    const traitName = config.name;
    const entryType = config.type;
    const collections = collectionsByConfig.get(configId) || [];
    if (collections.length === 0) continue;

    // photo and transcription have no measured object; they live in the other CSVs.
    if (entryType === "photo" || entryType === "transcription") continue;

    // All unique sample keys across collectors, sorted numerically (matches long format)
    const sampleKeys = new Set<string>();
    for (const collection of collections) {
      getCollectionSampleKeys(collection).forEach((k) => sampleKeys.add(k));
    }
    const sortedSampleKeys = Array.from(sampleKeys).sort((a, b) => Number(a) - Number(b));

    for (const sampleKey of sortedSampleKeys) {
      const collectionsWithSample = collections.filter((c) => getCollectionSampleKeys(c).includes(sampleKey));
      if (collectionsWithSample.length === 0) continue;

      for (const collection of collectionsWithSample) {
        const collectorEmail = getCollectorEmail(collection, userEmailCache);
        const lastUpdated = getCollectionLastUpdated(collection, sampleKey);

        switch (entryType) {
          case "imageMeasurement": {
            const entry = collection.imageMeasurementEntries?.[sampleKey];
            if (!entry) break;
            const metadata = metadataCache.get(`${plotId}-${collection.id}-${sampleKey}`);
            const visible = extractVisibleAnnotations(metadata);
            if (visible.length === 0) break; // skip samples without per-object metadata
            // Mirror the long/wide format's weight + grading resolution exactly.
            const hasWeight =
              config.imageMeasurementConfig?.imageMeasurementModelOptions?.hasWeightMeasurement ?? false;
            const grading = (config as ImageMeasurementConfig).imageMeasurementConfig?.grading;
            const imagePath = perObjectImagePath(entry, collection, entryType, sampleKey, includeMedia);
            const extractedText = sanitizeExtractedTextForCsv(entry.extractedImageText);
            for (let i = 0; i < visible.length; i++) {
              rows.push(
                createPerObjectRow(
                  plotMetadata,
                  traitName,
                  entryType,
                  sampleKey,
                  i + 1,
                  buildAnnotationCells(visible[i], hasWeight, grading),
                  collectorEmail,
                  imagePath,
                  extractedText,
                  lastUpdated
                )
              );
            }
            break;
          }

          case "standCountMeasurement": {
            const entry = collection.standCountMeasurementEntries?.[sampleKey];
            if (!entry) break;
            const metadata = metadataCache.get(`${plotId}-${collection.id}-${sampleKey}`);
            const visible = extractVisibleAnnotations(metadata);
            if (visible.length === 0) break;
            const imagePath = perObjectImagePath(entry, collection, entryType, sampleKey, includeMedia);
            const extractedText = sanitizeExtractedTextForCsv(entry.extractedImageText);
            for (let i = 0; i < visible.length; i++) {
              rows.push(
                createPerObjectRow(
                  plotMetadata,
                  traitName,
                  entryType,
                  sampleKey,
                  i + 1,
                  buildAnnotationCells(visible[i], true, undefined),
                  collectorEmail,
                  imagePath,
                  extractedText,
                  lastUpdated
                )
              );
            }
            break;
          }

          case "rowPlantedStandCount": {
            const entry = collection.rowPlantedStandCountEntries?.[sampleKey];
            if (!entry?.standCountMedia) break;
            // Reuse the shared row-planted filename formula so the per-object image
            // paths match the ZIP entries and wide-format CSV byte-for-byte.
            const imageFilenameByMediaKey = new Map(
              getRowPlantedImageFilenames(
                entry,
                plotId,
                sampleKey,
                collection.createdAt as Timestamp,
                collection.id || "unknown"
              ).map((f) => [f.mediaKey, f.filename])
            );
            // Object # runs continuously across the sample's media captures.
            let objectNumber = 0;
            for (const mediaKey of Object.keys(entry.standCountMedia).sort()) {
              const metadata = metadataCache.get(`${plotId}-${collection.id}-${sampleKey}-rpsc-${mediaKey}`);
              const visible = extractVisibleAnnotations(metadata);
              if (visible.length === 0) continue;
              const filename = imageFilenameByMediaKey.get(mediaKey);
              const imagePath = includeMedia && filename ? getImageCsvPath(filename) : "";
              for (const annotation of visible) {
                objectNumber += 1;
                rows.push(
                  createPerObjectRow(
                    plotMetadata,
                    traitName,
                    entryType,
                    sampleKey,
                    objectNumber,
                    buildAnnotationCells(annotation, true, undefined),
                    collectorEmail,
                    imagePath,
                    "",
                    lastUpdated
                  )
                );
              }
            }
            break;
          }

          case "ruler": {
            const entry = collection.rulerEntries?.[sampleKey];
            const readings = entry?.measurementsMeters;
            if (!readings || readings.length === 0) break;
            const imagePath = perObjectImagePath(entry, collection, entryType, sampleKey, includeMedia);
            const extractedText = sanitizeExtractedTextForCsv(entry?.extractedImageText);
            let objectNumber = 0;
            for (const reading of readings) {
              if (reading == null) continue;
              objectNumber += 1;
              rows.push(
                createPerObjectRow(
                  plotMetadata,
                  traitName,
                  entryType,
                  sampleKey,
                  objectNumber,
                  { ...EMPTY_PER_OBJECT_CELLS, lengthCm: formatMetersAsCentimetersForCsv(reading) },
                  collectorEmail,
                  imagePath,
                  extractedText,
                  lastUpdated
                )
              );
            }
            break;
          }

          case "text": {
            const entry = collection.textEntries?.[sampleKey];
            if (!entry) break;
            rows.push(
              createPerObjectRow(
                plotMetadata,
                traitName,
                entryType,
                sampleKey,
                1,
                { ...EMPTY_PER_OBJECT_CELLS, value: entry.text || "" },
                collectorEmail,
                perObjectImagePath(entry, collection, entryType, sampleKey, includeMedia),
                sanitizeExtractedTextForCsv(entry.extractedImageText),
                lastUpdated
              )
            );
            break;
          }

          case "number": {
            const entry = collection.numberEntries?.[sampleKey];
            if (!entry || entry.val == null) break;
            rows.push(
              createPerObjectRow(
                plotMetadata,
                traitName,
                entryType,
                sampleKey,
                1,
                { ...EMPTY_PER_OBJECT_CELLS, value: entry.val },
                collectorEmail,
                perObjectImagePath(entry, collection, entryType, sampleKey, includeMedia),
                sanitizeExtractedTextForCsv(entry.extractedImageText),
                lastUpdated
              )
            );
            break;
          }

          case "category": {
            const entry = collection.categoryEntries?.[sampleKey];
            if (!entry || !entry.selections || entry.selections.length === 0) break;
            rows.push(
              createPerObjectRow(
                plotMetadata,
                traitName,
                entryType,
                sampleKey,
                1,
                { ...EMPTY_PER_OBJECT_CELLS, value: JSON.stringify(entry.selections) },
                collectorEmail,
                perObjectImagePath(entry, collection, entryType, sampleKey, includeMedia),
                sanitizeExtractedTextForCsv(entry.extractedImageText),
                lastUpdated
              )
            );
            break;
          }

          case "boolean": {
            const entry = collection.booleanEntries?.[sampleKey];
            if (!entry || entry.val == null) break;
            rows.push(
              createPerObjectRow(
                plotMetadata,
                traitName,
                entryType,
                sampleKey,
                1,
                { ...EMPTY_PER_OBJECT_CELLS, value: entry.val ? "Yes" : "No" },
                collectorEmail,
                perObjectImagePath(entry, collection, entryType, sampleKey, includeMedia),
                sanitizeExtractedTextForCsv(entry.extractedImageText),
                lastUpdated
              )
            );
            break;
          }

          case "percentage": {
            const entry = collection.percentageEntries?.[sampleKey];
            if (!entry || entry.val == null) break;
            rows.push(
              createPerObjectRow(
                plotMetadata,
                traitName,
                entryType,
                sampleKey,
                1,
                { ...EMPTY_PER_OBJECT_CELLS, value: entry.val },
                collectorEmail,
                perObjectImagePath(entry, collection, entryType, sampleKey, includeMedia),
                sanitizeExtractedTextForCsv(entry.extractedImageText),
                lastUpdated
              )
            );
            break;
          }

          case "date": {
            const entry = collection.dateEntries?.[sampleKey];
            if (!entry?.date) break;
            const date = (entry.date as Timestamp).toDate();
            const dateStr = `${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()}`;
            rows.push(
              createPerObjectRow(
                plotMetadata,
                traitName,
                entryType,
                sampleKey,
                1,
                { ...EMPTY_PER_OBJECT_CELLS, value: dateStr },
                collectorEmail,
                perObjectImagePath(entry, collection, entryType, sampleKey, includeMedia),
                sanitizeExtractedTextForCsv(entry.extractedImageText),
                lastUpdated
              )
            );
            break;
          }

          case "leafAreaIndex": {
            const entry = collection.leafAreaIndexEntries?.[sampleKey];
            if (!entry || entry.val == null) break;
            rows.push(
              createPerObjectRow(
                plotMetadata,
                traitName,
                entryType,
                sampleKey,
                1,
                { ...EMPTY_PER_OBJECT_CELLS, value: entry.val },
                collectorEmail,
                perObjectImagePath(entry, collection, entryType, sampleKey, includeMedia),
                sanitizeExtractedTextForCsv(entry.extractedImageText),
                lastUpdated
              )
            );
            break;
          }

          case "timer": {
            const elapsedMs = getStoppedTimerMs(collection.timerEntries?.[sampleKey]);
            if (elapsedMs == null) break;
            rows.push(
              createPerObjectRow(
                plotMetadata,
                `${traitName} (s)`, // scalar unit label, plain space (matches long format)
                entryType,
                sampleKey,
                1,
                { ...EMPTY_PER_OBJECT_CELLS, value: timerMsToSeconds(elapsedMs) },
                collectorEmail,
                "",
                "",
                lastUpdated
              )
            );
            break;
          }
        }
      }
    }
  }

  return rows;
}

/**
 * Generate the per-object ("one row per detected object") CSV from task data.
 */
export async function generatePerObjectCSV(
  data: TaskExportData,
  includeMedia: boolean = true,
  metadataByCloudPath?: Map<string, CaptureMetadata | null>
): Promise<string> {
  const { experiment, plots, entryCollections, entryConfigs } = data;

  // Fetch per-object metadata (imageMeasurement / standCount / rowPlanted media)
  const metadataPaths = collectMeasurementMetadataPaths(entryCollections);
  const metadataCache = await fetchAllMetadataForCSV(metadataPaths, metadataByCloudPath);

  // Fetch user emails for Data Collector column
  const userEmailCache = await fetchUserEmailsForCollections(entryCollections);

  let headers = generatePerObjectHeaders(experiment);

  let rows: LongFormatRow[] = [];
  for (let i = 0; i < plots.length; i++) {
    const plotRows = generatePerObjectRowsForPlot(
      plots[i],
      experiment,
      entryCollections,
      entryConfigs,
      metadataCache,
      includeMedia,
      userEmailCache
    );
    rows.push(...plotRows);

    if (i > 0 && i % 20 === 0) {
      await yieldToMain();
    }
  }

  const filtered = removeEmptyColumns(headers, rows, PER_OBJECT_ALWAYS_KEEP);
  headers = filtered.headers;
  rows = filtered.rows;

  return unparse({
    fields: headers,
    data: rows,
  });
}

// Export for unit tests
export { buildTranscriptionAggregates as _buildTranscriptionAggregatesForTests };
export { generateHeaderColumns as _generateHeaderColumnsForTests };
export { extractPlotMetadata as _extractPlotMetadataForTests };
export {
  extractValue as _extractValueForTests,
  extractIndividualMeasurementsFromMetadata as _extractIndividualMeasurementsFromMetadataForTests,
};
export { generateLongFormatHeaders as _generateLongFormatHeadersForTests };
export { generateLongFormatRowsForPlot as _generateLongFormatRowsForPlotForTests };
export { generatePerObjectHeaders as _generatePerObjectHeadersForTests };
export { generatePerObjectRowsForPlot as _generatePerObjectRowsForPlotForTests };
export { removeEmptyColumns as _removeEmptyColumnsForTests };
export { collectMeasurementMetadataPaths as _collectMeasurementMetadataPathsForTests };
export { sanitizeExtractedTextForCsv as _sanitizeExtractedTextForCsvForTests };
export { hasExtractedImageTextInCollection as _hasExtractedImageTextInCollectionForTests };
export { buildAnnotatedImageRenderOptions as _buildAnnotatedImageRenderOptionsForTests };
export { fetchAllMetadataByCloudPath as _fetchAllMetadataByCloudPathForTests };
export { generateReadmeContent as _generateReadmeContentForTests };
export { collectAnnotatedEntries as _collectAnnotatedEntriesForTests };
